import { useState, useRef, useEffect, useLayoutEffect, useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import useSeo from '../../seo/useSeo';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../supabaseClient';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Header from '../../components/Header';
import SafeAvatar, { PersonIcon } from '../../components/SafeAvatar';
import {
  generateProjectSchedule,
  validateProjectInput,
  recalculateCost,
  stopChatMessage,
  generateChecklistHints,
} from '../../services/difyApi';
import { createProject as createProjectInDB } from '../../services/projectService';
import { aiChat, aiApply } from '../../services/aiAgentApi';
import { mapProjectDashboardDetail } from './mapProjectDashboardDetail';
import { deriveAvatarInitial } from '../../components/AvatarEditPopup';
import MemberActivityPopup from './MemberActivityPopup';
import ChatAttachments, { ChatLightbox } from './ChatAttachments';
import {
  requestPresign,
  uploadFileToR2,
  deleteAttachments,
  extractKeys,
} from '../../services/chatUpload';
import {
  HiOutlineBars4,
  HiOutlineViewColumns,
  HiOutlinePaperAirplane,
  HiOutlineArrowPath,
  HiOutlinePlus,
  HiOutlinePencilSquare,
  HiOutlineCheckCircle,
  HiOutlineTrash,
  HiOutlineUserPlus,
  HiOutlinePaperClip,
  HiOutlineChatBubbleLeftRight,
  HiOutlineChatBubbleLeftEllipsis,
  HiOutlineUser,
  HiOutlineArrowUturnLeft,
  HiOutlineEye,
  HiOutlineEyeSlash,
  HiOutlineChevronDown,
  HiOutlineChevronLeft,
  HiOutlineChevronRight,
  HiOutlineHeart,
} from 'react-icons/hi2';
import { HiHeart } from 'react-icons/hi2';
import { MdOutlineCalendarMonth } from 'react-icons/md';
import './Dashboard.css';

// 카드사 심사용 임시: 연간 플랜 숨김(월간만). Polar 복귀 시 true 로 원복.
const SHOW_YEARLY = false;

/** AI 스파클 아이콘 — 큰 4꼭지 별 + 작은 악센트 별(채움, currentColor).
    크기·색은 부모 CSS(.xxx svg{width/height}, color)가 결정 */
function SparkleIcon({ className }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M12 1.5 Q12.7 11.3 22.5 12 Q12.7 12.7 12 22.5 Q11.3 12.7 1.5 12 Q11.3 11.3 12 1.5 Z" />
    </svg>
  );
}

/** DB에 기타 행이 없을 때만 셀렉트에 붙이는 옵션; 제출 시 name=기타로 id 조회 */
const TEMPLATE_CATEGORY_SYNTHETIC_ETC = '__synthetic_etc__';
const LAST_SELECTED_PROJECT_KEY_PREFIX = 'lansly_last_selected_project_id_';
const TIMELINE_VIEW_STORAGE_KEY = 'lansly_timeline_view';

/**
 * updated_avatar_url 이 비어있으면 닉네임 첫 글자 fallback. OAuth user_metadata.avatar_url
 * 로는 떨어지지 않음 — 사용자가 user_profiles 에서 지운 썸네일이 OAuth 옛 사진으로
 * 다시 노출되는 문제 방지.
 */
function pickAvatar(profileUrl, displayName) {
  if (typeof profileUrl === 'string' && profileUrl.trim()) return profileUrl;
  const initial = deriveAvatarInitial(displayName);
  if (initial && initial !== '?') return initial;
  // null 을 반환하면 SafeAvatar 가 PersonIcon SVG 로 폴백 처리
  return null;
}

/** 모바일 삼성 브라우저 우회 — 키보드 올라온 직후 포커스된 input 을 화면 가운데로.
 *  iOS Safari/Chrome 은 자동 처리되지만 삼성은 안 됨. 300ms 는 키보드 애니메이션 끝날 시간.
 *  추가로 fixed overlay 안 짧은 모달(스크롤 영역 없음) 케이스는 scrollIntoView 가 무효 →
 *  visualViewport 기준으로 input 이 키보드 아래로 가려졌으면 모달 자체를 translateY 로 끌어올림. */
function scrollInputIntoCenterOnFocus(e) {
  const el = e.target;
  setTimeout(() => {
    try {
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    } catch {}
    // 모달 보정 — visualViewport 가 있는 환경에서만
    requestAnimationFrame(() => {
      try {
        const vp = window.visualViewport;
        if (!vp) return;
        const modal = el.closest('.claude-popup-modal, .name-edit-modal, .avatar-edit-modal');
        if (!modal) return;
        const inputRect = el.getBoundingClientRect();
        const visibleBottom = vp.offsetTop + vp.height;
        const overflow = inputRect.bottom - visibleBottom;
        if (overflow > 0) {
          const shift = overflow + 16;
          modal.style.transition = 'transform 0.25s ease';
          modal.style.transform = `translateY(-${shift}px)`;
          // 키보드 닫힐 때(=blur) 원위치
          const restore = () => {
            modal.style.transform = '';
            el.removeEventListener('blur', restore);
          };
          el.addEventListener('blur', restore);
        }
      } catch {}
    });
  }, 300);
}

/** 소유자 또는 역할이 관리자인 멤버만 프로젝트 AI 기능(비용 근거, 체크리스트 AI 가이드 생성) 사용 */
function canUserManageProjectAi(project, user) {
  if (!user?.id || !project) return false;
  if (project.owner_id === user.id) return true;
  return project.members?.find((m) => m.id === user.id)?.role === '관리자';
}

/** `<input type="date">`용 로컬 날짜 문자열 (UTC toISOString 시 하루 밀림 방지) */
function formatLocalDateInput(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** 프로젝트 생성 폼 기본 날짜: 시작=오늘, 종료=오늘로부터 1달 뒤 */
function getDefaultProjectDates() {
  const today = new Date();
  const end = new Date(today);
  end.setMonth(end.getMonth() + 1);
  return { startDate: formatLocalDateInput(today), endDate: formatLocalDateInput(end) };
}

/** 멤버 아바타 클릭 → 활동 캘린더 팝업 트리거 (5곳 공용 래퍼).
    id 가 없으면 비활성. onClick 은 e.stopPropagation 으로 상위 click 핸들러(예: SNS 카드)
    를 막아 의도치 않은 작업 팝업 열림을 방지. */
function MemberAvatarButton({ id, name, avatar, onOpen, className, children }) {
  const { t } = useTranslation('dashboard');
  const safeName = name || t('common.member');
  return (
    <button
      type="button"
      className={`claude-team-avatar-btn${className ? ` ${className}` : ''}`}
      onClick={(e) => {
        e.stopPropagation();
        if (id) onOpen({ id, name: safeName, avatar });
      }}
      aria-label={t('common.viewActivity', { name: safeName })}
      title={t('common.viewActivity', { name: safeName })}
      disabled={!id}
    >
      {children}
    </button>
  );
}

/**
 * SNS 스택 뷰용: 카테고리·작업 맥락에서 게시물·댓글을 합쳐 최신순 정렬
 */
function buildProjectActivityFeed(activeProject, t) {
  if (!activeProject?.categories?.length) return [];

  const shorten = (s, n = 160) => {
    if (!s || typeof s !== 'string') return '';
    const str = s.trim().replace(/\s+/g, ' ');
    return str.length <= n ? str : `${str.slice(0, n)}…`;
  };

  const events = [];

  activeProject.categories.forEach((category) => {
    category.tasks.forEach((task) => {
      const categoryId = category.id;
      const taskId = task.id;

      (task.posts || []).forEach((post) => {
        if (post.createdAt == null) return;
        const postTs = new Date(post.createdAt).getTime();
        if (Number.isNaN(postTs)) return;
        events.push({
          id: `post-${post.id}`,
          postId: post.id,
          sortAt: postTs,
          type: 'post',
          actor: {
            id: post.authorId || '',
            name: post.author || t('common.author'),
            avatar: post.avatar,
          },
          categoryId,
          taskId,
          categoryName: category.name,
          taskName: task.name,
          body: post.content || '',
          attachments: Array.isArray(post.attachments) ? post.attachments : [],
          hiddenAt: post.hiddenAt || null,
        });

        (post.comments || []).forEach((comment) => {
          if (comment.createdAt == null) return;
          const ct = new Date(comment.createdAt).getTime();
          if (Number.isNaN(ct)) return;
          events.push({
            id: `cmt-${comment.id}`,
            commentId: comment.id,
            postId: post.id,
            sortAt: ct,
            type: 'comment',
            actor: {
              id: comment.authorId || '',
              name: comment.author || t('common.author'),
              avatar: comment.avatar,
            },
            categoryId,
            taskId,
            categoryName: category.name,
            taskName: task.name,
            postSnippet: post.hiddenAt ? '' : shorten(post.content, 100),
            postHiddenAt: post.hiddenAt || null,
            // 대댓글이면 부모 댓글 인용용 정보. 부모가 가려져 있으면 본문은 비워서 노출 안 함.
            parentCommentId: comment.parentCommentId || null,
            parentAuthor: comment.parentAuthor || null,
            parentSnippet: comment.parentHiddenAt ? '' : shorten(comment.parentSnippet || '', 100),
            parentHiddenAt: comment.parentHiddenAt || null,
            body: comment.content || '',
            attachments: Array.isArray(comment.attachments) ? comment.attachments : [],
            hiddenAt: comment.hiddenAt || null,
          });
        });
      });
    });
  });

  events.sort((a, b) => b.sortAt - a.sortAt);
  return events;
}

/** 목록에서 저장된 단일 구독 행과 Supabase 중첩(user_profiles + subscriptions[]) 형태 모두 처리 */
function resolveOwnerSubscriptionRecord(ownerSubscription) {
  if (!ownerSubscription) return null;
  if (ownerSubscription.subscriptions && Array.isArray(ownerSubscription.subscriptions)) {
    const list = ownerSubscription.subscriptions
      .filter((s) => s.status === 'active' || s.status === 'canceled')
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    return list[0] || null;
  }
  return ownerSubscription;
}

function isOwnerSubscriptionExpired(ownerSub) {
  if (!ownerSub) return false;
  return new Date(ownerSub.current_period_end) < new Date();
}

/** 캘린더 뷰: 월 첫 주 패딩(null) + 해당 월 각 일 Date (마지막 주 패딩 null) */
function buildCalendarWeekCells(monthPage) {
  if (!monthPage?.start || !monthPage?.end) return [];
  const year = monthPage.start.getFullYear();
  const month = monthPage.start.getMonth();
  const lastDay = monthPage.end.getDate();
  const firstDow = new Date(year, month, 1).getDay();
  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) {
    cells.push(new Date(year, month, d));
  }
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

/** 캘린더 한 주(7칸)에서 작업 막대: 시작·끝 열 + 겹침 없이 쌓을 lane */
function calendarDayTs(dt) {
  const x = new Date(dt);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function buildCalendarWeekBarSegments(weekCells, categories) {
  if (!categories?.length) return [];
  const weekStartTs = weekCells.find(Boolean) ? calendarDayTs(weekCells.find(Boolean)) : null;
  const weekEndDay = [...weekCells].reverse().find(Boolean);
  const weekEndTs = weekEndDay ? calendarDayTs(weekEndDay) : null;
  const segments = [];
  categories.forEach((cat) => {
    cat.tasks.forEach((task) => {
      const s = calendarDayTs(task.startDate);
      const e = calendarDayTs(task.endDate);
      let startCol = null;
      let endCol = null;
      for (let col = 0; col < 7; col++) {
        const d = weekCells[col];
        if (!d) continue;
        const t = calendarDayTs(d);
        if (t >= s && t <= e) {
          if (startCol === null) startCol = col;
          endCol = col;
        }
      }
      if (startCol !== null) {
        segments.push({
          category: cat,
          task,
          startCol,
          endCol,
          continuesFromPrev: weekStartTs !== null && s < weekStartTs,
          continuesToNext: weekEndTs !== null && e > weekEndTs,
        });
      }
    });
  });
  segments.sort((a, b) => {
    if (a.startCol !== b.startCol) return a.startCol - b.startCol;
    return b.endCol - b.startCol - (a.endCol - a.startCol);
  });
  const laneEnds = [];
  segments.forEach((seg) => {
    let lane = 0;
    while (laneEnds[lane] !== undefined && laneEnds[lane] >= seg.startCol) {
      lane += 1;
    }
    laneEnds[lane] = seg.endCol;
    seg.lane = lane;
  });
  return segments;
}

function Dashboard() {
  const { t, i18n } = useTranslation('dashboard');
  // 로그인 이후 화면 — 색인 대상에서 제외
  useSeo({ page: 'dashboard', path: '/dashboard', noindex: true });
  const location = useLocation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [activeProjectIndex, setActiveProjectIndex] = useState(0);
  /** 커뮤니티에서 복사 완료 후 대시보드 진입 시 해당 프로젝트로 포커스 */
  const [pendingSelectProjectId, setPendingSelectProjectId] = useState(null);
  const [showCreatePopup, setShowCreatePopup] = useState(false);
  const [showTaskPopup, setShowTaskPopup] = useState(false);
  // 관리자에 의해 가려진 항목 펼침 상태 — key 형식: 'post:{id}' / 'comment:{id}' / 'chat:{id}' / 'feed:{id}'
  const [hiddenRevealed, setHiddenRevealed] = useState(() => new Set());
  const toggleHiddenRevealed = (key) =>
    setHiddenRevealed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showInvitePopup, setShowInvitePopup] = useState(false);
  const [showAssignPopup, setShowAssignPopup] = useState(false);
  const [assignTarget, setAssignTarget] = useState(null);
  // 담당자 할당 팝업의 임시 선택 상태 — 체크는 여기에만 반영, 저장 시 서버에 적용
  const [assignDraft, setAssignDraft] = useState(null);
  const [selectedMembersToRemove, setSelectedMembersToRemove] = useState([]);
  const [activeMonthIndex, setActiveMonthIndex] = useState(0);
  const [showAddCategoryPopup, setShowAddCategoryPopup] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newCategoryError, setNewCategoryError] = useState('');
  const [showEditCategoryPopup, setShowEditCategoryPopup] = useState(false);
  const [editCategoryId, setEditCategoryId] = useState(null);
  const [editCategoryName, setEditCategoryName] = useState('');
  const [editCategoryError, setEditCategoryError] = useState('');
  const [newChecklistText, setNewChecklistText] = useState('');
  const [editingChecklistId, setEditingChecklistId] = useState(null);
  const [editingChecklistText, setEditingChecklistText] = useState('');
  const [showAiGuide, setShowAiGuide] = useState(false);
  const [generatingHintsTaskId, setGeneratingHintsTaskId] = useState(null);

  // 작업 생성 팝업 상태
  const [showCreateTaskPopup, setShowCreateTaskPopup] = useState(false);
  const [createTaskCategoryId, setCreateTaskCategoryId] = useState(null);
  const [newTaskData, setNewTaskData] = useState({
    name: '',
    startDate: '',
    endDate: '',
    description: '',
    checklist: [],
  });
  const [newTaskErrors, setNewTaskErrors] = useState({});
  const [newChecklistInput, setNewChecklistInput] = useState('');

  // 작업 상세 수정 상태
  const [editingTaskField, setEditingTaskField] = useState(null); // 'name', 'dates', 'detail'
  const [editingTaskValue, setEditingTaskValue] = useState(null);
  const taskDetailTextRef = useRef(null);
  const [taskDetailExpanded, setTaskDetailExpanded] = useState(false);
  const [taskDetailNeedsToggle, setTaskDetailNeedsToggle] = useState(false);
  /** 작업 상세 모달 본문: 체크리스트 ↔ 게시물 */
  const [taskModalTab, setTaskModalTab] = useState('checklist');
  const taskModalNextTabRef = useRef(null);

  const DAY_MS = 1000 * 60 * 60 * 24;
  const MONTH_PAST_BUFFER = 1200; // 프로젝트 시작 2개월 전부터
  const MONTH_FUTURE_BUFFER = 1200; // 프로젝트 종료 2개월 후까지

  // 타임라인 레이아웃 상수
  const DEFAULT_CATEGORY_WIDTH = 180; // 작업 카테고리 열 기본 너비
  const MIN_CATEGORY_WIDTH = 120; // 최소 너비
  const MAX_CATEGORY_WIDTH = 400; // 최대 너비
  const TIMELINE_ASSIGNEE_WIDTH = 80; // 담당자 열 너비

  // 열 너비 상태 (localStorage에서 복원)
  const [categoryColumnWidth, setCategoryColumnWidth] = useState(() => {
    const saved = localStorage.getItem('lansly_category_width');
    return saved ? parseInt(saved, 10) : DEFAULT_CATEGORY_WIDTH;
  });

  const TIMELINE_HEADER_WIDTH = categoryColumnWidth + TIMELINE_ASSIGNEE_WIDTH + 2; // 헤더 영역 총 너비 (+2는 border 보정)

  // 열 리사이즈 핸들러
  const [isResizing, setIsResizing] = useState(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);

  const handleResizeStart = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsResizing(true);
      startXRef.current = e.clientX;
      startWidthRef.current = categoryColumnWidth;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [categoryColumnWidth]
  );

  useEffect(() => {
    const handleResizeMove = (e) => {
      if (!isResizing) return;
      const diff = e.clientX - startXRef.current;
      const newWidth = Math.min(
        MAX_CATEGORY_WIDTH,
        Math.max(MIN_CATEGORY_WIDTH, startWidthRef.current + diff)
      );
      setCategoryColumnWidth(newWidth);
    };

    const handleResizeEnd = () => {
      if (isResizing) {
        setIsResizing(false);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        localStorage.setItem('lansly_category_width', categoryColumnWidth.toString());
      }
    };

    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);

    return () => {
      document.removeEventListener('mousemove', handleResizeMove);
      document.removeEventListener('mouseup', handleResizeEnd);
    };
  }, [isResizing, categoryColumnWidth]);

  const normalizeDate = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const today = normalizeDate(new Date());

  const formatChatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const yyyy = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${yyyy}. ${m}. ${d}. ${hh}:${min}`;
  };

  const formatStackFeedDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const yyyy = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    const hh = String(date.getHours()).padStart(2, '0');
    const min = String(date.getMinutes()).padStart(2, '0');
    return `${yyyy}. ${m}. ${d}. ${hh}:${min}`;
  };

  const formatShortDotDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const yyyy = date.getFullYear();
    const m = date.getMonth() + 1;
    const d = date.getDate();
    return `${yyyy}. ${m}. ${d}.`;
  };

  const [chatMessages, setChatMessages] = useState([]);
  const [editingChatMessageId, setEditingChatMessageId] = useState(null);
  const [editingChatMessageText, setEditingChatMessageText] = useState('');
  // 메시지 수정 시 남길 첨부 목록 — 사용자가 칩 × 누르면 여기서 제거되고, 저장 시 R2 삭제까지
  const [editingChatAttachments, setEditingChatAttachments] = useState([]);
  const [editingDmAttachments, setEditingDmAttachments] = useState([]);
  // 답글 대상 메시지 (id 만 보관 — 원문 데이터는 chatMessages 에서 lookup)
  const [replyingToId, setReplyingToId] = useState(null);
  // 메시지 점프용 — id → DOM ref
  const chatMessageRefs = useRef(new Map());
  // 잠깐 하이라이트할 메시지 id
  const [highlightedChatMessageId, setHighlightedChatMessageId] = useState(null);
  // 채팅 팝업 모드: 'project' 전체 채팅 | 'dm' 다이렉트 메시지
  const [chatMode, setChatMode] = useState('project');
  // DM 스레드 리스트 (활성 프로젝트 안에서 내가 참여 중인 모든 1:1 스레드)
  const [dmThreads, setDmThreads] = useState([]);
  // 현재 이 프로젝트 대시보드를 '실제로 보고 있는' 사용자 id 집합 (Realtime Presence). 슬랙식 온라인 점.
  const [onlineUserIds, setOnlineUserIds] = useState(() => new Set());
  // 현재 열린 DM 스레드 id (null 이면 DM 모드에서 리스트만 보임)
  const [activeDmThreadId, setActiveDmThreadId] = useState(null);
  // 활성 DM 스레드의 메시지 리스트
  const [dmMessages, setDmMessages] = useState([]);
  // 활성 DM 스레드에서 "상대방"이 마지막으로 읽은 시각 — 내 메시지의 '안 읽음' 표시용
  const [dmOtherReadAt, setDmOtherReadAt] = useState(null);
  // 상대 읽음 시각을 아직 불러오기 전에는 '안 읽음'을 그리지 않음(기본=읽음으로 간주, 깜빡임 방지)
  const [dmOtherReadLoaded, setDmOtherReadLoaded] = useState(false);
  // DM 본문 입력 + 답글 + 첨부 (전체 채팅과 동일한 패턴이지만 분리)
  const [dmInput, setDmInput] = useState('');
  const [dmReplyingToId, setDmReplyingToId] = useState(null);
  const [dmFiles, setDmFiles] = useState([]);
  const [editingDmMessageId, setEditingDmMessageId] = useState(null);
  const [editingDmMessageText, setEditingDmMessageText] = useState('');
  const dmFileInputRef = useRef(null);
  const dmEndRef = useRef(null);
  const dmMessageRefs = useRef(new Map());
  const [highlightedDmMessageId, setHighlightedDmMessageId] = useState(null);
  // 읽음 상태 — 전체 채팅 last_read_at + DM 스레드별 last_read_at
  const [projectChatReadAt, setProjectChatReadAt] = useState(null);
  // 작업 단위 read state — taskReadStateByTask[taskId] = { checklistAt, postAt }
  // 체크리스트 탭 / 게시물 탭 read 시각을 별도 추적. null = fetch 전 (unread 계산 skip).
  const [taskReadStateByTask, setTaskReadStateByTask] = useState(null);
  const [projectSnsReadAt, setProjectSnsReadAt] = useState(null);
  // read state 가 어느 프로젝트의 어느 task id 들에 대한 것인지 — activeProject.id / taskIdsSignature
  // 와 mismatch 면 stale 로 간주해 unread 계산 skip. 프로젝트 전환 직후나 categories 가 뒤늦게
  // 로드돼 taskIdsSignature 가 바뀌는 사이 OLD 데이터로 잘못 계산되는 깜빡임 방지.
  const [readStateProjectId, setReadStateProjectId] = useState(null);
  const [readStateTaskIdsSig, setReadStateTaskIdsSig] = useState(null);
  // getter 는 현재 UI 에서 직접 안 읽고 dmThreads.unreadCount 로 합산해서 사용. setter 만 유지.
  const [, setDmReadAtByThread] = useState({});
  const [newMessage, setNewMessage] = useState('');
  const [chatFiles, setChatFiles] = useState([]);
  const chatFileInputRef = useRef(null);
  const [newComments, setNewComments] = useState({});
  const [commentFiles, setCommentFiles] = useState({}); // { [postId]: File[] }
  // 어떤 게시물의 댓글 composer 팝업이 열려있는지 — null = 닫힘
  const [commentComposerPostId, setCommentComposerPostId] = useState(null);
  // 답글 대상 — 댓글의 댓글일 때만 set. composer 열 때 같이 set.
  const [replyTargetComment, setReplyTargetComment] = useState(null);
  const closeCommentComposer = () => {
    setCommentComposerPostId(null);
    setReplyTargetComment(null);
  };
  const [newPost, setNewPost] = useState('');
  const [editingComment, setEditingComment] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [newPostFiles, setNewPostFiles] = useState([]);
  const newPostFileInputRef = useRef(null);
  const dashboardSilentRefreshRef = useRef(null);
  // 탭 포커스 복귀 시 채팅 catch-up용 — 활성 프로젝트의 fetchChatMessages를 노출
  const fetchChatMessagesRef = useRef(null);
  const [newPostError, setNewPostError] = useState('');
  const [commentErrors, setCommentErrors] = useState({});
  const { user, profile } = useAuth();
  const currentUser = {
    id: user?.id,
    name: profile?.updated_display_name,
    role: '관리자',
    avatar: pickAvatar(profile?.updated_avatar_url, profile?.updated_display_name),
  };

  const [showChatPopup, setShowChatPopup] = useState(false);
  const [showAiPopup, setShowAiPopup] = useState(false);
  // 멤버 아바타 클릭 시 활동 캘린더 팝업 — 선택된 멤버 객체(또는 null)
  const [activityMember, setActivityMember] = useState(null);
  // 채팅 첨부 라이트박스 (이미지·동영상 풀스크린 viewer)
  const [chatLightbox, setChatLightbox] = useState(null);
  // 체크리스트 필터: 미완료만 보기 (로컬 저장)
  const [hideCompletedChecklist, setHideCompletedChecklist] = useState(() => {
    try {
      return localStorage.getItem('lansly:checklist-hide-completed') === '1';
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem('lansly:checklist-hide-completed', hideCompletedChecklist ? '1' : '0');
    } catch {
      // 저장 불가(쿠키 차단·시크릿 등) 시 무시
    }
  }, [hideCompletedChecklist]);
  const [isAiLoading, setIsAiLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [showPricingPopup, setShowPricingPopup] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [showTemplatePublishModal, setShowTemplatePublishModal] = useState(false);
  const [templatePublishDescription, setTemplatePublishDescription] = useState('');
  const [templatePublishCategoryId, setTemplatePublishCategoryId] = useState('');
  const [templateCategoriesList, setTemplateCategoriesList] = useState([]);
  const [templateCategoriesLoading, setTemplateCategoriesLoading] = useState(false);
  const [templatePublishDescriptionError, setTemplatePublishDescriptionError] = useState('');
  const [templatePublishCategoryError, setTemplatePublishCategoryError] = useState('');
  const [templatePublishSubmitting, setTemplatePublishSubmitting] = useState(false);
  const [showTemplateEditModal, setShowTemplateEditModal] = useState(false);
  const [templateEditDescription, setTemplateEditDescription] = useState('');
  const [templateEditCategoryId, setTemplateEditCategoryId] = useState('');
  const [templateEditDescriptionError, setTemplateEditDescriptionError] = useState('');
  const [templateEditCategoryError, setTemplateEditCategoryError] = useState('');
  const [templateEditSubmitting, setTemplateEditSubmitting] = useState(false);
  const [templateRevertSubmitting, setTemplateRevertSubmitting] = useState(false);
  // 챌린지로 내놓기 (템플릿과 동일 패턴, 카테고리는 challenge_categories 전용 테이블)
  const [challengeCategoriesList, setChallengeCategoriesList] = useState([]);
  const [challengeCategoriesLoading, setChallengeCategoriesLoading] = useState(false);
  const [showChallengePublishModal, setShowChallengePublishModal] = useState(false);
  const [challengePublishCategoryId, setChallengePublishCategoryId] = useState('');
  const [challengePublishDescription, setChallengePublishDescription] = useState('');
  const [challengePublishPrice, setChallengePublishPrice] = useState('');
  const [challengePublishCategoryError, setChallengePublishCategoryError] = useState('');
  const [challengePublishDescriptionError, setChallengePublishDescriptionError] = useState('');
  const [challengePublishPriceError, setChallengePublishPriceError] = useState('');
  const [challengePublishSubmitting, setChallengePublishSubmitting] = useState(false);
  const [challengeEditMode, setChallengeEditMode] = useState(false); // 발행 모달 재사용(수정)
  const [challengeRevertSubmitting, setChallengeRevertSubmitting] = useState(false);
  // 사이드바 프로젝트 그룹 접기/펼치기 상태 — 브라우저 localStorage 에 기억
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try {
      const raw = localStorage.getItem('lansly_sidebar_collapsed');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const toggleGroupCollapsed = useCallback((key) => {
    setCollapsedGroups((prev) => {
      const next = { ...prev, [key]: !prev[key] };
      try {
        localStorage.setItem('lansly_sidebar_collapsed', JSON.stringify(next));
      } catch {
        /* localStorage 사용 불가 시 무시 */
      }
      return next;
    });
  }, []);
  const [projectSidebarOpen, setProjectSidebarOpen] = useState(false);
  const [dashboardSidebarOpen, setDashboardSidebarOpen] = useState(false);
  const timelineContainerRef = useRef(null);
  const resizeLineRef = useRef(null);
  const hasRestoredInitialProjectRef = useRef(false);

  const selectProjectIndex = useCallback((index) => {
    setActiveProjectIndex(index);
    setProjectSidebarOpen(false);
    setDashboardSidebarOpen(false);
  }, []);

  // Polar Checkout URLs
  // Production URLs (실제 결제)
  const POLAR_CHECKOUT = {
    monthly: 'https://buy.polar.sh/polar_cl_VL1ZYMzG2lRGZ54EAQFqt0xIKPw6vDUt7JBXb1e4dcb',
    yearly: 'https://buy.polar.sh/polar_cl_8653rK7SholvZNMtuewox4L8Uz3TETZZ1zwLQ1IHT0a',
  };

  // Sandbox URLs (테스트용)
  // const POLAR_CHECKOUT = {
  //   monthly:
  //     'https://sandbox-api.polar.sh/v1/checkout-links/polar_cl_yfYiO3VD5yqy4so1i1xxevWyJ5OWwrc4pAaXj2487UL/redirect',
  //   yearly:
  //     'https://sandbox-api.polar.sh/v1/checkout-links/polar_cl_9Cx8jV7dQtndHGq8pOE2sO67eLk5Re6qrLOof1S2yB8/redirect',
  // };

  // Polar Checkout으로 이동
  const handleCheckout = (plan) => {
    const checkoutUrl = plan === 'yearly' ? POLAR_CHECKOUT.yearly : POLAR_CHECKOUT.monthly;

    // 결제 완료 후 프로필 페이지로 리다이렉트
    const successUrl = `${window.location.origin}/profile?success=true`;
    const urlWithParams = `${checkoutUrl}?customer_email=${encodeURIComponent(user.email)}&success_url=${encodeURIComponent(successUrl)}`;

    window.location.href = urlWithParams;
  };
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [isCreatingBlank, setIsCreatingBlank] = useState(false);
  const [aiProgress, setAiProgress] = useState({ text: '', icon: '🤖' });
  const createProjectAbortRef = useRef(null);
  const createProjectTaskIdRef = useRef(null);
  const [isValidating, setIsValidating] = useState(false);
  const [showValidationWarning, setShowValidationWarning] = useState(false);
  const [validationResult, setValidationResult] = useState({
    isValid: true,
    warnings: [],
    suggestions: '',
  });
  // AI 에이전트 대화(세션 메모리)
  const [aiMessages, setAiMessages] = useState([]); // [{ role:'user'|'assistant', content }]
  const [aiInput, setAiInput] = useState('');
  const [aiStaged, setAiStaged] = useState([]); // 승인 대기 변경
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiShowExamples, setAiShowExamples] = useState(true); // 예시 질문 표시 토글
  const [aiStatus, setAiStatus] = useState(''); // SSE 진행 단계 라벨
  const [showEditProjectPopup, setShowEditProjectPopup] = useState(false);
  const [editProjectIndex, setEditProjectIndex] = useState(null);
  const [editProjectName, setEditProjectName] = useState('');
  const [editProjectError, setEditProjectError] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteError, setInviteError] = useState('');
  const [pendingInvites, setPendingInvites] = useState({});
  const [showResendPopup, setShowResendPopup] = useState(false);
  const [showAllMembers, setShowAllMembers] = useState({});
  // 프로젝트 정보 패널 — '지나간 작업 안 보기' 아래 관리자 권한 설정 영역 펼치기/접기
  const [showAdminSettings, setShowAdminSettings] = useState(false);
  const [showCostReason, setShowCostReason] = useState(false);
  const [showCostEditPopup, setShowCostEditPopup] = useState(false);
  const [costEditValue, setCostEditValue] = useState('');
  const [costEditError, setCostEditError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [timelineView, setTimelineView] = useState(() => {
    try {
      const saved =
        typeof window !== 'undefined'
          ? window.localStorage.getItem(TIMELINE_VIEW_STORAGE_KEY)
          : null;
      if (saved === 'kanban' || saved === 'calendar' || saved === 'stack') return saved;
    } catch (_) {}
    return 'kanban';
  });

  // 가로 스크롤해도 리사이즈 라인을 sticky 카테고리 컬럼의 우측 경계(뷰포트 기준)에 고정.
  // 라인은 스크롤되는 테이블 안의 absolute 요소라, scrollLeft만큼 보정하지 않으면
  // 스크롤 시 고정된 카테고리 컬럼 안으로 파고든다.
  useLayoutEffect(() => {
    const container = timelineContainerRef.current;
    const line = resizeLineRef.current;
    if (!container || !line) return;
    const sync = () => {
      line.style.left = `${categoryColumnWidth + container.scrollLeft}px`;
    };
    sync();
    container.addEventListener('scroll', sync, { passive: true });
    return () => container.removeEventListener('scroll', sync);
  }, [categoryColumnWidth, timelineView]);

  // 프로젝트 생성 폼
  const [formData, setFormData] = useState(() => ({
    name: '',
    description: '',
    ...getDefaultProjectDates(),
  }));
  const [formErrors, setFormErrors] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
  });

  const activeProject = projects[activeProjectIndex];
  // 무료 플랜 개수 제한 기준: 내놓은 템플릿·월간 챌린지는 제외한 '일반 프로젝트'만 셈
  const myProjectCount = projects.filter(
    (p) => p.owner_id === user?.id && !p.is_template && !p.is_challenge
  ).length;
  const isMySubActive = subscription && new Date(subscription.current_period_end) >= new Date();

  const projectsRef = useRef(projects);
  const activeProjectRef = useRef(activeProject);
  const detailFetchSeqRef = useRef(0);
  useEffect(() => {
    projectsRef.current = projects;
  }, [projects]);
  useEffect(() => {
    activeProjectRef.current = activeProject;
  }, [activeProject]);

  useEffect(() => {
    if (!activeProject) setDashboardSidebarOpen(false);
  }, [activeProject]);

  // 프로젝트가 바뀌면 AI 어시스턴트 세션도 초기화 — 이전 프로젝트의 대화가 새 프로젝트로 새지 않게.
  useEffect(() => {
    setAiMessages([]);
    setAiInput('');
    setAiStaged([]);
    setAiError('');
    setAiStatus('');
    setAiShowExamples(true);
  }, [activeProject?.id]);

  useEffect(() => {
    try {
      window.localStorage.setItem(TIMELINE_VIEW_STORAGE_KEY, timelineView);
    } catch (_) {}
  }, [timelineView]);

  useEffect(() => {
    hasRestoredInitialProjectRef.current = false;
  }, [user?.id]);

  const fetchProjectList = useCallback(
    async (isSilent = false) => {
      if (!user) return;

      try {
        // 0. 초대 확인 및 자동 수락 처리 (내 이메일로 온 초대가 있다면 협업자로 자동 참여)
        if (user.email) {
          const { data: invites, error: inviteCheckError } = await supabase
            .from('project_invites')
            .select('*')
            .eq('email', user.email);

          if (inviteCheckError) {
            console.error('[Dashboard] Error checking invites:', inviteCheckError.message);
          }

          if (!inviteCheckError && invites && invites.length > 0) {
            for (const invite of invites) {
              // 1) 이미 협업자인지 먼저 확인 (409 에러 방지)
              const { data: existingMember } = await supabase
                .from('project_members')
                .select('*')
                .eq('project_id', invite.project_id)
                .eq('user_id', user.id)
                .maybeSingle();

              if (!existingMember) {
                const { error: memberInsertError } = await supabase.from('project_members').insert({
                  project_id: invite.project_id,
                  user_id: user.id,
                  role: '협업자',
                });

                if (memberInsertError) {
                  console.warn('[Dashboard] Member insert failed:', memberInsertError.message);
                }
              } else if (existingMember.left_at || existingMember.kicked_at) {
                // 이전에 나갔거나 내보내졌던 프로젝트라면 상태를 초기화하여 재참여 처리
                const { error: memberUpdateError } = await supabase
                  .from('project_members')
                  .update({
                    left_at: null,
                    kicked_at: null,
                    kicked_by: null,
                    joined_at: new Date(),
                  })
                  .eq('project_id', invite.project_id)
                  .eq('user_id', user.id);

                if (memberUpdateError) {
                  console.warn(
                    '[Dashboard] Member re-join update failed:',
                    memberUpdateError.message
                  );
                }
              }

              // 2) 초대 데이터 삭제 (수락 완료 처리)
              const { error: inviteDeleteError } = await supabase
                .from('project_invites')
                .delete()
                .eq('project_id', invite.project_id)
                .eq('email', user.email);

              if (inviteDeleteError) {
                console.error(
                  '[Dashboard] Error deleting invite (Check RLS!):',
                  inviteDeleteError.message
                );
              }
            }
          }
        }

        // 1. 내가 협업자로 참여 중인 모든 프로젝트 목록 가져오기 (소유 프로젝트 포함, 나간/내보내진 프로젝트 제외)
        // 프로젝트 소유자의 구독 정보를 함께 가져옵니다.
        const { data: membersInfo, error: projectsError } = await supabase
          .from('project_members')
          .select(
            `
          project_id,
          role,
          projects (
            *,
            owner_subscription:user_profiles!owner_id (
              subscriptions (*)
            )
          )
        `
          )
          .eq('user_id', user.id)
          .is('left_at', null)
          .is('kicked_at', null);

        if (projectsError) {
          console.error('[Dashboard] Error fetching project list:', projectsError.message);
          if (!isSilent) setIsLoading(false);
          return;
        }

        // 현재 사용자의 구독 정보 가져오기
        const { data: mySubData, error: subError } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .in('status', ['active', 'canceled'])
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (subError) {
          console.error('[Dashboard] Error fetching my subscription:', subError.message);
        }
        setSubscription(mySubData);

        // 가져온 협업자 정보에서 프로젝트 데이터만 추출 및 정렬 (생성일 순)
        const projectsData = (membersInfo || [])
          .filter((m) => m.projects)
          .map((m) => ({
            ...m.projects,
            myRole: m.role,
          }))
          .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

        if (projectsData) {
          /* 사이드바용 경량 목록만 먼저 적재 — 카테고리·작업 등 상세는 선택 시 fetchProjectDetail */
          const projectsWithDetails = [];

          for (const p of projectsData) {
            const ownerSubs = (p.owner_subscription?.subscriptions || [])
              .filter((s) => s.status === 'active' || s.status === 'canceled')
              .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            const ownerSub = ownerSubs[0] || null;
            const now = new Date();
            const isExpired = ownerSub ? new Date(ownerSub.current_period_end) < now : false;

            // 무료 첫 프로젝트 판정에서 내놓은 템플릿·챌린지는 제외 → 무료 플랜에서 이들은
            // '첫 프로젝트'가 아니게 되어 잠김(사용 불가), 일반 첫 프로젝트만 무료로 이용.
            const ownerProjects = projectsData
              .filter((proj) => proj.owner_id === p.owner_id && !proj.is_template && !proj.is_challenge)
              .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            const isFirstProject = ownerProjects[0]?.id === p.id;

            projectsWithDetails.push({
              id: p.id,
              owner_id: p.owner_id,
              owner_subscription: ownerSub,
              isFirstProject,
              isExpired,
              myRole: p.myRole,
              is_template: !!p.is_template,
              template_desc: p.template_desc ?? null,
              template_category_id: p.template_category_id ?? null,
              is_challenge: !!p.is_challenge,
              challenge_approved: !!p.challenge_approved,
              challenge_hidden: !!p.challenge_hidden,
              challenge_submitted_at: p.challenge_submitted_at ?? null,
              challenge_approved_at: p.challenge_approved_at ?? null,
              challenge_price: p.challenge_price ?? null,
              challenge_desc: p.challenge_desc ?? null,
              challenge_category_id: p.challenge_category_id ?? null,
              name: p.name,
              description: p.description || '',
              startDate: p.start_date ? new Date(p.start_date) : new Date(),
              endDate: p.end_date ? new Date(p.end_date) : new Date(),
              created_at: p.created_at,
              cost_unit: p.cost_unit || '원',
              updated_cost: p.updated_cost,
              hide_past_tasks: !!p.hide_past_tasks,
              admin_only_add_category: !!p.admin_only_add_category,
              admin_only_add_task: !!p.admin_only_add_task,
              admin_only_add_checklist: !!p.admin_only_add_checklist,
              admin_only_check_checklist: !!p.admin_only_check_checklist,
              ai_estimated_cost: p.ai_estimated_cost,
              ai_estimated_cost_reasons: p.ai_estimated_cost_reasons || '',
              estimatedCost: p.updated_cost
                ? `${parseInt(p.updated_cost).toLocaleString()}`
                : p.ai_estimated_cost
                  ? `${parseInt(p.ai_estimated_cost).toLocaleString()}`
                  : '0',
              categories: [],
              members: [],
              invites: [],
              detailLoaded: false,
            });
          }

          const projectsWithLimitInfo = projectsWithDetails.map((p) => {
            const isOwner = p.owner_id === user.id;
            let isLimitReached = false;
            let needsAdminSub = false;

            if (isOwner) {
              // 소유자인 경우: 첫 번째 프로젝트가 아니면서 구독이 없거나 만료된 경우 제한
              if (!p.isFirstProject) {
                if (!p.owner_subscription || p.isExpired) {
                  isLimitReached = true;
                }
              }
            } else {
              // 협업자인 경우: 관리자가 구독하지 않았거나 구독이 만료된 경우 "무조건" 제한
              if (!p.owner_subscription || p.isExpired) {
                needsAdminSub = true;
              }
            }

            return { ...p, isLimitReached, needsAdminSub };
          });

          setProjects((prev) => {
            const prevById = Object.fromEntries(prev.map((p) => [p.id, p]));
            return projectsWithLimitInfo.map((incoming) => {
              const old = prevById[incoming.id];
              if (old?.detailLoaded) {
                return {
                  ...incoming,
                  categories: old.categories,
                  members: old.members,
                  invites: old.invites,
                  detailLoaded: true,
                };
              }
              return incoming;
            });
          });

          setPendingInvites((prevInv) => {
            const ids = new Set(projectsWithLimitInfo.map((p) => p.id));
            const next = {};
            Object.keys(prevInv).forEach((id) => {
              if (ids.has(id)) next[id] = prevInv[id];
            });
            return next;
          });
        }
      } catch (error) {
        console.error('데이터를 불러오는 중 예외 발생:', error.message);
      } finally {
        if (!isSilent) setIsLoading(false);
      }
    },
    [user]
  );

  const fetchProjectDetail = useCallback(
    async (projectId, isSilent = false) => {
      if (!user?.id || !projectId) return;
      const seq = ++detailFetchSeqRef.current;

      try {
        const [
          { data: invitesData, error: invitesError },
          { data: membersData, error: membersError },
          { data: categoriesWithTasks, error: categoriesError },
        ] = await Promise.all([
          supabase.from('project_invites').select('*').eq('project_id', projectId),
          supabase
            .from('project_members')
            .select(
              `
                user_id,
                role,
                joined_at,
                user_profiles!user_id (
                  updated_display_name,
                  updated_avatar_url,
                  email
                )
              `
            )
            .eq('project_id', projectId)
            .is('left_at', null)
            .is('kicked_at', null),
          supabase
            .from('categories')
            .select(
              `
                *,
                tasks (
                  *,
                  checklists (
                    *,
                    user_profiles!completed_by (
                      updated_display_name,
                      updated_avatar_url
                    )
                  ),
                  task_assignees (
                    user_id,
                    user_profiles!user_id (
                      updated_display_name,
                      updated_avatar_url
                    )
                  ),
                  posts (
                    *,
                    user_profiles!author_id (
                      updated_display_name,
                      updated_avatar_url
                    ),
                    post_likes ( user_id ),
                    comments (
                      *,
                      user_profiles!author_id (
                        updated_display_name,
                        updated_avatar_url
                      ),
                      comment_likes ( user_id )
                    )
                  )
                )
              `
            )
            .eq('project_id', projectId)
            .order('sort_order', { ascending: true }),
        ]);

        if (seq !== detailFetchSeqRef.current) return;

        if (invitesError) {
          console.error(`프로젝트 ${projectId}의 초대를 불러오는 중 에러:`, invitesError.message);
        }
        if (membersError) {
          console.error(`프로젝트 ${projectId}의 협업자를 불러오는 중 에러:`, membersError.message);
        }
        if (categoriesError) {
          console.error(
            `프로젝트 ${projectId}의 카테고리를 불러오는 중 에러:`,
            categoriesError.message
          );
        }

        const baseRow = projectsRef.current.find((x) => x.id === projectId);
        if (!baseRow) return;

        const { mappedCategories, mappedMembers, mappedInvites } = mapProjectDashboardDetail({
          categoriesWithTasks,
          membersData,
          invitesData,
          baseRow,
          user,
          profile,
          t,
        });

        setProjects((prev) => {
          if (seq !== detailFetchSeqRef.current) return prev;
          const row = prev.find((x) => x.id === projectId);
          if (!row) return prev;

          const ownerSub = resolveOwnerSubscriptionRecord(row.owner_subscription);
          const isExpired = isOwnerSubscriptionExpired(ownerSub);

          const ownerProjects = prev
            .filter((proj) => proj.owner_id === row.owner_id && !proj.is_template && !proj.is_challenge)
            .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
          const isFirstProject = ownerProjects[0]?.id === row.id;

          const mergedBase = {
            ...row,
            owner_subscription: ownerSub,
            isFirstProject,
            isExpired,
            categories: mappedCategories,
            members: mappedMembers,
            invites: mappedInvites,
            detailLoaded: true,
          };

          const isOwner = mergedBase.owner_id === user.id;
          let isLimitReached = false;
          let needsAdminSub = false;
          if (isOwner) {
            if (!mergedBase.isFirstProject) {
              if (!mergedBase.owner_subscription || mergedBase.isExpired) {
                isLimitReached = true;
              }
            }
          } else {
            if (!mergedBase.owner_subscription || mergedBase.isExpired) {
              needsAdminSub = true;
            }
          }

          const merged = { ...mergedBase, isLimitReached, needsAdminSub };

          return prev.map((proj) => (proj.id === projectId ? merged : proj));
        });

        setPendingInvites((invPrev) => ({
          ...invPrev,
          [projectId]: mappedInvites,
        }));
      } catch (error) {
        console.error('프로젝트 상세를 불러오는 중 예외:', error.message);
      }
    },
    [user, profile, t]
  );

  const dashboardSilentRefresh = useCallback(async () => {
    await fetchProjectList(true);
    const pid = activeProjectRef.current?.id;
    if (pid) await fetchProjectDetail(pid, true);
  }, [fetchProjectList, fetchProjectDetail]);

  // ref를 항상 최신 dashboardSilentRefresh로 갱신 (Realtime 콜백에서 안정 참조용)
  dashboardSilentRefreshRef.current = dashboardSilentRefresh;

  useEffect(() => {
    fetchProjectList();
  }, [fetchProjectList]);

  /** 선택된 프로젝트만 상세 로드 (목록은 경량). 다른 프로젝트 탭 시 다시 호출됨 */
  useEffect(() => {
    if (!activeProject?.id) return;
    if (activeProject.detailLoaded) return;
    fetchProjectDetail(activeProject.id);
  }, [activeProject?.id, activeProject?.detailLoaded, fetchProjectDetail]);

  useEffect(() => {
    if (!pendingSelectProjectId || projects.length === 0) return;
    const idx = projects.findIndex((p) => p.id === pendingSelectProjectId);
    if (idx >= 0) {
      setActiveProjectIndex(idx);
      setPendingSelectProjectId(null);
      // pending이 풀린 직후 초기 복원 effect가 저장된/첫 프로젝트로 덮어쓰지 않도록 표시
      hasRestoredInitialProjectRef.current = true;
    }
  }, [projects, pendingSelectProjectId]);

  useEffect(() => {
    if (pendingSelectProjectId || projects.length === 0 || hasRestoredInitialProjectRef.current)
      return;

    const storageKey = `${LAST_SELECTED_PROJECT_KEY_PREFIX}${user?.id || 'anonymous'}`;
    const savedProjectId = localStorage.getItem(storageKey);
    const savedProjectIndex = savedProjectId
      ? projects.findIndex((p) => p.id === savedProjectId)
      : -1;

    if (savedProjectIndex >= 0) {
      setActiveProjectIndex(savedProjectIndex);
    } else {
      const firstMyProjectIndex = projects.findIndex(
        (p) => p.owner_id === user?.id && !p.is_template
      );
      setActiveProjectIndex(firstMyProjectIndex >= 0 ? firstMyProjectIndex : 0);
      if (savedProjectId) {
        localStorage.removeItem(storageKey);
      }
    }

    hasRestoredInitialProjectRef.current = true;
  }, [projects, pendingSelectProjectId, user?.id]);

  useEffect(() => {
    const importedId = location.state?.importedProjectId;
    if (!importedId) return;
    navigate(location.pathname, { replace: true, state: {} });
    setPendingSelectProjectId(importedId);
    dashboardSilentRefreshRef.current?.();
  }, [location.state?.importedProjectId, navigate, location.pathname]);

  useEffect(() => {
    if (!hasRestoredInitialProjectRef.current || !user?.id || !activeProject?.id) return;
    const storageKey = `${LAST_SELECTED_PROJECT_KEY_PREFIX}${user.id}`;
    localStorage.setItem(storageKey, activeProject.id);
  }, [activeProject?.id, user?.id]);

  // 팝업 데이터 실시간 동기화: projects 상태가 변경될 때 현재 선택된 작업 정보도 최신화
  useEffect(() => {
    if (showTaskPopup && selectedCategory && selectedTask) {
      const proj = projects[activeProjectIndex];
      if (proj) {
        const category = proj.categories?.find((c) => c.id === selectedCategory.id);
        const task = category?.tasks?.find((t) => t.id === selectedTask.id);
        if (category && task) {
          setSelectedCategory(category);
          setSelectedTask(task);
        }
      }
    }
  }, [projects, showTaskPopup, activeProjectIndex, selectedCategory, selectedTask]);

  // 실시간 DB 연동 설정 (user.id만 의존하여 동일 사용자면 재구독 방지)
  const userId = user?.id;
  useEffect(() => {
    if (!userId) return;

    const channels = [];
    let debounceTimer = null;
    let cancelled = false;

    const debouncedFetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        dashboardSilentRefreshRef.current?.();
      }, 300);
    };

    const subscribe = (channelName, table, callback) => {
      const channel = supabase
        .channel(channelName)
        .on('postgres_changes', { event: '*', schema: 'public', table }, (payload) => {
          callback?.(payload);
        })
        .subscribe();
      channels.push(channel);
    };

    // setTimeout(0)으로 React 18 StrictMode의 즉시 cleanup을 우회
    const initTimer = setTimeout(() => {
      if (cancelled) return;

      const tables = [
        'project_members',
        'project_invites',
        'subscriptions',
        'projects',
        'categories',
        'tasks',
        'checklists',
        'posts',
        'comments',
        'task_assignees',
      ];

      tables.forEach((table) => {
        subscribe(`sync_${table}_${userId}`, table, () => debouncedFetch());
      });
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(initTimer);
      if (debounceTimer) clearTimeout(debounceTimer);
      channels.forEach((channel) => {
        supabase.removeChannel(channel);
      });
    };
  }, [userId]);

  // 백그라운드 → 포그라운드 복귀 시 catch-up
  // 모바일 브라우저는 백그라운드 일정 시간 후 websocket을 끊고, Supabase Realtime은
  // 끊긴 구간의 이벤트를 replay 해주지 않음 → 60초 이상 백그라운드였다면 1회 refetch.
  // 폴링 아님 — 탭 복귀 순간에만 한 번 fetch.
  useEffect(() => {
    let lastHiddenAt = null;
    const VISIBILITY_THRESHOLD_MS = 60_000;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        lastHiddenAt = Date.now();
        return;
      }
      if (document.visibilityState === 'visible') {
        if (lastHiddenAt != null && Date.now() - lastHiddenAt > VISIBILITY_THRESHOLD_MS) {
          dashboardSilentRefreshRef.current?.();
          fetchChatMessagesRef.current?.();
        }
        lastHiddenAt = null;
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  useEffect(() => {
    if (!activeProject || !user) return;

    // 프로젝트 전환 시 이전 프로젝트 채팅 메시지가 남아 unread 가 잘못 계산되지 않도록 즉시 초기화
    setChatMessages([]);

    const fetchChatMessages = async () => {
      try {
        const { data, error } = await supabase
          .from('chat_messages')
          .select(
            `
            *,
            user_profiles!author_id (
              updated_display_name,
              updated_avatar_url
            )
          `
          )
          .eq('project_id', activeProject.id)
          .order('created_at', { ascending: true });

        if (error) throw error;

        const mappedMessages = (data || []).map((msg) => {
          const isMe = msg.author_id === user.id;
          return {
            id: msg.id,
            authorId: msg.author_id,
            author:
              msg.user_profiles?.updated_display_name ||
              (isMe
                ? profile?.updated_display_name || user?.user_metadata?.full_name
                : t('common.noName')),
            avatar: pickAvatar(
              msg.user_profiles?.updated_avatar_url || (isMe ? profile?.updated_avatar_url : null),
              msg.user_profiles?.updated_display_name ||
                (isMe
                  ? profile?.updated_display_name || user?.user_metadata?.full_name
                  : t('common.noName'))
            ),
            message: msg.message,
            attachments: Array.isArray(msg.attachments) ? msg.attachments : [],
            replyToId: msg.reply_to_id || null,
            hiddenAt: msg.hidden_at || null,
            hiddenBy: msg.hidden_by || null,
            createdAt: msg.created_at,
            time: formatChatDate(msg.created_at),
          };
        });
        setChatMessages(mappedMessages);
      } catch (error) {
        console.error('채팅 메시지를 불러오는 중 에러 발생:', error.message);
      }
    };

    fetchChatMessages();
    fetchChatMessagesRef.current = fetchChatMessages;

    // 리얼타임 구독 설정
    const channel = supabase
      .channel(`project_chat_${activeProject.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_messages',
          filter: `project_id=eq.${activeProject.id}`,
        },
        async (payload) => {
          if (payload.eventType === 'INSERT') {
            // 새 메시지는 작성자 프로필 정보를 함께 가져와야 하므로 별도로 조회
            const { data, error } = await supabase
              .from('chat_messages')
              .select(
                `
                *,
                user_profiles!author_id (
                  updated_display_name,
                  updated_avatar_url
                )
              `
              )
              .eq('id', payload.new.id)
              .single();

            if (!error && data) {
              const isMe = data.author_id === user?.id;
              const newMsgObj = {
                id: data.id,
                authorId: data.author_id,
                author:
                  data.user_profiles?.updated_display_name ||
                  (isMe
                    ? profile?.updated_display_name || user?.user_metadata?.full_name
                    : t('common.noName')),
                avatar: pickAvatar(
                  data.user_profiles?.updated_avatar_url ||
                    (isMe ? profile?.updated_avatar_url : null),
                  data.user_profiles?.updated_display_name ||
                    (isMe
                      ? profile?.updated_display_name || user?.user_metadata?.full_name
                      : t('common.noName'))
                ),
                message: data.message,
                attachments: Array.isArray(data.attachments) ? data.attachments : [],
                replyToId: data.reply_to_id || null,
                hiddenAt: data.hidden_at || null,
                hiddenBy: data.hidden_by || null,
                createdAt: data.created_at,
                time: formatChatDate(data.created_at),
              };
              setChatMessages((prev) => {
                // 이미 존재하지 않는 경우에만 추가 (중복 방지)
                if (prev.find((m) => m.id === newMsgObj.id)) return prev;
                return [...prev, newMsgObj];
              });
            }
          } else if (payload.eventType === 'UPDATE') {
            setChatMessages((prev) =>
              prev.map((msg) =>
                msg.id === payload.new.id
                  ? {
                      ...msg,
                      message: payload.new.message,
                      attachments: Array.isArray(payload.new.attachments)
                        ? payload.new.attachments
                        : msg.attachments,
                      replyToId:
                        payload.new.reply_to_id !== undefined
                          ? payload.new.reply_to_id
                          : msg.replyToId,
                      hiddenAt:
                        payload.new.hidden_at !== undefined ? payload.new.hidden_at : msg.hiddenAt,
                      hiddenBy:
                        payload.new.hidden_by !== undefined ? payload.new.hidden_by : msg.hiddenBy,
                    }
                  : msg
              )
            );
          } else if (payload.eventType === 'DELETE') {
            setChatMessages((prev) => prev.filter((msg) => msg.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      // 이 프로젝트의 fetcher가 ref에 남아 있을 때만 비움 — 다음 프로젝트가 이미 덮어쓴 경우엔 그대로 둠
      if (fetchChatMessagesRef.current === fetchChatMessages) {
        fetchChatMessagesRef.current = null;
      }
    };
    // 채널은 프로젝트 변경 시에만 재구독 — user/profile 변경마다 재구독하지 않도록 의도적으로 제외
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.id]);

  // 활성 프로젝트의 전체 채팅 read state 로딩
  useEffect(() => {
    if (!activeProject?.id || !user?.id) {
      setProjectChatReadAt(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('project_chat_read_state')
        .select('last_read_at')
        .eq('project_id', activeProject.id)
        .eq('user_id', user.id)
        .maybeSingle();
      if (!cancelled) {
        setProjectChatReadAt(data?.last_read_at || '1970-01-01T00:00:00Z');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeProject?.id, user?.id]);

  // 활성 프로젝트의 모든 task id signature — categories/tasks 가 로딩되거나 추가/삭제되면 변경 → fetch 재실행 트리거
  const taskIdsSignature = useMemo(() => {
    if (!activeProject?.categories) return '';
    return activeProject.categories
      .flatMap((c) => (c.tasks || []).map((t) => t.id))
      .sort()
      .join(',');
  }, [activeProject?.categories]);

  // 활성 프로젝트의 작업 read state + SNS read state 로딩 + realtime
  useEffect(() => {
    if (!activeProject?.id || !user?.id) {
      setTaskReadStateByTask(null);
      setProjectSnsReadAt(null);
      setReadStateProjectId(null);
      setReadStateTaskIdsSig(null);
      return undefined;
    }
    // 프로젝트(또는 task 목록) 변경 시 즉시 null 로 리셋 → 다음 fetch 완료 전까지 unread 계산 skip
    setTaskReadStateByTask(null);
    setProjectSnsReadAt(null);
    setReadStateProjectId(null);
    setReadStateTaskIdsSig(null);
    const projectId = activeProject.id;
    const userId = user.id;
    const signatureAtFetch = taskIdsSignature;
    let cancelled = false;
    (async () => {
      // SNS read state
      const { data: snsData } = await supabase
        .from('project_sns_read_state')
        .select('last_seen_at')
        .eq('project_id', projectId)
        .eq('user_id', userId)
        .maybeSingle();
      if (cancelled) return;
      setProjectSnsReadAt(snsData?.last_seen_at || '1970-01-01T00:00:00Z');

      // 작업 read state — 이 프로젝트의 task id 들에 대해
      const taskIds = signatureAtFetch ? signatureAtFetch.split(',') : [];
      if (taskIds.length === 0) {
        if (!cancelled) {
          setTaskReadStateByTask({});
          setReadStateProjectId(projectId);
          setReadStateTaskIdsSig(signatureAtFetch);
        }
        return;
      }
      const { data: trsData } = await supabase
        .from('task_read_state')
        .select('task_id, last_read_checklist_at, last_read_post_at')
        .in('task_id', taskIds)
        .eq('user_id', userId);
      if (cancelled) return;
      const map = {};
      (trsData || []).forEach((r) => {
        map[r.task_id] = {
          checklistAt: r.last_read_checklist_at,
          postAt: r.last_read_post_at,
        };
      });
      setTaskReadStateByTask(map);
      setReadStateProjectId(projectId);
      setReadStateTaskIdsSig(signatureAtFetch);
    })();

    // realtime — 본인 row 만 (RLS 가 다른 user row 차단)
    const taskRsChannel = supabase
      .channel(`task_rs_${projectId}_${userId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'task_read_state' },
        (payload) => {
          const row = payload.new || payload.old;
          if (!row || row.user_id !== userId) return;
          setTaskReadStateByTask((prev) => {
            const cur = prev || {};
            if (payload.eventType === 'DELETE') {
              const next = { ...cur };
              delete next[row.task_id];
              return next;
            }
            return {
              ...cur,
              [row.task_id]: {
                checklistAt: payload.new.last_read_checklist_at,
                postAt: payload.new.last_read_post_at,
              },
            };
          });
        }
      )
      .subscribe();

    const snsRsChannel = supabase
      .channel(`sns_rs_${projectId}_${userId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_sns_read_state',
          filter: `project_id=eq.${projectId}`,
        },
        (payload) => {
          const row = payload.new || payload.old;
          if (!row || row.user_id !== userId) return;
          if (payload.eventType === 'DELETE') {
            setProjectSnsReadAt('1970-01-01T00:00:00Z');
          } else {
            setProjectSnsReadAt(payload.new.last_seen_at);
          }
        }
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(taskRsChannel);
      supabase.removeChannel(snsRsChannel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.id, user?.id, taskIdsSignature]);

  // 체크리스트 탭 read 마크 — 작업 팝업의 체크리스트 탭을 열 때 호출
  const markTaskChecklistRead = async (taskId) => {
    if (!taskId || !user?.id) return;
    const now = new Date().toISOString();
    setTaskReadStateByTask((prev) => ({
      ...(prev || {}),
      [taskId]: { ...((prev || {})[taskId] || {}), checklistAt: now },
    }));
    try {
      const { error } = await supabase
        .from('task_read_state')
        .upsert(
          { task_id: taskId, user_id: user.id, last_read_checklist_at: now },
          { onConflict: 'task_id,user_id' }
        );
      if (error) throw error;
    } catch (e) {
      console.warn('체크리스트 read 마크 실패:', e?.message);
    }
  };

  // 게시물 탭 read 마크 — 작업 팝업의 게시물 탭을 열 때 호출
  const markTaskPostRead = async (taskId) => {
    if (!taskId || !user?.id) return;
    const now = new Date().toISOString();
    setTaskReadStateByTask((prev) => ({
      ...(prev || {}),
      [taskId]: { ...((prev || {})[taskId] || {}), postAt: now },
    }));
    try {
      const { error } = await supabase
        .from('task_read_state')
        .upsert(
          { task_id: taskId, user_id: user.id, last_read_post_at: now },
          { onConflict: 'task_id,user_id' }
        );
      if (error) throw error;
    } catch (e) {
      console.warn('게시물 read 마크 실패:', e?.message);
    }
  };

  // SNS 뷰 read 마크 — 스택 뷰 전환할 때 호출
  const markProjectSnsRead = async () => {
    if (!activeProject?.id || !user?.id) return;
    const now = new Date().toISOString();
    setProjectSnsReadAt(now);
    try {
      await supabase
        .from('project_sns_read_state')
        .upsert(
          { project_id: activeProject.id, user_id: user.id, last_seen_at: now },
          { onConflict: 'project_id,user_id' }
        );
    } catch (e) {
      console.warn('SNS read 마크 실패:', e?.message);
    }
  };

  // SNS 스택 뷰 전환 → mark
  useEffect(() => {
    if (timelineView === 'stack' && activeProject?.id && user?.id) {
      markProjectSnsRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timelineView, activeProject?.id, user?.id]);

  // 활성 프로젝트의 DM 스레드 (내가 참여 중인) 로딩 + realtime 구독
  useEffect(() => {
    if (!activeProject?.id || !user?.id) {
      setDmThreads([]);
      return undefined;
    }
    const projectId = activeProject.id;
    const userId = user.id;

    // 프로젝트 전환 시 이전 프로젝트의 DM 스레드(안 읽음 포함)가 새어 보이지 않도록 즉시 초기화.
    // (아래 비동기 재조회 완료 전/실패 시 이전 프로젝트 배지가 남는 것을 방지)
    setDmThreads([]);

    const mapThread = (row) => {
      const isAuthorA = row.participant_a === userId;
      const otherId = isAuthorA ? row.participant_b : row.participant_a;
      const otherProfile = isAuthorA ? row.b : row.a;
      const isSelf = otherId === userId; // 나와의 채팅
      return {
        id: row.id,
        projectId: row.project_id,
        otherId,
        isSelf,
        otherName: isSelf ? t('common.me') : otherProfile?.updated_display_name || t('common.noName'),
        otherAvatar: pickAvatar(
          otherProfile?.updated_avatar_url,
          isSelf ? t('common.me') : otherProfile?.updated_display_name
        ),
        createdAt: row.created_at,
        lastMessage: null,
        lastMessageAt: null,
        unreadCount: 0,
      };
    };

    const fetchDmThreads = async () => {
      try {
        const { data, error } = await supabase
          .from('dm_threads')
          .select(
            `
            *,
            a:user_profiles!participant_a (updated_display_name, updated_avatar_url),
            b:user_profiles!participant_b (updated_display_name, updated_avatar_url)
          `
          )
          .eq('project_id', projectId)
          .or(`participant_a.eq.${userId},participant_b.eq.${userId}`);
        if (error) throw error;
        const threads = (data || []).map(mapThread);

        // 각 thread 의 마지막 메시지 + 안 읽음 수 한 번에 계산
        if (threads.length > 0) {
          const ids = threads.map((t) => t.id);
          const [msgsRes, readsRes] = await Promise.all([
            supabase
              .from('dm_messages')
              .select('thread_id, content, attachments, created_at, author_id')
              .in('thread_id', ids)
              .order('created_at', { ascending: false }),
            supabase
              .from('dm_read_state')
              .select('thread_id, last_read_at')
              .in('thread_id', ids)
              .eq('user_id', userId),
          ]);
          const msgs = msgsRes.data || [];
          const reads = readsRes.data || [];
          const readMap = new Map(reads.map((r) => [r.thread_id, r.last_read_at]));
          const readSnapshot = {};
          threads.forEach((t) => {
            readSnapshot[t.id] = readMap.get(t.id) || '1970-01-01T00:00:00Z';
          });
          setDmReadAtByThread(readSnapshot);

          const lastByThread = new Map();
          const unreadByThread = new Map();
          msgs.forEach((m) => {
            if (!lastByThread.has(m.thread_id)) lastByThread.set(m.thread_id, m);
            const readAt = readMap.get(m.thread_id) || '1970-01-01T00:00:00Z';
            if (m.author_id !== userId && new Date(m.created_at) > new Date(readAt)) {
              unreadByThread.set(m.thread_id, (unreadByThread.get(m.thread_id) || 0) + 1);
            }
          });
          threads.forEach((thread) => {
            const m = lastByThread.get(thread.id);
            if (m) {
              thread.lastMessage =
                m.content ||
                (Array.isArray(m.attachments) && m.attachments.length > 0
                  ? t('common.attachmentCount', { n: m.attachments.length })
                  : '');
              thread.lastMessageAt = m.created_at;
            }
            thread.unreadCount = unreadByThread.get(thread.id) || 0;
          });
          threads.sort(
            (a, b) =>
              new Date(b.lastMessageAt || b.createdAt) - new Date(a.lastMessageAt || a.createdAt)
          );
        }
        setDmThreads(threads);
      } catch (e) {
        console.error('DM 스레드 로딩 에러:', e.message);
      }
    };

    fetchDmThreads();

    const threadChannel = supabase
      .channel(`dm_threads_${projectId}_${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'dm_threads',
          filter: `project_id=eq.${projectId}`,
        },
        async (payload) => {
          const row = payload.new;
          if (row.participant_a !== userId && row.participant_b !== userId) {
            return;
          }
          // 상대 프로필 가져와 리스트에 prepend
          const otherId = row.participant_a === userId ? row.participant_b : row.participant_a;
          const { data: prof } = await supabase
            .from('user_profiles')
            .select('updated_display_name, updated_avatar_url')
            .eq('id', otherId)
            .single();
          const isSelfRow = otherId === userId;
          const fakeRow = {
            ...row,
            // 나와의 채팅이면 양쪽 모두 내 프로필(otherId 로 가져온 prof)
            a: isSelfRow ? { ...prof } : row.participant_a === userId ? null : { ...prof },
            b: isSelfRow ? { ...prof } : row.participant_b === userId ? null : { ...prof },
          };
          setDmThreads((prev) => {
            if (prev.find((t) => t.id === row.id)) return prev;
            return [mapThread(fakeRow), ...prev];
          });
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'dm_threads',
        },
        (payload) => {
          setDmThreads((prev) => prev.filter((t) => t.id !== payload.old.id));
        }
      )
      .subscribe();

    // 안 보고 있는 thread 의 새 메시지 unread 누적용 — 모든 dm_messages INSERT 감시
    // (RLS 가 내가 참여 중인 thread 의 메시지만 통과시킴)
    const unreadChannel = supabase
      .channel(`dm_messages_unread_${projectId}_${userId}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'dm_messages' },
        (payload) => {
          const m = payload.new;
          if (!m?.thread_id) return;
          setDmThreads((prev) => {
            const idx = prev.findIndex((t) => t.id === m.thread_id);
            if (idx < 0) return prev;
            const thread = prev[idx];
            const lastMessage =
              m.content ||
              (Array.isArray(m.attachments) && m.attachments.length > 0
                ? t('common.attachmentCount', { n: m.attachments.length })
                : '');
            const next = {
              ...thread,
              lastMessage,
              lastMessageAt: m.created_at,
              unreadCount:
                m.author_id !== userId ? (thread.unreadCount || 0) + 1 : thread.unreadCount || 0,
            };
            const updated = [...prev];
            updated[idx] = next;
            updated.sort(
              (a, b) =>
                new Date(b.lastMessageAt || b.createdAt) - new Date(a.lastMessageAt || a.createdAt)
            );
            return updated;
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(threadChannel);
      supabase.removeChannel(unreadChannel);
    };
    // t 의존성 제외: 언어 전환마다 실시간 채널을 재구독하면 이벤트 누락 위험.
    // 실시간 이벤트 텍스트는 구독 시점 언어를 쓰며 프로젝트 전환 시 갱신됨.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.id, user?.id]);

  // ── 온라인 표시(Realtime Presence) ──
  // 이 프로젝트 대시보드를 '실제로 보고 있는' 동안만 presence 채널에 자신을 track.
  // 화면을 벗어나거나(언마운트·프로젝트 전환) 탭이 백그라운드로 가면 untrack → 다른 사람 화면에서 초록 점 사라짐.
  useEffect(() => {
    if (!activeProject?.id || !user?.id) {
      setOnlineUserIds(new Set());
      return undefined;
    }
    const projectId = activeProject.id;
    const myId = user.id;
    const channel = supabase.channel(`presence:project:${projectId}`, {
      config: { presence: { key: myId } },
    });

    const syncOnline = () => {
      const state = channel.presenceState();
      setOnlineUserIds(new Set(Object.keys(state)));
    };

    const trackIfVisible = () => {
      if (document.visibilityState === 'visible') {
        channel.track({ user_id: myId });
      } else {
        channel.untrack();
      }
    };

    channel
      .on('presence', { event: 'sync' }, syncOnline)
      .on('presence', { event: 'join' }, syncOnline)
      .on('presence', { event: 'leave' }, syncOnline)
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') trackIfVisible();
      });

    document.addEventListener('visibilitychange', trackIfVisible);

    return () => {
      document.removeEventListener('visibilitychange', trackIfVisible);
      supabase.removeChannel(channel);
      setOnlineUserIds(new Set());
    };
  }, [activeProject?.id, user?.id]);

  // 활성 DM 스레드 메시지 fetch + realtime
  useEffect(() => {
    if (!activeDmThreadId || !user?.id) {
      setDmMessages([]);
      setDmOtherReadAt(null);
      setDmOtherReadLoaded(false);
      return undefined;
    }
    const threadId = activeDmThreadId;
    // 스레드가 바뀌면 이전 상태를 쓰지 않도록 '읽음 미로딩'으로 초기화
    setDmOtherReadLoaded(false);
    setDmOtherReadAt(null);

    // 상대방이 이 스레드를 마지막으로 읽은 시각 — 내 메시지 '안 읽음' 판정에 사용
    const fetchOtherReadState = async () => {
      try {
        const { data, error } = await supabase
          .from('dm_read_state')
          .select('user_id, last_read_at')
          .eq('thread_id', threadId)
          .neq('user_id', user.id);
        if (error) throw error;
        const latest = (data || []).reduce((acc, r) => {
          if (!r.last_read_at) return acc;
          return !acc || new Date(r.last_read_at) > new Date(acc) ? r.last_read_at : acc;
        }, null);
        setDmOtherReadAt(latest);
        // 성공적으로 읽음 시각을 확정한 경우에만 '안 읽음' 판정을 시작(실패 시엔 표시 안 함=읽음 간주)
        setDmOtherReadLoaded(true);
      } catch (e) {
        console.warn('DM 상대 읽음 상태 로딩 실패:', e?.message);
      }
    };
    fetchOtherReadState();

    const fetchDmMessages = async () => {
      try {
        const { data, error } = await supabase
          .from('dm_messages')
          .select(
            `
            *,
            user_profiles!author_id (updated_display_name, updated_avatar_url)
          `
          )
          .eq('thread_id', threadId)
          .order('created_at', { ascending: true });
        if (error) throw error;
        const mapped = (data || []).map((msg) => {
          const isMe = msg.author_id === user.id;
          return {
            id: msg.id,
            authorId: msg.author_id,
            author:
              msg.user_profiles?.updated_display_name ||
              (isMe
                ? profile?.updated_display_name || user?.user_metadata?.full_name
                : t('common.noName')),
            avatar: pickAvatar(
              msg.user_profiles?.updated_avatar_url || (isMe ? profile?.updated_avatar_url : null),
              msg.user_profiles?.updated_display_name ||
                (isMe
                  ? profile?.updated_display_name || user?.user_metadata?.full_name
                  : t('common.noName'))
            ),
            message: msg.content,
            attachments: Array.isArray(msg.attachments) ? msg.attachments : [],
            replyToId: msg.reply_to_id || null,
            createdAt: msg.created_at,
            time: formatChatDate(msg.created_at),
          };
        });
        setDmMessages(mapped);
      } catch (e) {
        console.error('DM 메시지 로딩 에러:', e.message);
      }
    };
    fetchDmMessages();

    const msgChannel = supabase
      .channel(`dm_messages_${threadId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'dm_messages',
          filter: `thread_id=eq.${threadId}`,
        },
        async (payload) => {
          if (payload.eventType === 'INSERT') {
            const { data, error } = await supabase
              .from('dm_messages')
              .select(
                `
                *,
                user_profiles!author_id (updated_display_name, updated_avatar_url)
              `
              )
              .eq('id', payload.new.id)
              .single();
            if (!error && data) {
              const isMe = data.author_id === user.id;
              const obj = {
                id: data.id,
                authorId: data.author_id,
                author:
                  data.user_profiles?.updated_display_name ||
                  (isMe
                    ? profile?.updated_display_name || user?.user_metadata?.full_name
                    : t('common.noName')),
                avatar: pickAvatar(
                  data.user_profiles?.updated_avatar_url ||
                    (isMe ? profile?.updated_avatar_url : null),
                  data.user_profiles?.updated_display_name ||
                    (isMe
                      ? profile?.updated_display_name || user?.user_metadata?.full_name
                      : t('common.noName'))
                ),
                message: data.content,
                attachments: Array.isArray(data.attachments) ? data.attachments : [],
                replyToId: data.reply_to_id || null,
                createdAt: data.created_at,
                time: formatChatDate(data.created_at),
              };
              setDmMessages((prev) => {
                if (prev.find((m) => m.id === obj.id)) return prev;
                return [...prev, obj];
              });
            }
          } else if (payload.eventType === 'UPDATE') {
            setDmMessages((prev) =>
              prev.map((m) =>
                m.id === payload.new.id
                  ? {
                      ...m,
                      message: payload.new.content,
                      attachments: Array.isArray(payload.new.attachments)
                        ? payload.new.attachments
                        : m.attachments,
                      replyToId:
                        payload.new.reply_to_id !== undefined
                          ? payload.new.reply_to_id
                          : m.replyToId,
                    }
                  : m
              )
            );
          } else if (payload.eventType === 'DELETE') {
            setDmMessages((prev) => prev.filter((m) => m.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    // 상대방이 이 스레드를 읽으면(last_read_at 갱신) 내 메시지 '안 읽음'을 실시간으로 해제
    const readChannel = supabase
      .channel(`dm_read_${threadId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'dm_read_state',
          filter: `thread_id=eq.${threadId}`,
        },
        (payload) => {
          const row = payload.new || {};
          if (row.user_id && row.user_id !== user.id && row.last_read_at) {
            setDmOtherReadAt((prev) =>
              !prev || new Date(row.last_read_at) > new Date(prev) ? row.last_read_at : prev
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(msgChannel);
      supabase.removeChannel(readChannel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDmThreadId, user?.id]);

  // 전체 채팅을 "지금 본 것"으로 표시 (project_chat_read_state upsert)
  const markProjectChatRead = async () => {
    if (!activeProject?.id || !user?.id) return;
    const now = new Date().toISOString();
    setProjectChatReadAt(now);
    try {
      await supabase.from('project_chat_read_state').upsert(
        {
          project_id: activeProject.id,
          user_id: user.id,
          last_read_at: now,
        },
        { onConflict: 'project_id,user_id' }
      );
    } catch (e) {
      // 네트워크 실패해도 UI 는 갱신된 상태 유지 (다음 진입 시 다시 시도)
      console.warn('전체 채팅 읽음 처리 실패:', e?.message);
    }
  };

  // 특정 DM 스레드를 "지금 본 것"으로 표시
  const markDmRead = async (threadId) => {
    if (!threadId || !user?.id) return;
    const now = new Date().toISOString();
    setDmReadAtByThread((prev) => ({ ...prev, [threadId]: now }));
    setDmThreads((prev) => prev.map((t) => (t.id === threadId ? { ...t, unreadCount: 0 } : t)));
    try {
      await supabase.from('dm_read_state').upsert(
        {
          thread_id: threadId,
          user_id: user.id,
          last_read_at: now,
        },
        { onConflict: 'thread_id,user_id' }
      );
    } catch (e) {
      console.warn('DM 읽음 처리 실패:', e?.message);
    }
  };

  // DM 열기 — 멤버 활동 팝업/DM 리스트 등에서 호출. 없으면 새 thread 생성.
  const openDmWithMember = async (memberId) => {
    // memberId === user.id 면 '나와의 채팅'(self DM) — 허용.
    if (!activeProject?.id || !user?.id || !memberId) return;
    const projectId = activeProject.id;
    const userId = user.id;
    // participant_a < participant_b 정렬 (UUID 문자열 비교)
    const [pa, pb] = [userId, memberId].sort();
    try {
      // 기존 thread 확인
      const { data: existing } = await supabase
        .from('dm_threads')
        .select('id')
        .eq('project_id', projectId)
        .eq('participant_a', pa)
        .eq('participant_b', pb)
        .maybeSingle();
      let threadId = existing?.id;
      if (!threadId) {
        const { data: created, error } = await supabase
          .from('dm_threads')
          .insert([
            {
              project_id: projectId,
              participant_a: pa,
              participant_b: pb,
            },
          ])
          .select('id')
          .single();
        if (error) throw error;
        threadId = created.id;
      }
      // DM 열기 전에 다른 팝업(담당자 할당/작업 상세/프로젝트 수정 등) 전부 닫음 —
      // 안 닫으면 z-index 2000 의 팝업 뒤에 채팅 팝업(z 1200) 이 깔려 클릭 불가
      setShowAssignPopup(false);
      setShowTaskPopup(false);
      setShowEditProjectPopup(false);
      setShowAddCategoryPopup(false);
      setShowCreatePopup(false);
      setShowCostEditPopup(false);
      setShowChatPopup(true);
      setShowAiPopup(false);
      setChatMode('dm');
      setActiveDmThreadId(threadId);
    } catch (e) {
      console.error('DM 열기 에러:', e.message);
      alert(t('dm.openError'));
    }
  };

  // DM 메시지 전송
  const handleSendDmMessage = async () => {
    if (!activeDmThreadId || !user?.id) return;
    const trimmed = dmInput.trim();
    const doneFiles = dmFiles.filter((f) => f.status === 'done');
    if (!trimmed && doneFiles.length === 0) return;
    if (dmFiles.some((f) => f.status === 'pending' || f.status === 'uploading')) return;
    const attachments = doneFiles.map((f) => ({
      key: f.key,
      name: f.file.name,
      type: f.file.type,
      size: f.file.size,
    }));
    try {
      const { error } = await supabase.from('dm_messages').insert([
        {
          thread_id: activeDmThreadId,
          author_id: user.id,
          content: trimmed || null,
          reply_to_id: dmReplyingToId || null,
          attachments,
        },
      ]);
      if (error) throw error;
      setDmInput('');
      setDmReplyingToId(null);
      setDmFiles([]);
    } catch (e) {
      console.error('DM 전송 에러:', e.message);
      alert(t('dm.sendError'));
    }
  };

  const handleUpdateDmMessage = async () => {
    if (!editingDmMessageId) return;
    const trimmed = editingDmMessageText.trim();
    if (!trimmed && editingDmAttachments.length === 0) return;
    const original = dmMessages.find((m) => m.id === editingDmMessageId);
    const originalKeys = (original?.attachments || []).map((a) => a.key).filter(Boolean);
    const remainingKeys = editingDmAttachments.map((a) => a.key).filter(Boolean);
    const removedKeys = originalKeys.filter((k) => !remainingKeys.includes(k));
    try {
      const { error } = await supabase
        .from('dm_messages')
        .update({
          content: trimmed || null,
          attachments: editingDmAttachments,
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingDmMessageId);
      if (error) throw error;
      if (removedKeys.length > 0) deleteAttachments(removedKeys);
      setEditingDmMessageId(null);
      setEditingDmMessageText('');
      setEditingDmAttachments([]);
    } catch (e) {
      console.error('DM 수정 에러:', e.message);
      alert(t('dm.editError'));
    }
  };

  const handleDeleteDmMessage = async (messageId) => {
    if (!window.confirm(t('chat.deleteMessageConfirm'))) return;
    const target = dmMessages.find((m) => m.id === messageId);
    const keys = extractKeys(target?.attachments);
    try {
      const { error } = await supabase.from('dm_messages').delete().eq('id', messageId);
      if (error) throw error;
      if (keys.length > 0) deleteAttachments(keys);
    } catch (e) {
      console.error('DM 삭제 에러:', e.message);
      alert(t('dm.deleteError'));
    }
  };

  const jumpToDmMessage = (messageId) => {
    if (!messageId) return;
    const el = dmMessageRefs.current.get(messageId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedDmMessageId(messageId);
      setTimeout(() => {
        setHighlightedDmMessageId((cur) => (cur === messageId ? null : cur));
      }, 1600);
    }
  };

  // DM 열릴 때 / 새 메시지 도착 시 맨 아래로 스크롤
  useEffect(() => {
    if (showChatPopup && chatMode === 'dm' && activeDmThreadId) {
      dmEndRef.current?.scrollIntoView({ behavior: 'auto' });
    }
  }, [showChatPopup, chatMode, activeDmThreadId]);

  useEffect(() => {
    if (showChatPopup && chatMode === 'dm') {
      dmEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dmMessages]);

  // 채팅 팝업 열고 전체 채팅 보고 있을 때 → 본 시각 갱신
  useEffect(() => {
    if (showChatPopup && chatMode === 'project' && activeProject?.id && user?.id) {
      markProjectChatRead();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showChatPopup, chatMode, chatMessages.length, activeProject?.id, user?.id]);

  // 채팅 팝업 열고 특정 DM 스레드 보고 있을 때 → 본 시각 갱신
  useEffect(() => {
    if (showChatPopup && chatMode === 'dm' && activeDmThreadId && user?.id) {
      markDmRead(activeDmThreadId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showChatPopup, chatMode, activeDmThreadId, dmMessages.length, user?.id]);

  const chatEndRef = useRef(null);
  const chatMessagesRef = useRef(null);
  // 채팅 팝업이 막 열렸는지 — 열린 직후엔 부드러운 스크롤 대신 즉시 맨 아래로 점프
  const chatJustOpenedRef = useRef(false);
  const chatFabContainerRef = useRef(null);
  const chatPopupRef = useRef(null);
  const aiPopupRef = useRef(null);
  const aiThreadRef = useRef(null);

  // AI 대화 자동 스크롤: 메시지·진행상태 변경 시 항상 최신 내용으로
  useEffect(() => {
    if (!showAiPopup) return;
    const el = aiThreadRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [showAiPopup, aiMessages, aiBusy, aiStatus]);

  // 팝업이 열리거나, (DM↔전체) 탭이 '전체'로 바뀌는 순간 '즉시 맨 아래로 점프' 플래그를 켠다.
  // (탭 전환 시 DM 의 스크롤 위치 영향으로 smooth 가 중간에 멈추는 문제 방지)
  useEffect(() => {
    if (showChatPopup && chatMode === 'project') chatJustOpenedRef.current = true;
  }, [showChatPopup, chatMode]);

  // 전체 채팅 자동 스크롤 — 열린 직후/탭 전환/새 메시지 시 부드럽게 맨 아래로.
  // 네이티브 scrollTo({behavior:'smooth'}) 는 브라우저(iOS 사파리·안드로이드 크롬/삼성)별로
  // 이미지 늦은 로드나 애니메이션 중단으로 '중간에 멈추는' 문제가 있어, 매 프레임 목표(scrollHeight)를
  // 다시 계산하는 수동 rAF 스크롤 + 마지막 바닥 보정으로 안정적으로 바닥까지 내린다.
  useEffect(() => {
    if (!showChatPopup || chatMode !== 'project') return undefined;
    const el = chatMessagesRef.current;
    if (!el) return undefined;

    let rafId = 0;
    const timeouts = [];
    const animateToBottom = () => {
      const startTime = performance.now();
      const startTop = el.scrollTop;
      const duration = 320;
      const tick = (now) => {
        const t = Math.min(1, (now - startTime) / duration);
        const ease = 1 - Math.pow(1 - t, 3); // easeOutCubic
        const target = el.scrollHeight - el.clientHeight; // 매 프레임 재계산(내용 증가 대응)
        el.scrollTop = startTop + (target - startTop) * ease;
        if (t < 1) rafId = requestAnimationFrame(tick);
        else el.scrollTop = el.scrollHeight; // 정확히 바닥으로
      };
      rafId = requestAnimationFrame(tick);
    };

    if (chatJustOpenedRef.current) {
      // 아직 메시지가 로딩되지 않았으면 점프를 미룬다(플래그 유지) — 다음 메시지 변동 때 처리
      if (chatMessages.length === 0) return undefined;
      chatJustOpenedRef.current = false;
      // 레이아웃이 잡힌 뒤 애니메이션 시작
      rafId = requestAnimationFrame(() => requestAnimationFrame(animateToBottom));
      // 늦게 로드되는 이미지로 높이가 커지는 경우 대비 — 잠깐 더 바닥으로 보정
      [450, 850].forEach((ms) =>
        timeouts.push(
          setTimeout(() => {
            el.scrollTop = el.scrollHeight;
          }, ms)
        )
      );
    } else {
      // 이미 열린 상태에서 새 메시지: 부드럽게 따라 내려감
      animateToBottom();
    }

    return () => {
      cancelAnimationFrame(rafId);
      timeouts.forEach(clearTimeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showChatPopup, chatMode, chatMessages]);

  // 모바일 반응형 감지 + 넓은 화면으로 전환 시 프로젝트 드로어 닫기
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
      if (window.innerWidth > 992) {
        setProjectSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 채팅 팝업 외부 클릭 닫기 — 단, 라이트박스/팝업 등 다른 overlay 가 열려있으면 무시
  useEffect(() => {
    const handleChatOutside = (e) => {
      if (isAiLoading) return;
      const fabHas = chatFabContainerRef.current?.contains(e.target);
      const chatHas = chatPopupRef.current?.contains(e.target);
      const aiHas = aiPopupRef.current?.contains(e.target);
      const isInOtherOverlay = e.target?.closest?.(
        '.claude-popup-overlay, .claude-chat-lightbox-overlay'
      );
      if (!fabHas && !chatHas && !aiHas && !isInOtherOverlay) {
        setShowChatPopup(false);
        setShowAiPopup(false);
      }
    };
    document.addEventListener('mousedown', handleChatOutside);
    return () => document.removeEventListener('mousedown', handleChatOutside);
  }, [isAiLoading]);

  // 프로젝트 전체 기간 계산 (작업 기준)
  const projectDateRange = (() => {
    if (!activeProject || !activeProject.categories) return t('common.notCalculable');
    const allTasks = activeProject.categories.flatMap((c) => c.tasks || []);
    if (allTasks.length === 0) return t('common.notCalculable');

    const startDates = allTasks
      .map((t) => new Date(t.startDate).getTime())
      .filter((t) => !isNaN(t));
    const endDates = allTasks.map((t) => new Date(t.endDate).getTime()).filter((t) => !isNaN(t));

    if (startDates.length === 0 || endDates.length === 0) return t('common.notCalculable');

    const minStart = new Date(Math.min(...startDates));
    const maxEnd = new Date(Math.max(...endDates));

    return `${minStart.toLocaleDateString('ko-KR')} ~ ${maxEnd.toLocaleDateString('ko-KR')}`;
  })();

  const handleAddCategory = async () => {
    if (!activeProject) return;
    const name = (newCategoryName || '').trim();
    if (!name) {
      setNewCategoryError(t('category.nameRequired'));
      return;
    }

    const exists = (activeProject.categories || []).some((p) => (p.name || '').trim() === name);
    if (exists) {
      setNewCategoryError(t('category.nameDuplicate'));
      return;
    }

    try {
      // sort_order 가 0 부터 연속이 아닐 수 있음 (중간 삭제 등) — 항상 MAX + 1 로 맨 뒤에 추가
      const existingMaxSortOrder = (activeProject.categories || []).reduce(
        (max, c) => (typeof c.sort_order === 'number' && c.sort_order > max ? c.sort_order : max),
        -1
      );
      const { data, error } = await supabase
        .from('categories')
        .insert([
          {
            project_id: activeProject.id,
            name: name,
            created_by: user.id,
            sort_order: existingMaxSortOrder + 1,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      if (data) {
        const newCategory = {
          id: data.id,
          name: data.name,
          createdBy: data.created_by,
          sort_order: data.sort_order,
          status: '대기',
          startDate: new Date(activeProject.startDate),
          endDate: new Date(activeProject.endDate),
          progress: 0,
          assignees: [],
          tasks: [],
        };

        setProjects((prev) =>
          prev.map((p, idx) =>
            idx === activeProjectIndex ? { ...p, categories: [...p.categories, newCategory] } : p
          )
        );

        setShowAddCategoryPopup(false);
        setNewCategoryName('');
        setNewCategoryError('');
        // 새 작업 추가 팝업이 열린 채 카테고리를 추가했다면 방금 만든 카테고리를 자동 선택
        if (showCreateTaskPopup) {
          setCreateTaskCategoryId(data.id);
          setNewTaskErrors((prev) => ({ ...prev, category: '' }));
        }
      }
    } catch (error) {
      console.error('카테고리 추가 에러:', error.message);
      alert(t('category.addError'));
    }
  };

  // 카테고리 수정 핸들러
  const handleEditCategory = async () => {
    if (!activeProject || !editCategoryId) return;
    const name = (editCategoryName || '').trim();
    if (!name) {
      setEditCategoryError(t('category.nameRequired'));
      return;
    }

    const current = (activeProject.categories || []).find((p) => p.id === editCategoryId);
    if (current && (current.name || '').trim() === name) {
      setEditCategoryError(t('common.nameSameError'));
      return;
    }

    const exists = (activeProject.categories || []).some(
      (p) => p.id !== editCategoryId && (p.name || '').trim() === name
    );
    if (exists) {
      setEditCategoryError(t('category.nameDuplicate'));
      return;
    }

    try {
      const { error } = await supabase
        .from('categories')
        .update({
          name,
          updated_at: new Date(),
          updated_by: user.id,
        })
        .eq('id', editCategoryId);

      if (error) throw error;

      setProjects((prev) =>
        prev.map((p, idx) =>
          idx === activeProjectIndex
            ? {
                ...p,
                categories: p.categories.map((category) =>
                  category.id === editCategoryId ? { ...category, name } : category
                ),
              }
            : p
        )
      );

      setShowEditCategoryPopup(false);
      setEditCategoryId(null);
      setEditCategoryName('');
      setEditCategoryError('');
    } catch (error) {
      console.error('카테고리 수정 에러:', error.message);
      alert(t('category.editError'));
    }
  };

  // 작업 삭제 핸들러
  const handleDeleteTask = async (categoryId, taskId) => {
    if (!activeProject) return;
    if (!window.confirm(t('task.deleteConfirm'))) return;

    try {
      const { error } = await supabase.from('tasks').delete().eq('id', taskId);

      if (error) throw error;

      setProjects((prev) =>
        prev.map((p, idx) =>
          idx === activeProjectIndex
            ? {
                ...p,
                categories: p.categories.map((category) =>
                  category.id === categoryId
                    ? { ...category, tasks: category.tasks.filter((task) => task.id !== taskId) }
                    : category
                ),
              }
            : p
        )
      );
    } catch (error) {
      console.error('작업 삭제 에러:', error.message);
      alert(t('task.deleteError'));
    }
  };

  // 카테고리 삭제 핸들러
  const handleDeleteCategory = async (categoryId) => {
    if (!activeProject) return;
    if (!window.confirm(t('category.deleteConfirm')))
      return;

    try {
      const { error } = await supabase.from('categories').delete().eq('id', categoryId);

      if (error) throw error;

      setProjects((prev) =>
        prev.map((p, idx) =>
          idx === activeProjectIndex
            ? { ...p, categories: p.categories.filter((category) => category.id !== categoryId) }
            : p
        )
      );
    } catch (error) {
      console.error('카테고리 삭제 에러:', error.message);
      alert(t('category.deleteError'));
    }
  };

  // 카테고리에 작업 추가 핸들러
  // 카테고리에 작업 추가 팝업 열기
  const handleAddTaskToCategory = (categoryId, presetDate) => {
    if (!activeProject) return;
    const day = formatLocalDateInput(presetDate ? new Date(presetDate) : new Date());

    setCreateTaskCategoryId(categoryId);
    setNewTaskData({
      name: '',
      startDate: day,
      endDate: day,
      description: '',
      checklist: [],
    });
    setNewTaskErrors({});
    setNewChecklistInput('');
    setEditingChecklistId(null);
    setEditingChecklistText('');
    setShowCreateTaskPopup(true);
  };

  const closeCreateTaskPopup = () => {
    setShowCreateTaskPopup(false);
    setNewTaskData({ name: '', startDate: '', endDate: '', description: '', checklist: [] });
    setCreateTaskCategoryId(null);
    setNewTaskErrors({});
    setNewChecklistInput('');
    setEditingChecklistId(null);
    setEditingChecklistText('');
  };

  // 새 작업 제출 핸들러
  const handleSubmitNewTask = async () => {
    const errors = {};
    const availableCategories = activeProject?.categories || [];
    if (availableCategories.length === 0) {
      errors.category = t('task.categoryFirst');
    } else if (!createTaskCategoryId) {
      errors.category = t('task.categoryRequired');
    }
    if (!newTaskData.name.trim()) errors.name = t('task.nameRequired');
    if (!newTaskData.startDate) errors.startDate = t('task.startRequired');
    if (!newTaskData.endDate) errors.endDate = t('task.endRequired');

    if (
      newTaskData.startDate &&
      newTaskData.endDate &&
      new Date(newTaskData.startDate) > new Date(newTaskData.endDate)
    ) {
      errors.endDate = t('task.endBeforeStart');
    }

    if (Object.keys(errors).length > 0) {
      setNewTaskErrors(errors);
      return;
    }

    try {
      // 1. tasks 테이블에 삽입
      const { data: taskData, error: taskError } = await supabase
        .from('tasks')
        .insert([
          {
            category_id: createTaskCategoryId,
            name: newTaskData.name,
            description: newTaskData.description?.trim() || null,
            start_date: newTaskData.startDate,
            end_date: newTaskData.endDate,
            created_by: user.id,
          },
        ])
        .select()
        .single();

      if (taskError) throw taskError;

      let finalChecklist = [];

      // 2. checklists 데이터가 있으면 삽입
      if (newTaskData.checklist.length > 0 && taskData) {
        const checklistToInsert = newTaskData.checklist.map((item) => ({
          task_id: taskData.id,
          name: item.text,
          created_by: user.id,
          is_completed: false,
        }));

        const { data: clData, error: clError } = await supabase
          .from('checklists')
          .insert(checklistToInsert)
          .select();

        if (clError) throw clError;

        if (clData) {
          finalChecklist = clData.map((c) => ({
            id: c.id,
            text: c.name,
            createdBy: c.created_by,
            createdAt: c.created_at,
            completed: c.is_completed || false,
            completedAt: c.completed_at,
            completedBy: c.completed_by,
          }));
        }
      }

      const newTask = {
        id: taskData.id,
        name: taskData.name,
        createdBy: taskData.created_by,
        startDate: new Date(taskData.start_date),
        endDate: new Date(taskData.end_date),
        progress: finalChecklist.length > 0 ? 0 : 100,
        assignees: [],
        checklist: finalChecklist,
        posts: [],
        description: taskData.description,
      };

      setProjects((prev) =>
        prev.map((p, idx) =>
          idx === activeProjectIndex
            ? {
                ...p,
                categories: p.categories.map((ph) =>
                  ph.id === createTaskCategoryId ? { ...ph, tasks: [...ph.tasks, newTask] } : ph
                ),
              }
            : p
        )
      );

      setShowCreateTaskPopup(false);
      setNewTaskData({ name: '', startDate: '', endDate: '', description: '', checklist: [] });
      setCreateTaskCategoryId(null);
    } catch (error) {
      console.error('작업 생성 에러:', error.message);
      alert(t('task.createError'));
    }
  };

  // 팝업 내 체크리스트 추가
  const handleAddChecklistInPopup = () => {
    const trimmed = newChecklistInput.trim();
    if (!trimmed) return;
    setNewTaskData((prev) => ({
      ...prev,
      checklist: [
        {
          id: `cl-temp-${Date.now()}`,
          text: trimmed,
          completed: false,
          createdBy: user?.id,
        },
        ...prev.checklist,
      ],
    }));
    setNewChecklistInput('');
  };

  // 팝업 내 체크리스트 삭제
  const handleDeleteChecklistInPopup = (id) => {
    if (!window.confirm(t('common.deleteConfirmShort'))) return;
    setNewTaskData((prev) => ({
      ...prev,
      checklist: prev.checklist.filter((item) => item.id !== id),
    }));
  };

  // 팝업 내 체크리스트 수정
  const handleUpdateChecklistInPopup = () => {
    if (!editingChecklistText.trim() || !editingChecklistId) return;
    setNewTaskData((prev) => ({
      ...prev,
      checklist: prev.checklist.map((item) =>
        item.id === editingChecklistId ? { ...item, text: editingChecklistText } : item
      ),
    }));
    setEditingChecklistId(null);
    setEditingChecklistText('');
  };

  // 작업 상세 필드 수정 시작
  const handleStartEditTaskField = (field, value) => {
    setEditingTaskField(field);
    setEditingTaskValue(value);
  };

  // 작업 상세 필드 저장
  const handleSaveTaskField = async () => {
    if (!selectedTask || !selectedCategory || !editingTaskField) return;

    let updatedValue = editingTaskValue;
    let updatePayload = {};

    if (editingTaskField === 'dates') {
      const { startDate, endDate } = editingTaskValue;
      if (!startDate || !endDate) {
        alert(t('task.dateRequired'));
        return;
      }
      if (new Date(startDate) > new Date(endDate)) {
        alert(t('task.endBeforeStart'));
        return;
      }
      updatePayload = { start_date: startDate, end_date: endDate };
    } else if (editingTaskField === 'name') {
      if (!editingTaskValue.trim()) {
        alert(t('task.nameRequired'));
        return;
      }
      updatePayload = { name: editingTaskValue.trim() };
    } else if (editingTaskField === 'detail') {
      updatePayload = { description: editingTaskValue?.trim() || null };
    }

    try {
      const { error } = await supabase
        .from('tasks')
        .update({
          ...updatePayload,
          updated_at: new Date(),
          updated_by: user.id,
        })
        .eq('id', selectedTask.id);

      if (error) throw error;

      let nextSelectedTask = null;

      setProjects((prev) =>
        prev.map((p, idx) => {
          if (idx !== activeProjectIndex) return p;
          return {
            ...p,
            categories: p.categories.map((category) => {
              if (category.id !== selectedCategory.id) return category;
              return {
                ...category,
                tasks: category.tasks.map((task) => {
                  if (task.id !== selectedTask.id) return task;

                  let newTask = { ...task };
                  if (editingTaskField === 'name') newTask.name = updatedValue;
                  else if (editingTaskField === 'dates') {
                    newTask.startDate = new Date(updatedValue.startDate);
                    newTask.endDate = new Date(updatedValue.endDate);
                  } else if (editingTaskField === 'detail') newTask.description = updatedValue;

                  nextSelectedTask = newTask;
                  return newTask;
                }),
              };
            }),
          };
        })
      );

      if (nextSelectedTask) {
        setSelectedTask(nextSelectedTask);
      }

      setEditingTaskField(null);
      setEditingTaskValue(null);
    } catch (error) {
      console.error('작업 수정 에러:', error.message);
      alert(t('task.editError'));
    }
  };

  // 전체 진행률 계산
  const calculateOverallProgress = (project) => {
    if (!project) return 0;
    let totalTasks = 0;
    let completedProgress = 0;
    project.categories.forEach((category) => {
      category.tasks.forEach((task) => {
        totalTasks++;
        completedProgress += task.progress;
      });
    });
    return totalTasks > 0 ? Math.round(completedProgress / totalTasks) : 0;
  };

  // 총 작업 시간 계산
  const calculateTotalHours = (project) => {
    if (!project) return { completed: 0, total: 0 };
    let total = 0;
    let completed = 0;
    project.categories.forEach((category) => {
      category.tasks.forEach((task) => {
        const taskDays = Math.ceil(
          (new Date(task.endDate) - new Date(task.startDate)) / (1000 * 60 * 60 * 24)
        );
        const taskHours = taskDays * 8; // 하루 8시간 기준
        total += taskHours;
        completed += taskHours * (task.progress / 100);
      });
    });
    return { completed: Math.round(completed), total };
  };

  // 프로젝트 입력값 검증 (AI 검증)
  const handleValidateProject = async () => {
    if (isCreatingProject || isValidating) return;

    const errors = {
      name: !formData.name.trim() ? t('project.nameRequired') : '',
      description: !formData.description.trim() ? t('project.overviewRequired') : '',
      startDate: !formData.startDate ? t('project.startRequired') : '',
      endDate: !formData.endDate ? t('project.endRequired') : '',
    };

    if (!errors.startDate && !errors.endDate) {
      const start = new Date(formData.startDate);
      const end = new Date(formData.endDate);
      if (start > end) {
        errors.endDate = t('project.endBeforeStart');
      }
    }

    setFormErrors(errors);
    if (errors.name || errors.description || errors.startDate || errors.endDate) return;

    setIsValidating(true);

    try {
      const result = await validateProjectInput(
        {
          name: formData.name,
          description: formData.description,
          startDate: formData.startDate,
          endDate: formData.endDate,
        },
        user.id
      );

      setValidationResult(result);

      // 경고가 있으면 경고 팝업 표시
      if (result.warnings.length > 0 || result.suggestions) {
        setShowValidationWarning(true);
      } else {
        // 문제 없으면 바로 프로젝트 생성 진행
        proceedWithProjectCreation();
      }
    } catch (error) {
      console.error('[Dashboard] 검증 실패:', error);
      // 검증 실패 시에도 프로젝트 생성 진행 (검증은 선택적)
      proceedWithProjectCreation();
    } finally {
      setIsValidating(false);
    }
  };

  // 경고 무시하고 프로젝트 생성 진행
  const handleProceedAnyway = () => {
    setShowValidationWarning(false);
    proceedWithProjectCreation();
  };

  // 경고 확인 후 입력 수정
  const handleModifyInput = () => {
    setShowValidationWarning(false);
  };

  // 실제 프로젝트 생성 로직
  const proceedWithProjectCreation = async () => {
    if (isCreatingProject) return;

    setIsCreatingProject(true);
    setAiProgress({ text: t('project.aiScheduling'), icon: '🤖' });

    // AbortController 생성
    const abortController = new AbortController();
    createProjectAbortRef.current = abortController;
    createProjectTaskIdRef.current = null;

    try {
      // 1. Dify AI로 스케줄 생성
      const scheduleData = await generateProjectSchedule(
        {
          name: formData.name,
          description: formData.description,
          startDate: formData.startDate,
          endDate: formData.endDate,
        },
        user.id,
        (progressMessage) => {
          setAiProgress(progressMessage);
        },
        {
          signal: abortController.signal,
          onTaskId: (taskId) => {
            createProjectTaskIdRef.current = taskId;
          },
        }
      );

      // 2. Supabase에 저장
      setAiProgress({ text: t('project.saving'), icon: '💾' });
      const savedProject = await createProjectInDB(scheduleData, user.id);

      // 3. 프로젝트 목록에 추가
      const myOwnedProjectsCount = projects.filter(
        (p) => p.owner_id === user.id && !p.is_template && !p.is_challenge
      ).length;
      let isLimitReached = false;
      if (myOwnedProjectsCount >= 1) {
        if (
          !subscription ||
          (subscription && new Date(subscription.current_period_end) < new Date())
        ) {
          isLimitReached = true;
        }
      }

      // Dashboard용 구조로 매핑
      const mappedProject = {
        id: savedProject.id,
        owner_id: savedProject.owner_id,
        name: savedProject.name,
        description: savedProject.description || '',
        startDate: savedProject.start_date ? new Date(savedProject.start_date) : new Date(),
        endDate: savedProject.end_date ? new Date(savedProject.end_date) : new Date(),
        ai_estimated_cost: savedProject.ai_estimated_cost,
        ai_estimated_cost_reasons: savedProject.ai_estimated_cost_reasons,
        categories: savedProject.categories.map((cat) => ({
          ...cat,
          startDate: cat.tasks?.[0]?.start_date ? new Date(cat.tasks[0].start_date) : new Date(),
          endDate: cat.tasks?.[cat.tasks.length - 1]?.end_date
            ? new Date(cat.tasks[cat.tasks.length - 1].end_date)
            : new Date(),
          progress: 0,
          tasks: cat.tasks.map((task) => ({
            ...task,
            startDate: task.start_date ? new Date(task.start_date) : new Date(),
            endDate: task.end_date ? new Date(task.end_date) : new Date(),
            progress: 0,
            assignees: [],
            posts: [],
            // checklists -> checklist 변환 (DB 스키마 -> Dashboard 구조)
            checklist: (task.checklists || []).map((cl) => ({
              id: cl.id,
              text: cl.name,
              createdAt: cl.created_at,
              completed: cl.is_completed || false,
              completedAt: cl.completed_at ? new Date(cl.completed_at) : null,
            })),
          })),
        })),
        members: [
          {
            id: user.id,
            name: currentUser.name,
            role: '관리자',
            avatar: currentUser.avatar,
            email: user.email,
          },
        ],
        invites: [],
        myRole: '관리자',
        detailLoaded: true,
        isLimitReached,
      };

      setProjects((prev) => [...prev, mappedProject]);
      setActiveProjectIndex(projects.length);
      setShowCreatePopup(false);
      setFormData({ name: '', description: '', ...getDefaultProjectDates() });
      setFormErrors({ name: '', description: '', startDate: '', endDate: '' });
    } catch (error) {
      // 사용자가 취소한 경우 에러 메시지 표시하지 않음
      if (error.name === 'AbortError') {
        return;
      }
      console.error('[Dashboard] 프로젝트 생성 실패:', error);
      alert(t('project.createError'));
    } finally {
      createProjectAbortRef.current = null;
      createProjectTaskIdRef.current = null;
      setIsCreatingProject(false);
      setAiProgress({ text: '', icon: '🤖' });
    }
  };

  const handleCreateBlankProject = async () => {
    if (isCreatingProject || isCreatingBlank) return;

    const name = (formData.name || '').trim();
    if (!name) {
      setFormErrors((prev) => ({ ...prev, name: t('project.nameRequired') }));
      return;
    }

    setIsCreatingBlank(true);

    try {
      const newProjectData = {
        owner_id: user.id,
        name: name,
        description: formData.description?.trim() || null,
        start_date: formData.startDate ? new Date(formData.startDate) : null,
        end_date: formData.endDate ? new Date(formData.endDate) : null,
        cost_unit: '원',
      };

      const { data, error } = await supabase
        .from('projects')
        .insert([newProjectData])
        .select()
        .single();

      if (error) throw error;

      if (data) {
        // 프로젝트 협업자 테이블에 생성자 추가
        const { error: memberError } = await supabase.from('project_members').insert([
          {
            project_id: data.id,
            user_id: user.id,
            role: '관리자',
            joined_at: new Date(),
          },
        ]);

        if (memberError) throw memberError;

        const newMappedProject = {
          id: data.id,
          owner_id: data.owner_id,
          name: data.name,
          description: data.description || '',
          startDate: data.start_date ? new Date(data.start_date) : new Date(),
          endDate: data.end_date ? new Date(data.end_date) : new Date(),
          created_at: data.created_at,
          estimatedCost: '0원',
          cost_unit: '원',
          categories: [],
          invites: [],
          members: [
            {
              id: user.id,
              name: currentUser.name,
              role: '관리자',
              avatar: currentUser.avatar,
              email: user.email,
            },
          ],
          myRole: '관리자',
          detailLoaded: true,
        };

        // 내 프로젝트 개수 체크
        const myOwnedProjectsCount = projects.filter(
        (p) => p.owner_id === user.id && !p.is_template && !p.is_challenge
      ).length;
        let isLimitReached = false;
        if (myOwnedProjectsCount >= 1) {
          if (
            !subscription ||
            (subscription && new Date(subscription.current_period_end) < new Date())
          ) {
            isLimitReached = true;
          }
        }

        setProjects((prev) => [...prev, { ...newMappedProject, isLimitReached }]);
        setActiveProjectIndex(projects.length);
        setShowCreatePopup(false);
        setFormData({ name: '', description: '', ...getDefaultProjectDates() });
        setFormErrors({ name: '', description: '', startDate: '', endDate: '' });
      }
    } catch (error) {
      console.error('프로젝트 생성 에러:', error.message);
      alert(t('project.createErrorShort'));
    } finally {
      setIsCreatingBlank(false);
    }
  };

  // 프로젝트 삭제 (관리자만)
  const handleDeleteProject = async (index) => {
    const targetProject = projects[index];
    if (!targetProject) return;

    if (window.confirm(t('project.deleteConfirm', { name: targetProject.name }))) {
      try {
        const { error } = await supabase.from('projects').delete().eq('id', targetProject.id);

        if (error) throw error;

        const remainingProjects = projects.filter((_, i) => i !== index);

        // 삭제 후 남은 프로젝트들에 대해 isLimitReached 다시 계산
        const updatedProjects = remainingProjects.map((p) => {
          let isLimitReached = false;
          if (!p.isFirstProject) {
            if (!p.owner_subscription || p.isExpired) {
              isLimitReached = true;
            }
          }
          return { ...p, isLimitReached };
        });

        setProjects(updatedProjects);
        setActiveProjectIndex(Math.max(0, activeProjectIndex - 1));
      } catch (error) {
        console.error('프로젝트 삭제 에러:', error.message);
        alert(t('project.deleteError'));
      }
    }
  };

  const handleOpenEditProject = (index) => {
    const target = projects[index];
    if (!target) return;
    setEditProjectIndex(index);
    setEditProjectName(target.name);
    setEditProjectError('');
    setShowEditProjectPopup(true);
  };

  const handleSaveProjectName = async () => {
    if (!editProjectName.trim()) {
      setEditProjectError(t('project.nameRequired'));
      return;
    }

    const targetProject = projects[editProjectIndex];
    if (!targetProject) return;

    if (editProjectName.trim() === (targetProject.name || '').trim()) {
      setEditProjectError(t('common.nameSameError'));
      return;
    }

    try {
      const { error } = await supabase
        .from('projects')
        .update({
          name: editProjectName.trim(),
          updated_at: new Date(),
          updated_by: user.id,
        })
        .eq('id', targetProject.id);

      if (error) throw error;

      setProjects(
        projects.map((p, idx) =>
          idx === editProjectIndex ? { ...p, name: editProjectName.trim() } : p
        )
      );
      setShowEditProjectPopup(false);
      setEditProjectIndex(null);
      setEditProjectName('');
      setEditProjectError('');
    } catch (error) {
      console.error('프로젝트 수정 에러:', error.message);
      alert(t('project.editError'));
    }
  };

  const isValidInviteEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const handleInviteSubmit = async () => {
    const raw = inviteEmail.trim();
    if (!raw) {
      setInviteError(t('invite.emailRequired'));
      return;
    }
    if (!isValidInviteEmail(raw)) {
      setInviteError(t('invite.emailInvalid'));
      return;
    }
    const fullEmail = raw.toLowerCase();

    if (!activeProject) return;

    const current = pendingInvites[activeProject.id] || [];
    const isMember = activeProject?.members?.some((m) => m.email === fullEmail);
    const isPending = current.some((inv) => inv.email === fullEmail);

    if (isMember || isPending) {
      setInviteError(t('invite.emailDuplicate'));
      return;
    }

    try {
      const { error } = await supabase.from('project_invites').insert({
        project_id: activeProject.id,
        email: fullEmail,
        invited_by: user.id,
      });

      if (error) throw error;

      const newInvite = {
        id: fullEmail,
        email: fullEmail,
        status: 'pending',
        lastSent: Date.now(),
      };
      setPendingInvites((prev) => ({
        ...prev,
        [activeProject.id]: [...(prev[activeProject.id] || []), newInvite],
      }));
      setInviteError('');
      setInviteEmail('');
    } catch (error) {
      console.error('초대 에러:', error.message);
      setInviteError(t('invite.error'));
    }
  };

  const handleCancelInvite = async (inviteId) => {
    if (!activeProject) return;
    if (!window.confirm(t('invite.cancelConfirm'))) return;

    try {
      const { error } = await supabase
        .from('project_invites')
        .delete()
        .eq('project_id', activeProject.id)
        .eq('email', inviteId);

      if (error) throw error;

      setPendingInvites((prev) => {
        const list = prev[activeProject.id] || [];
        const filtered = list.filter((inv) => inv.id !== inviteId);
        return { ...prev, [activeProject.id]: filtered };
      });
    } catch (error) {
      console.error('초대 취소 에러:', error.message);
      alert(t('invite.cancelError'));
    }
  };

  const handleToggleMemberSelection = (memberId) => {
    setSelectedMembersToRemove((prev) =>
      prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]
    );
  };

  const handleRemoveSelectedMembers = async () => {
    if (!activeProject || selectedMembersToRemove.length === 0) return;

    if (!window.confirm(t('member.removeConfirm', { n: selectedMembersToRemove.length })))
      return;

    try {
      const { error } = await supabase
        .from('project_members')
        .update({
          kicked_at: new Date().toISOString(),
          kicked_by: user.id,
        })
        .eq('project_id', activeProject.id)
        .in('user_id', selectedMembersToRemove);

      if (error) throw error;

      setProjects((prevProjects) => {
        return prevProjects.map((project) => {
          if (project.id === activeProject.id) {
            return {
              ...project,
              members: project.members.filter(
                (member) => !selectedMembersToRemove.includes(member.id)
              ),
            };
          }
          return project;
        });
      });
      setSelectedMembersToRemove([]);
    } catch (error) {
      console.error('협업자 내보내기 에러:', error.message);
      alert(t('member.removeError'));
    }
  };

  const handleCloseInvitePopup = () => {
    setShowInvitePopup(false);
    setSelectedMembersToRemove([]);
  };

  const formatNumberWithComma = (value) => {
    const digits = (value || '').toString().replace(/[^\d]/g, '');
    if (!digits) return '';
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  const handleCostEditOpen = () => {
    if (!activeProject) return;
    if (!canUserManageProjectAi(activeProject, user)) return;
    setCostEditValue(formatNumberWithComma(activeProject.estimatedCost));
    setCostEditError('');
    setShowCostEditPopup(true);
  };

  const handleCostSave = async () => {
    const digits = (costEditValue || '').replace(/[^\d]/g, '');
    if (!digits) {
      setCostEditError(t('cost.numberRequired'));
      return;
    }

    if (!activeProject) return;

    try {
      const updatedCost = parseInt(digits);
      const { error } = await supabase
        .from('projects')
        .update({
          updated_cost: updatedCost,
          updated_at: new Date(),
          updated_by: user.id,
        })
        .eq('id', activeProject.id);

      if (error) throw error;

      const formatted = formatNumberWithComma(digits);
      setProjects((prev) =>
        prev.map((p, idx) =>
          idx === activeProjectIndex
            ? {
                ...p,
                updated_cost: updatedCost,
                estimatedCost: formatted,
              }
            : p
        )
      );

      setShowCostEditPopup(false);
      setCostEditError('');
    } catch (error) {
      console.error('비용 수정 에러:', error.message);
      alert(t('cost.editError'));
    }
  };

  // 프로젝트 boolean 설정 토글 (지나간 작업/관리자 전용 권한 등) — 관리자/소유자만, 낙관적 업데이트.
  const handleToggleProjectFlag = async (column, nextValue) => {
    if (!activeProject?.id || !column) return;
    if (!canUserManageProjectAi(activeProject, user)) return;
    const targetProjectId = activeProject.id;
    setProjects((prev) =>
      prev.map((p) => (p.id === targetProjectId ? { ...p, [column]: nextValue } : p))
    );
    try {
      const { error } = await supabase
        .from('projects')
        .update({ [column]: nextValue, updated_at: new Date().toISOString() })
        .eq('id', targetProjectId);
      if (error) throw error;
    } catch (e) {
      setProjects((prev) =>
        prev.map((p) => (p.id === targetProjectId ? { ...p, [column]: !nextValue } : p))
      );
      console.error(`프로젝트 설정(${column}) 토글 에러:`, e?.message || e);
      alert(t('settings.error'));
    }
  };

  // 체크리스트 토글 (완료 일시 기록)
  const handleToggleChecklist = async (categoryId, taskId, checklistId) => {
    const now = new Date().toISOString();

    // 현재 체크리스트 상태 확인
    const targetProject = projects[activeProjectIndex];
    const targetCategory = targetProject?.categories.find((c) => c.id === categoryId);
    const targetTask = targetCategory?.tasks.find((t) => t.id === taskId);
    const targetItem = targetTask?.checklist.find((i) => i.id === checklistId);

    if (!targetItem) return;

    const newCompleted = !targetItem.completed;

    try {
      const { error } = await supabase
        .from('checklists')
        .update({
          is_completed: newCompleted,
          completed_at: newCompleted ? now : null,
          completed_by: newCompleted ? user.id : null,
          updated_at: new Date(),
          updated_by: user.id,
        })
        .eq('id', checklistId);

      if (error) throw error;

      const updatedProjects = projects.map((project, idx) => {
        if (idx !== activeProjectIndex) return project;
        return {
          ...project,
          categories: project.categories.map((category) => {
            if (category.id !== categoryId) return category;
            return {
              ...category,
              tasks: category.tasks.map((task) => {
                if (task.id !== taskId) return task;
                const newChecklist = task.checklist.map((item) => {
                  if (item.id !== checklistId) return item;
                  return {
                    ...item,
                    completed: newCompleted,
                    completedAt: newCompleted ? now : null,
                    completedBy: newCompleted ? currentUser : null,
                  };
                });
                const completedCount = newChecklist.filter((item) => item.completed).length;
                const newProgress =
                  newChecklist.length > 0
                    ? Math.round((completedCount / newChecklist.length) * 100)
                    : 100;
                return { ...task, checklist: newChecklist, progress: newProgress };
              }),
            };
          }),
        };
      });

      setProjects(updatedProjects);
      refreshSelectedTask(categoryId, taskId, updatedProjects);
    } catch (error) {
      console.error('체크리스트 상태 업데이트 에러:', error.message);
      alert(t('settings.statusError'));
    }
  };

  // AI 가이드 토글 (전체 힌트 표시/숨기기)
  const toggleAiGuide = () => {
    setShowAiGuide((prev) => !prev);
  };

  // AI 가이드 생성 (hint가 없는 체크리스트에 대해 Dify로 생성)
  const handleGenerateHints = async () => {
    if (!selectedTask || !activeProject || generatingHintsTaskId) return;
    if (!canUserManageProjectAi(activeProject, user)) return;

    setGeneratingHintsTaskId(selectedTask.id);
    try {
      // hint가 없는 체크리스트만 필터링
      const checklistsWithoutHints = selectedTask.checklist.filter((item) => !item.hint);
      if (checklistsWithoutHints.length === 0) {
        setGeneratingHintsTaskId(null);
        return;
      }

      const taskInfo = {
        taskName: selectedTask.name,
        taskDescription: selectedTask.description || '',
        categoryName: selectedCategory?.name || '',
        startDate: selectedTask.startDate ? selectedTask.startDate.toISOString().split('T')[0] : '',
        endDate: selectedTask.endDate ? selectedTask.endDate.toISOString().split('T')[0] : '',
        checklists: checklistsWithoutHints.map((item) => ({
          id: item.id,
          name: item.text,
        })),
      };
      const projectInfo = {
        name: activeProject.name,
        description: activeProject.description || '',
        startDate: activeProject.startDate
          ? activeProject.startDate.toISOString().split('T')[0]
          : '',
        endDate: activeProject.endDate ? activeProject.endDate.toISOString().split('T')[0] : '',
      };

      const results = await generateChecklistHints(taskInfo, projectInfo, user.id);

      // DB에 hint 업데이트
      let successCount = 0;
      const hintsToSave = results.filter((r) => r.hint);

      if (hintsToSave.length === 0) {
        alert(t('aiGuide.emptyResult'));
        return;
      }

      for (const { checklistId, hint } of hintsToSave) {
        const { error } = await supabase.from('checklists').update({ hint }).eq('id', checklistId);
        if (error) {
          console.error(`[AI 가이드] DB 업데이트 실패 (${checklistId}):`, error);
        } else {
          successCount++;
        }
      }

      if (successCount === 0) {
        alert(t('aiGuide.saveFailed'));
        return;
      }

      // 상태 갱신 후 AI 가이드 자동 표시
      await dashboardSilentRefresh();
      setShowAiGuide(true);
    } catch (error) {
      console.error('AI 가이드 생성 오류:', error);
      alert(t('aiGuide.generateFailed'));
    } finally {
      setGeneratingHintsTaskId(null);
    }
  };

  // 첨부 업로드 시작 — 새로 선택된 파일들의 presign 받아오고 R2 에 PUT 시작.
  // items: setItems 가 가리키는 state 안에 이미 추가된 pending 엔트리들 (id 기준 매칭).
  const startUploadBatch = async ({
    kind,
    projectId,
    threadId,
    taskId,
    postId,
    items,
    setItems,
  }) => {
    try {
      const presign = await requestPresign({
        kind,
        projectId,
        threadId,
        taskId,
        postId,
        files: items.map((it) => ({
          name: it.file.name,
          type: it.file.type || 'application/octet-stream',
          size: it.file.size,
        })),
      });
      presign.files.forEach((res, i) => {
        const entry = items[i];
        if (!entry) return;
        if (!res.ok) {
          setItems((prev) =>
            prev.map((x) =>
              x.id === entry.id ? { ...x, status: 'error', error: res.error || t('common.uploadFailed') } : x
            )
          );
          return;
        }
        const upload = uploadFileToR2({
          file: entry.file,
          url: res.url,
          type: res.type,
          onProgress: (pct) =>
            setItems((prev) => prev.map((x) => (x.id === entry.id ? { ...x, progress: pct } : x))),
        });
        setItems((prev) =>
          prev.map((x) =>
            x.id === entry.id
              ? {
                  ...x,
                  status: 'uploading',
                  progress: 0,
                  key: res.key,
                  abort: upload.abort,
                }
              : x
          )
        );
        upload.promise
          .then(() => {
            setItems((prev) =>
              prev.map((x) =>
                x.id === entry.id ? { ...x, status: 'done', progress: 100, abort: undefined } : x
              )
            );
          })
          .catch((e) => {
            if (e?.message === 'aborted') return;
            setItems((prev) =>
              prev.map((x) =>
                x.id === entry.id
                  ? { ...x, status: 'error', error: e?.message || t('common.uploadFailed') }
                  : x
              )
            );
          });
      });
    } catch (e) {
      const ids = new Set(items.map((it) => it.id));
      setItems((prev) =>
        prev.map((x) =>
          ids.has(x.id) ? { ...x, status: 'error', error: e?.message || t('common.uploadFailed') } : x
        )
      );
    }
  };

  // 새 파일 선택 시 처리 — File[] → 업로드 엔트리로 wrap + 자동 업로드 시작
  const buildPendingEntries = (files) =>
    files.map((f) => ({
      id:
        (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
        `tmp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      file: f,
      status: 'pending',
      progress: 0,
    }));

  // 한 메시지/게시물/댓글에 이미지·파일을 합쳐 최대 30개까지만 첨부 허용.
  // 이미 담긴 개수(existingCount)를 고려해 새로 고른 파일을 잘라낸다. 넘치면 경고.
  const MAX_ATTACHMENTS = 30;
  const capPickedFiles = (existingCount, picked) => {
    const room = MAX_ATTACHMENTS - existingCount;
    if (room <= 0) {
      alert(t('chat.attachLimit', { n: MAX_ATTACHMENTS }));
      return [];
    }
    if (picked.length > room) {
      alert(t('chat.attachLimitRoom', { n: MAX_ATTACHMENTS, room }));
      return picked.slice(0, room);
    }
    return picked;
  };

  // 첨부 칩 제거 — 업로드 중이면 abort, 완료됐으면 R2 객체 삭제
  const removeAttachmentEntry = (items, id, setItems) => {
    const target = items.find((x) => x.id === id);
    if (target?.status === 'uploading' && typeof target.abort === 'function') {
      try {
        target.abort();
      } catch {}
    }
    if (target?.status === 'done' && target.key) {
      deleteAttachments([target.key]);
    }
    setItems((prev) => prev.filter((x) => x.id !== id));
  };

  // 채팅 메시지 전송 — 본문/첨부/답글 어느 하나라도 있으면 전송 가능
  const handleSendMessage = async () => {
    if (!activeProject) return;
    const trimmed = newMessage.trim();
    const doneFiles = chatFiles.filter((f) => f.status === 'done');
    if (!trimmed && doneFiles.length === 0) return;
    // 업로드 중이면 전송 차단 (UI 에서도 disabled 이지만 한 번 더)
    if (chatFiles.some((f) => f.status === 'pending' || f.status === 'uploading')) return;

    const attachments = doneFiles.map((f) => ({
      key: f.key,
      name: f.file.name,
      type: f.file.type,
      size: f.file.size,
    }));

    try {
      const { error } = await supabase.from('chat_messages').insert([
        {
          project_id: activeProject.id,
          author_id: user.id,
          message: trimmed || null,
          reply_to_id: replyingToId || null,
          attachments,
        },
      ]);

      if (error) throw error;
      setNewMessage('');
      setReplyingToId(null);
      setChatFiles([]);
    } catch (error) {
      console.error('채팅 메시지 전송 에러:', error.message);
      alert(t('chat.sendError'));
    }
  };

  // 원문 메시지로 점프 + 잠깐 하이라이트
  const jumpToChatMessage = (messageId) => {
    if (!messageId) return;
    const el = chatMessageRefs.current.get(messageId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightedChatMessageId(messageId);
      setTimeout(() => {
        setHighlightedChatMessageId((cur) => (cur === messageId ? null : cur));
      }, 1600);
    }
  };

  // 답글 모드 시작 — replyingToId 만 세팅, 원문 데이터는 chatMessages 에서 lookup
  const startReplyTo = (messageId) => {
    if (!messageId) return;
    setReplyingToId(messageId);
  };

  // 게시물 가리기 토글 — 관리자/소유자만. RPC 서버단 권한 검증 + 로컬 state 즉시 업데이트.
  const handleToggleHidePost = async (categoryId, taskId, postId) => {
    try {
      const { data, error } = await supabase.rpc('toggle_post_hidden', {
        p_post_id: postId,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      const nextHiddenAt = row?.hidden_at || null;
      const nextHiddenBy = row?.hidden_by || null;
      const updatedProjects = projects.map((p, idx) => {
        if (idx !== activeProjectIndex) return p;
        return {
          ...p,
          categories: p.categories.map((category) => {
            if (category.id !== categoryId) return category;
            return {
              ...category,
              tasks: category.tasks.map((task) => {
                if (task.id !== taskId) return task;
                return {
                  ...task,
                  posts: task.posts.map((post) =>
                    post.id === postId
                      ? { ...post, hiddenAt: nextHiddenAt, hiddenBy: nextHiddenBy }
                      : post
                  ),
                };
              }),
            };
          }),
        };
      });
      setProjects(updatedProjects);
      refreshSelectedTask(categoryId, taskId, updatedProjects);
    } catch (e) {
      console.error('게시물 가리기 토글 에러:', e?.message || e);
      alert(e?.message || t('chat.hidePostError'));
    }
  };

  // 댓글 가리기 토글
  const handleToggleHideComment = async (categoryId, taskId, postId, commentId) => {
    try {
      const { data, error } = await supabase.rpc('toggle_comment_hidden', {
        p_comment_id: commentId,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      const nextHiddenAt = row?.hidden_at || null;
      const nextHiddenBy = row?.hidden_by || null;
      const updatedProjects = projects.map((p, idx) => {
        if (idx !== activeProjectIndex) return p;
        return {
          ...p,
          categories: p.categories.map((category) => {
            if (category.id !== categoryId) return category;
            return {
              ...category,
              tasks: category.tasks.map((task) => {
                if (task.id !== taskId) return task;
                return {
                  ...task,
                  posts: task.posts.map((post) => {
                    if (post.id !== postId) return post;
                    return {
                      ...post,
                      comments: post.comments.map((c) =>
                        c.id === commentId
                          ? { ...c, hiddenAt: nextHiddenAt, hiddenBy: nextHiddenBy }
                          : c
                      ),
                    };
                  }),
                };
              }),
            };
          }),
        };
      });
      setProjects(updatedProjects);
      refreshSelectedTask(categoryId, taskId, updatedProjects);
    } catch (e) {
      console.error('댓글 가리기 토글 에러:', e?.message || e);
      alert(e?.message || t('chat.hideCommentError'));
    }
  };

  // 채팅 메시지 가리기 토글 — 관리자/소유자만. RPC가 서버단에서 권한 검증.
  const handleToggleHideChatMessage = async (messageId) => {
    try {
      const { error } = await supabase.rpc('toggle_chat_message_hidden', {
        p_message_id: messageId,
      });
      if (error) throw error;
    } catch (e) {
      console.error('메시지 가리기 토글 에러:', e?.message || e);
      alert(e?.message || t('chat.hideMessageError'));
    }
  };

  // 채팅 메시지 삭제 — DB 삭제 후 R2 첨부 객체도 같이 삭제
  const handleDeleteChatMessage = async (messageId) => {
    if (!window.confirm(t('chat.deleteMessageConfirm'))) return;

    const target = chatMessages.find((m) => m.id === messageId);
    const keys = extractKeys(target?.attachments);

    try {
      const { error } = await supabase.from('chat_messages').delete().eq('id', messageId);

      if (error) throw error;
      if (keys.length > 0) deleteAttachments(keys);
    } catch (error) {
      console.error('채팅 메시지 삭제 에러:', error.message);
      alert(t('chat.deleteMessageError'));
    }
  };

  // 채팅 메시지 수정 저장 — 본문 + 첨부 (사용자가 제거한 첨부는 R2 에서도 삭제)
  const handleUpdateChatMessage = async () => {
    if (!editingChatMessageId) return;
    const trimmed = editingChatMessageText.trim();
    if (!trimmed && editingChatAttachments.length === 0) return;
    const original = chatMessages.find((m) => m.id === editingChatMessageId);
    const originalKeys = (original?.attachments || []).map((a) => a.key).filter(Boolean);
    const remainingKeys = editingChatAttachments.map((a) => a.key).filter(Boolean);
    const removedKeys = originalKeys.filter((k) => !remainingKeys.includes(k));

    try {
      const { error } = await supabase
        .from('chat_messages')
        .update({
          message: trimmed || null,
          attachments: editingChatAttachments,
          updated_at: new Date(),
        })
        .eq('id', editingChatMessageId);

      if (error) throw error;
      if (removedKeys.length > 0) deleteAttachments(removedKeys);
      setEditingChatMessageId(null);
      setEditingChatMessageText('');
      setEditingChatAttachments([]);
    } catch (error) {
      console.error('채팅 메시지 수정 에러:', error.message);
      alert(t('chat.editMessageError'));
    }
  };

  // 체크리스트 추가
  const handleAddChecklist = async (categoryId, taskId) => {
    if (!newChecklistText.trim()) return;

    try {
      const { data, error } = await supabase
        .from('checklists')
        .insert([
          {
            task_id: taskId,
            name: newChecklistText.trim(),
            is_completed: false,
            created_by: user.id,
          },
        ])
        .select()
        .single();

      if (error) throw error;

      let nextSelectedTask = null;
      let nextSelectedCategory = null;

      setProjects((prev) => {
        const updatedProjects = prev.map((project, idx) => {
          if (idx !== activeProjectIndex) return project;
          return {
            ...project,
            categories: project.categories.map((category) => {
              if (category.id !== categoryId) return category;
              const updatedTasks = category.tasks.map((task) => {
                if (task.id !== taskId) return task;
                const newChecklist = [
                  ...task.checklist,
                  {
                    id: data.id,
                    text: data.name,
                    createdBy: data.created_by,
                    createdAt: data.created_at,
                    completed: data.is_completed || false,
                    completedAt: data.completed_at,
                    completedBy: data.completed_by,
                  },
                ];
                const completedCount = newChecklist.filter((item) => item.completed).length;
                const progress =
                  newChecklist.length > 0
                    ? Math.round((completedCount / newChecklist.length) * 100)
                    : 100;

                const newTask = {
                  ...task,
                  checklist: newChecklist,
                  progress: progress,
                };
                nextSelectedTask = newTask;
                return newTask;
              });
              const updatedCategory = { ...category, tasks: updatedTasks };
              if (nextSelectedTask) nextSelectedCategory = updatedCategory;
              return updatedCategory;
            }),
          };
        });
        return updatedProjects;
      });

      if (nextSelectedTask && nextSelectedCategory) {
        setSelectedCategory(nextSelectedCategory);
        setSelectedTask(nextSelectedTask);
      }
      setNewChecklistText('');
    } catch (error) {
      console.error('체크리스트 추가 에러:', error.message);
      alert(t('checklist.addError'));
    }
  };

  // 체크리스트 삭제
  const handleDeleteChecklist = async (categoryId, taskId, checklistId) => {
    if (!window.confirm(t('common.deleteConfirmShort'))) return;

    try {
      const { error } = await supabase.from('checklists').delete().eq('id', checklistId);

      if (error) throw error;

      let nextSelectedTask = null;
      let nextSelectedCategory = null;

      setProjects((prev) => {
        const updatedProjects = prev.map((project, idx) => {
          if (idx !== activeProjectIndex) return project;
          return {
            ...project,
            categories: project.categories.map((category) => {
              if (category.id !== categoryId) return category;
              const updatedTasks = category.tasks.map((task) => {
                if (task.id !== taskId) return task;
                const newChecklist = task.checklist.filter((item) => item.id !== checklistId);
                const completedCount = newChecklist.filter((item) => item.completed).length;
                const progress =
                  newChecklist.length > 0
                    ? Math.round((completedCount / newChecklist.length) * 100)
                    : 100;

                const newTask = {
                  ...task,
                  checklist: newChecklist,
                  progress: progress,
                };
                nextSelectedTask = newTask;
                return newTask;
              });
              const updatedCategory = { ...category, tasks: updatedTasks };
              if (nextSelectedTask) nextSelectedCategory = updatedCategory;
              return updatedCategory;
            }),
          };
        });
        return updatedProjects;
      });

      if (nextSelectedTask && nextSelectedCategory) {
        setSelectedCategory(nextSelectedCategory);
        setSelectedTask(nextSelectedTask);
      }
    } catch (error) {
      console.error('체크리스트 삭제 에러:', error.message);
      alert(t('checklist.deleteError'));
    }
  };

  // 체크리스트 수정
  const handleUpdateChecklist = async (categoryId, taskId) => {
    if (!editingChecklistText.trim() || !editingChecklistId) return;

    try {
      const { error } = await supabase
        .from('checklists')
        .update({
          name: editingChecklistText.trim(),
          updated_at: new Date(),
          updated_by: user.id,
        })
        .eq('id', editingChecklistId);

      if (error) throw error;

      let nextSelectedTask = null;
      let nextSelectedCategory = null;

      setProjects((prev) => {
        const updatedProjects = prev.map((project, idx) => {
          if (idx !== activeProjectIndex) return project;
          return {
            ...project,
            categories: project.categories.map((category) => {
              if (category.id !== categoryId) return category;
              const updatedTasks = category.tasks.map((task) => {
                if (task.id !== taskId) return task;
                const newTask = {
                  ...task,
                  checklist: task.checklist.map((item) =>
                    item.id === editingChecklistId
                      ? { ...item, text: editingChecklistText.trim() }
                      : item
                  ),
                };
                nextSelectedTask = newTask;
                return newTask;
              });
              const updatedCategory = { ...category, tasks: updatedTasks };
              if (nextSelectedTask) nextSelectedCategory = updatedCategory;
              return updatedCategory;
            }),
          };
        });
        return updatedProjects;
      });

      if (nextSelectedTask && nextSelectedCategory) {
        setSelectedCategory(nextSelectedCategory);
        setSelectedTask(nextSelectedTask);
      }
      setEditingChecklistId(null);
      setEditingChecklistText('');
    } catch (error) {
      console.error('체크리스트 수정 에러:', error.message);
      alert(t('checklist.editError'));
    }
  };

  // 댓글 추가 — 첨부 업로드 완료 후 attachments 같이 insert
  const handleAddComment = async (categoryId, taskId, postId, parentCommentId = null) => {
    const commentText = (newComments[postId] || '').trim();
    const entries = commentFiles[postId] || [];
    const doneFiles = entries.filter((f) => f.status === 'done');

    if (!commentText && doneFiles.length === 0) {
      setCommentErrors((prev) => ({ ...prev, [postId]: t('comment.required') }));
      return;
    }
    if (entries.some((f) => f.status === 'pending' || f.status === 'uploading')) return;
    setCommentErrors((prev) => ({ ...prev, [postId]: '' }));

    const attachments = doneFiles.map((f) => ({
      key: f.key,
      name: f.file.name,
      type: f.file.type,
      size: f.file.size,
    }));

    try {
      const { data, error } = await supabase
        .from('comments')
        .insert([
          {
            post_id: postId,
            author_id: user.id,
            content: commentText || null,
            attachments,
            parent_comment_id: parentCommentId || null,
          },
        ])
        .select(
          `
          *,
          user_profiles!author_id (
            updated_display_name,
            updated_avatar_url
          )
        `
        )
        .single();

      if (error) throw error;

      // 부모 댓글 정보 (답글일 때) — 로컬 state 에서 lookup
      const parentInProject = parentCommentId
        ? projects[activeProjectIndex]?.categories
            .find((c) => c.id === categoryId)
            ?.tasks.find((t) => t.id === taskId)
            ?.posts.find((p) => p.id === postId)
            ?.comments.find((c) => c.id === parentCommentId)
        : null;

      const newCommentObj = {
        id: data.id,
        authorId: data.author_id,
        author:
          data.user_profiles?.updated_display_name ||
          profile?.updated_display_name ||
          user?.user_metadata?.full_name,
        avatar: pickAvatar(
          data.user_profiles?.updated_avatar_url || profile?.updated_avatar_url,
          data.user_profiles?.updated_display_name ||
            profile?.updated_display_name ||
            user?.user_metadata?.full_name
        ),
        content: data.content,
        createdAt: data.created_at,
        attachments: Array.isArray(data.attachments) ? data.attachments : attachments,
        parentCommentId: parentCommentId || null,
        parentAuthor: parentInProject?.author || null,
        parentAuthorId: parentInProject?.authorId || null,
        parentSnippet: parentInProject?.content || '',
        likeUserIds: [],
        likeCount: 0,
        likedByMe: false,
        replyCount: 0,
      };

      const updatedProjects = projects.map((project, idx) => {
        if (idx !== activeProjectIndex) return project;
        return {
          ...project,
          categories: project.categories.map((category) => {
            if (category.id !== categoryId) return category;
            return {
              ...category,
              tasks: category.tasks.map((task) => {
                if (task.id !== taskId) return task;
                return {
                  ...task,
                  posts: task.posts.map((post) => {
                    if (post.id !== postId) return post;
                    // 새 댓글 시간 오름차순 끝(가장 최근) 위치로
                    const nextComments = [...post.comments, newCommentObj];
                    // 부모 댓글 있으면 replyCount 도 +1
                    const incremented = parentCommentId
                      ? nextComments.map((c) =>
                          c.id === parentCommentId
                            ? { ...c, replyCount: (c.replyCount || 0) + 1 }
                            : c
                        )
                      : nextComments;
                    return { ...post, comments: incremented };
                  }),
                };
              }),
            };
          }),
        };
      });

      setProjects(updatedProjects);
      refreshSelectedTask(categoryId, taskId, updatedProjects);
      setNewComments((prev) => ({ ...prev, [postId]: '' }));
      setCommentFiles((prev) => ({ ...prev, [postId]: [] }));
      closeCommentComposer();
    } catch (error) {
      console.error('댓글 추가 에러:', error.message);
      alert(t('comment.addError'));
    }
  };

  // 게시물 좋아요 토글
  const handleTogglePostLike = async (categoryId, taskId, postId) => {
    if (!user) return;
    const target = projects[activeProjectIndex]?.categories
      .find((c) => c.id === categoryId)
      ?.tasks.find((t) => t.id === taskId)
      ?.posts.find((p) => p.id === postId);
    if (!target) return;
    const currentlyLiked = !!target.likedByMe;
    try {
      if (currentlyLiked) {
        const { error } = await supabase
          .from('post_likes')
          .delete()
          .match({ post_id: postId, user_id: user.id });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('post_likes')
          .insert({ post_id: postId, user_id: user.id });
        if (error && error.code !== '23505') throw error;
      }
      const updatedProjects = projects.map((project, idx) => {
        if (idx !== activeProjectIndex) return project;
        return {
          ...project,
          categories: project.categories.map((category) => {
            if (category.id !== categoryId) return category;
            return {
              ...category,
              tasks: category.tasks.map((task) => {
                if (task.id !== taskId) return task;
                return {
                  ...task,
                  posts: task.posts.map((post) => {
                    if (post.id !== postId) return post;
                    const nextIds = currentlyLiked
                      ? (post.likeUserIds || []).filter((u) => u !== user.id)
                      : [...(post.likeUserIds || []), user.id];
                    return {
                      ...post,
                      likeUserIds: nextIds,
                      likeCount: nextIds.length,
                      likedByMe: !currentlyLiked,
                    };
                  }),
                };
              }),
            };
          }),
        };
      });
      setProjects(updatedProjects);
      refreshSelectedTask(categoryId, taskId, updatedProjects);
    } catch (error) {
      console.error('게시물 좋아요 에러:', error.message);
    }
  };

  // 댓글 좋아요 토글
  const handleToggleCommentLike = async (categoryId, taskId, postId, commentId) => {
    if (!user) return;
    const target = projects[activeProjectIndex]?.categories
      .find((c) => c.id === categoryId)
      ?.tasks.find((t) => t.id === taskId)
      ?.posts.find((p) => p.id === postId)
      ?.comments.find((c) => c.id === commentId);
    if (!target) return;
    const currentlyLiked = !!target.likedByMe;
    try {
      if (currentlyLiked) {
        const { error } = await supabase
          .from('comment_likes')
          .delete()
          .match({ comment_id: commentId, user_id: user.id });
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('comment_likes')
          .insert({ comment_id: commentId, user_id: user.id });
        if (error && error.code !== '23505') throw error;
      }
      const updatedProjects = projects.map((project, idx) => {
        if (idx !== activeProjectIndex) return project;
        return {
          ...project,
          categories: project.categories.map((category) => {
            if (category.id !== categoryId) return category;
            return {
              ...category,
              tasks: category.tasks.map((task) => {
                if (task.id !== taskId) return task;
                return {
                  ...task,
                  posts: task.posts.map((post) => {
                    if (post.id !== postId) return post;
                    return {
                      ...post,
                      comments: post.comments.map((c) => {
                        if (c.id !== commentId) return c;
                        const nextIds = currentlyLiked
                          ? (c.likeUserIds || []).filter((u) => u !== user.id)
                          : [...(c.likeUserIds || []), user.id];
                        return {
                          ...c,
                          likeUserIds: nextIds,
                          likeCount: nextIds.length,
                          likedByMe: !currentlyLiked,
                        };
                      }),
                    };
                  }),
                };
              }),
            };
          }),
        };
      });
      setProjects(updatedProjects);
      refreshSelectedTask(categoryId, taskId, updatedProjects);
    } catch (error) {
      console.error('댓글 좋아요 에러:', error.message);
    }
  };

  // 댓글 삭제
  const handleDeleteComment = async (categoryId, taskId, postId, commentId) => {
    if (!window.confirm(t('comment.deleteConfirm'))) return;

    try {
      // R2 첨부 정리 — DB delete 전에 키만 모아둔다 (delete 후엔 row 사라짐)
      const targetComment = projects[activeProjectIndex]?.categories
        ?.find((c) => c.id === categoryId)
        ?.tasks?.find((t) => t.id === taskId)
        ?.posts?.find((p) => p.id === postId)
        ?.comments?.find((c) => c.id === commentId);
      const keysToDelete = extractKeys(targetComment?.attachments);

      const { error } = await supabase.from('comments').delete().eq('id', commentId);

      if (error) throw error;
      if (keysToDelete.length > 0) {
        deleteAttachments(keysToDelete);
      }

      const updatedProjects = projects.map((p, idx) => {
        if (idx !== activeProjectIndex) return p;
        return {
          ...p,
          categories: p.categories.map((category) => {
            if (category.id !== categoryId) return category;
            return {
              ...category,
              tasks: category.tasks.map((task) => {
                if (task.id !== taskId) return task;
                return {
                  ...task,
                  posts: task.posts.map((post) => {
                    if (post.id !== postId) return post;
                    return {
                      ...post,
                      comments: post.comments.filter((c) => c.id !== commentId),
                    };
                  }),
                };
              }),
            };
          }),
        };
      });

      setProjects(updatedProjects);
      refreshSelectedTask(categoryId, taskId, updatedProjects);
    } catch (error) {
      console.error('댓글 삭제 에러:', error.message);
      alert(t('comment.deleteError'));
    }
  };

  // 댓글 수정 저장
  const handleUpdateComment = async (categoryId, taskId, postId) => {
    if (!editingComment) return;
    const trimmed = (editingComment.content || '').trim();
    const nextAttachments = Array.isArray(editingComment.attachments)
      ? editingComment.attachments
      : [];
    if (!trimmed && nextAttachments.length === 0) return;

    // 원본 댓글 첨부와 비교해 제거된 R2 key 산출
    const original = projects[activeProjectIndex]?.categories
      ?.find((c) => c.id === categoryId)
      ?.tasks?.find((t) => t.id === taskId)
      ?.posts?.find((p) => p.id === postId)
      ?.comments?.find((c) => c.id === editingComment.id);
    const originalKeys = extractKeys(original?.attachments);
    const remainingKeys = new Set(extractKeys(nextAttachments));
    const removedKeys = originalKeys.filter((k) => !remainingKeys.has(k));

    try {
      const { error } = await supabase
        .from('comments')
        .update({
          content: trimmed || null,
          attachments: nextAttachments,
          updated_at: new Date(),
        })
        .eq('id', editingComment.id);

      if (error) throw error;
      if (removedKeys.length > 0) deleteAttachments(removedKeys);

      const updatedProjects = projects.map((p, idx) => {
        if (idx !== activeProjectIndex) return p;
        return {
          ...p,
          categories: p.categories.map((category) => {
            if (category.id !== categoryId) return category;
            return {
              ...category,
              tasks: category.tasks.map((task) => {
                if (task.id !== taskId) return task;
                return {
                  ...task,
                  posts: task.posts.map((post) => {
                    if (post.id !== postId) return post;
                    return {
                      ...post,
                      comments: post.comments.map((c) =>
                        c.id === editingComment.id
                          ? { ...c, content: trimmed || null, attachments: nextAttachments }
                          : c
                      ),
                    };
                  }),
                };
              }),
            };
          }),
        };
      });

      setProjects(updatedProjects);
      refreshSelectedTask(categoryId, taskId, updatedProjects);
      setEditingComment(null);
    } catch (error) {
      console.error('댓글 수정 에러:', error.message);
      alert(t('comment.editError'));
    }
  };

  // 게시물 삭제
  const handleDeletePost = async (categoryId, taskId, postId) => {
    if (!window.confirm(t('post.deleteConfirm'))) return;

    try {
      // R2 첨부 정리 — post 와 그 안의 모든 comment 첨부 키 다 모음
      const targetPost = projects[activeProjectIndex]?.categories
        ?.find((c) => c.id === categoryId)
        ?.tasks?.find((t) => t.id === taskId)
        ?.posts?.find((p) => p.id === postId);
      const keysToDelete = [
        ...extractKeys(targetPost?.attachments),
        ...(targetPost?.comments || []).flatMap((c) => extractKeys(c.attachments)),
      ];

      const { error } = await supabase.from('posts').delete().eq('id', postId);

      if (error) throw error;
      if (keysToDelete.length > 0) {
        deleteAttachments(keysToDelete);
      }

      const updatedProjects = projects.map((p, idx) => {
        if (idx !== activeProjectIndex) return p;
        return {
          ...p,
          categories: p.categories.map((category) => {
            if (category.id !== categoryId) return category;
            return {
              ...category,
              tasks: category.tasks.map((task) => {
                if (task.id !== taskId) return task;
                return {
                  ...task,
                  posts: task.posts.filter((p) => p.id !== postId),
                };
              }),
            };
          }),
        };
      });

      setProjects(updatedProjects);
      refreshSelectedTask(categoryId, taskId, updatedProjects);
    } catch (error) {
      console.error('게시물 삭제 에러:', error.message);
      alert(t('post.deleteError'));
    }
  };

  // 게시물 수정 저장
  const handleUpdatePost = async (categoryId, taskId) => {
    if (!editingPost) return;
    const trimmed = (editingPost.content || '').trim();
    const nextAttachments = Array.isArray(editingPost.attachments) ? editingPost.attachments : [];
    if (!trimmed && nextAttachments.length === 0) return;

    // 원본 게시물 첨부와 비교해 제거된 R2 key 산출
    const original = projects[activeProjectIndex]?.categories
      ?.find((c) => c.id === categoryId)
      ?.tasks?.find((t) => t.id === taskId)
      ?.posts?.find((p) => p.id === editingPost.id);
    const originalKeys = extractKeys(original?.attachments);
    const remainingKeys = new Set(extractKeys(nextAttachments));
    const removedKeys = originalKeys.filter((k) => !remainingKeys.has(k));

    try {
      const { error } = await supabase
        .from('posts')
        .update({
          content: trimmed || null,
          attachments: nextAttachments,
          updated_at: new Date(),
        })
        .eq('id', editingPost.id);

      if (error) throw error;
      if (removedKeys.length > 0) deleteAttachments(removedKeys);

      const updatedProjects = projects.map((p, idx) => {
        if (idx !== activeProjectIndex) return p;
        return {
          ...p,
          categories: p.categories.map((category) => {
            if (category.id !== categoryId) return category;
            return {
              ...category,
              tasks: category.tasks.map((task) => {
                if (task.id !== taskId) return task;
                return {
                  ...task,
                  posts: task.posts.map((post) =>
                    post.id === editingPost.id
                      ? { ...post, content: trimmed || null, attachments: nextAttachments }
                      : post
                  ),
                };
              }),
            };
          }),
        };
      });

      setProjects(updatedProjects);
      refreshSelectedTask(categoryId, taskId, updatedProjects);
      setEditingPost(null);
    } catch (error) {
      console.error('게시물 수정 에러:', error.message);
      alert(t('post.editError'));
    }
  };

  // 게시물 추가
  const handleAddPost = async (categoryId, taskId) => {
    const trimmed = newPost.trim();
    const doneFiles = newPostFiles.filter((f) => f.status === 'done');
    if (!trimmed && doneFiles.length === 0) {
      setNewPostError(t('post.required'));
      return;
    }
    if (newPostFiles.some((f) => f.status === 'pending' || f.status === 'uploading')) {
      // 업로드 중이면 차단 (UI 에서도 disabled)
      return;
    }
    setNewPostError('');

    const attachments = doneFiles.map((f) => ({
      key: f.key,
      name: f.file.name,
      type: f.file.type,
      size: f.file.size,
    }));

    try {
      const { data, error } = await supabase
        .from('posts')
        .insert([
          {
            task_id: taskId,
            author_id: user.id,
            content: trimmed || null,
            attachments,
          },
        ])
        .select(
          `
          *,
          user_profiles!author_id (
            updated_display_name,
            updated_avatar_url
          )
        `
        )
        .single();

      if (error) throw error;

      const newPostObj = {
        id: data.id,
        authorId: data.author_id,
        author:
          data.user_profiles?.updated_display_name ||
          profile?.updated_display_name ||
          user?.user_metadata?.full_name,
        avatar: pickAvatar(
          data.user_profiles?.updated_avatar_url || profile?.updated_avatar_url,
          data.user_profiles?.updated_display_name ||
            profile?.updated_display_name ||
            user?.user_metadata?.full_name
        ),
        content: data.content,
        createdAt: data.created_at,
        comments: [],
        attachments: Array.isArray(data.attachments) ? data.attachments : attachments,
      };

      const updatedProjects = projects.map((project, idx) => {
        if (idx !== activeProjectIndex) return project;
        return {
          ...project,
          categories: project.categories.map((category) => {
            if (category.id !== categoryId) return category;
            return {
              ...category,
              tasks: category.tasks.map((task) => {
                if (task.id !== taskId) return task;
                return {
                  ...task,
                  posts: [newPostObj, ...task.posts],
                };
              }),
            };
          }),
        };
      });

      setProjects(updatedProjects);
      refreshSelectedTask(categoryId, taskId, updatedProjects);
      setNewPost('');
      setNewPostFiles([]);
      if (newPostFileInputRef.current) newPostFileInputRef.current.value = '';
    } catch (error) {
      console.error('게시물 추가 에러:', error.message);
      alert(t('post.addError'));
    }
  };

  // 타임라인 월 목록 (프로젝트 기간 동안의 각 달) - 메모이제이션
  const monthPages = useMemo(() => {
    if (!activeProject) return [];
    const start = normalizeDate(activeProject.startDate);
    const end = normalizeDate(activeProject.endDate);
    const rangeStart = new Date(start.getFullYear(), start.getMonth() - MONTH_PAST_BUFFER, 1);
    const rangeEnd = new Date(end.getFullYear(), end.getMonth() + MONTH_FUTURE_BUFFER + 1, 0);
    const months = [];
    const cursor = new Date(rangeStart);
    while (cursor <= rangeEnd) {
      const monthStart = normalizeDate(cursor);
      const monthEnd = normalizeDate(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0));
      months.push({
        key: `${cursor.getFullYear()}-${cursor.getMonth()}`,
        start: monthStart,
        end: monthEnd,
      });
      cursor.setMonth(cursor.getMonth() + 1, 1);
    }
    return months;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.startDate, activeProject?.endDate]);
  const currentMonth = monthPages[activeMonthIndex] || null;

  // 타임라인 날짜 범위 계산 (현재 월 기준) - 메모이제이션
  const timelineDatesCalc = useMemo(() => {
    if (!currentMonth) return [];
    const dates = [];
    const current = new Date(currentMonth.start);
    while (current <= currentMonth.end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return dates;
  }, [currentMonth]);

  /** 해당 월에서 특정 날짜 열이 타임라인 가로 스크롤의 시작(왼쪽)에 오도록 scrollLeft 설정 */
  const scrollTimelineToDateForMonth = useCallback(
    (monthIdx, targetDate) => {
      const el = timelineContainerRef.current;
      const month = monthPages[monthIdx];
      if (!el || !month || !targetDate) return;
      const dayTs = (dt) => {
        const d = new Date(dt);
        d.setHours(0, 0, 0, 0);
        return d.getTime();
      };
      const targetTs = dayTs(targetDate);
      const dates = [];
      const cur = new Date(month.start);
      while (cur <= month.end) {
        dates.push(dayTs(cur));
        cur.setDate(cur.getDate() + 1);
      }
      const dayIdx = dates.findIndex((ts) => ts === targetTs);
      if (dayIdx < 0) return;
      const dayW = isMobile ? 28 : 32;
      const maxScroll = Math.max(0, el.scrollWidth - el.clientWidth);
      el.scrollLeft = Math.min(dayIdx * dayW, maxScroll);
    },
    [monthPages, isMobile]
  );

  const scrollTimelineToTodayForMonth = useCallback(
    (monthIdx) => scrollTimelineToDateForMonth(monthIdx, new Date()),
    [scrollTimelineToDateForMonth]
  );

  // 월 이동
  const handlePrevMonth = () => {
    if (!monthPages.length) return;
    const timelineContainer = timelineContainerRef.current;
    if (timelineContainer) {
      timelineContainer.scrollLeft = 0;
    }
    setActiveMonthIndex((prev) => (prev - 1 + monthPages.length) % monthPages.length);
  };

  const handleNextMonth = () => {
    if (!monthPages.length) return;
    const timelineContainer = timelineContainerRef.current;
    if (timelineContainer) {
      timelineContainer.scrollLeft = 0;
    }
    setActiveMonthIndex((prev) => (prev + 1) % monthPages.length);
  };

  // 오늘 날짜가 포함된 달로 이동 + 오늘 열이 보이는 영역 맨 앞에 오도록 스크롤
  const handleGoToday = () => {
    if (!monthPages.length) return;
    const todayMonthKey = `${today.getFullYear()}-${today.getMonth()}`;
    const todayIdx = monthPages.findIndex((m) => m.key === todayMonthKey);
    const nextIdx = todayIdx >= 0 ? todayIdx : 0;
    setActiveMonthIndex(nextIdx);
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollTimelineToTodayForMonth(nextIdx);
      });
    });
  };

  const getEarliestTaskDate = () => {
    if (!activeProject) return null;
    let min = null;
    activeProject.categories.forEach((category) => {
      category.tasks.forEach((task) => {
        const d = normalizeDate(task.startDate);
        if (!min || d < min) min = d;
      });
    });
    return min;
  };

  // 맨 처음 작업이 시작된 달로 이동 + 그 작업의 시작일이 가로 스크롤 맨 앞에 오게 정렬
  const handleGoFirstTask = () => {
    if (!monthPages.length) return;
    const earliest = getEarliestTaskDate();
    if (!earliest) {
      alert(t('task.notExist'));
      return;
    }
    const firstKey = `${earliest.getFullYear()}-${earliest.getMonth()}`;
    const firstIdx = monthPages.findIndex((m) => m.key === firstKey);
    const nextIdx = firstIdx >= 0 ? firstIdx : 0;
    setActiveMonthIndex(nextIdx);
    // month 변경에 따른 re-render + layout 후에 scroll 적용
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollTimelineToDateForMonth(nextIdx, earliest);
      });
    });
  };

  // 프로젝트 변경 시에만 오늘이 포함된 달로 이동 (없으면 첫 달)
  useEffect(() => {
    if (!activeProject || !monthPages.length) {
      setActiveMonthIndex(0);
      return;
    }
    const todayMonthKey = `${today.getFullYear()}-${today.getMonth()}`;
    const todayIdx = monthPages.findIndex((m) => m.key === todayMonthKey);
    if (todayIdx >= 0) {
      setActiveMonthIndex(todayIdx);
    } else {
      setActiveMonthIndex(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProject?.id]);

  useEffect(() => {
    if (activeMonthIndex > monthPages.length - 1) {
      setActiveMonthIndex(Math.max(0, monthPages.length - 1));
    }
  }, [monthPages.length, activeMonthIndex]);

  /** 프로젝트 진입·월 변경·칸반 뷰 전환 후 오늘이 포함된 달이면 오늘 열을 왼쪽에 정렬 */
  useLayoutEffect(() => {
    if (timelineView !== 'kanban') return;
    if (!activeProject?.detailLoaded || !monthPages.length) return;
    const frameId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        scrollTimelineToTodayForMonth(activeMonthIndex);
      });
    });
    return () => cancelAnimationFrame(frameId);
  }, [
    timelineView,
    activeProject?.detailLoaded,
    activeProject?.id,
    activeMonthIndex,
    monthPages,
    scrollTimelineToTodayForMonth,
  ]);

  // 작업 위치 계산 (현재 월 기준, 겹치지 않으면 hidden)
  const getTaskPosition = (task, monthRange) => {
    if (!activeProject || !monthRange) return { left: 0, width: 0, hidden: true };
    const monthStart = normalizeDate(
      new Date(monthRange.start.getFullYear(), monthRange.start.getMonth(), 1)
    );
    const monthEnd = normalizeDate(
      new Date(monthRange.start.getFullYear(), monthRange.start.getMonth() + 1, 0)
    );
    const taskStart = normalizeDate(task.startDate);
    const taskEnd = normalizeDate(task.endDate);

    if (taskEnd < monthStart || taskStart > monthEnd) return { left: 0, width: 0, hidden: true };

    const clampedStart = taskStart < monthStart ? monthStart : taskStart;
    const clampedEnd = taskEnd > monthEnd ? monthEnd : taskEnd;
    const daysInMonth = Math.floor((monthEnd - monthStart) / DAY_MS) + 1;
    const startOffset = Math.floor((clampedStart - monthStart) / DAY_MS);
    const duration = Math.max(1, Math.floor((clampedEnd - clampedStart) / DAY_MS) + 1);

    return {
      left: `${(startOffset / daysInMonth) * 100}%`,
      width: `${(duration / daysInMonth) * 100}%`,
      hidden: false,
    };
  };

  // 협업자 할당 저장 — 팝업의 체크는 draft(assignDraft)에만 반영되고,
  // 저장 버튼을 눌렀을 때만 원본 담당자와 비교해 task_assignees insert/delete 수행.
  const handleSaveAssignees = async () => {
    if (!assignTarget || !assignDraft) {
      setShowAssignPopup(false);
      return;
    }

    const targetProject = projects[activeProjectIndex];
    const targetCategory = targetProject?.categories.find((c) => c.id === assignTarget.categoryId);
    const targetTask = targetCategory?.tasks.find((t) => t.id === assignTarget.taskId);
    if (!targetTask || !targetProject) {
      setShowAssignPopup(false);
      return;
    }

    const members = targetProject.members || [];
    const originalIds = new Set(targetTask.assignees.map((a) => a.id));
    const draftIds = new Set(assignDraft);
    const toAdd = [...draftIds].filter((id) => !originalIds.has(id));
    const toRemove = [...originalIds].filter((id) => !draftIds.has(id));

    // 변경 사항이 없으면 그냥 닫기
    if (toAdd.length === 0 && toRemove.length === 0) {
      setShowAssignPopup(false);
      return;
    }

    try {
      if (toAdd.length > 0) {
        const { error } = await supabase.from('task_assignees').insert(
          toAdd.map((id) => ({
            task_id: targetTask.id,
            user_id: id,
            assigned_by: user.id,
          }))
        );
        if (error) throw error;
      }
      if (toRemove.length > 0) {
        const { error } = await supabase
          .from('task_assignees')
          .delete()
          .eq('task_id', targetTask.id)
          .in('user_id', toRemove);
        if (error) throw error;
      }

      // 멤버 순서를 유지해 assignees 재구성
      const newAssignees = members
        .filter((m) => draftIds.has(m.id))
        .map((m) => ({ id: m.id, name: m.name, avatar: m.avatar }));

      const updatedProjects = projects.map((project, idx) => {
        if (idx !== activeProjectIndex) return project;
        return {
          ...project,
          categories: project.categories.map((category) => {
            if (category.id !== assignTarget.categoryId) return category;
            return {
              ...category,
              tasks: category.tasks.map((task) =>
                task.id !== assignTarget.taskId ? task : { ...task, assignees: newAssignees }
              ),
            };
          }),
        };
      });
      setProjects(updatedProjects);
      refreshSelectedTask(assignTarget.categoryId, assignTarget.taskId, updatedProjects);
      setShowAssignPopup(false);
    } catch (error) {
      console.error('담당자 저장 에러:', error.message);
      alert(t('assign.saveError'));
    }
  };

  // 색상 계산 (진행률에 따라) — 키컬러 파랑, 전체적으로 옅은 톤
  const getProgressColor = (progress) => {
    if (progress >= 100) return '#5cadff';
    if (progress >= 75) return '#7db9fc';
    if (progress >= 50) return '#9dcbff';
    if (progress >= 25) return '#b9d9ff';
    return '#e8f3ff';
  };

  /** 막대 배경(getProgressColor)에 맞춘 파란 계열 글자색 */
  const getProgressTextColor = (progress) => {
    if (progress >= 100) return '#1a4a7c';
    if (progress >= 75) return '#1e5688';
    if (progress >= 50) return '#256194';
    if (progress >= 25) return '#2f6a9e';
    return '#3d6e9e';
  };

  // 모바일 반응형 날짜 셀 너비
  const DAY_WIDTH = isMobile ? 28 : 32;

  const timelineDates = timelineDatesCalc;
  const gridWidth = timelineDates.length * DAY_WIDTH;
  const headerWidth = isMobile ? 180 + 80 + 2 : TIMELINE_HEADER_WIDTH; // 모바일 반응형 헤더 너비
  const tableMinWidth = headerWidth + gridWidth; // 헤더 영역 + 그리드 영역
  const gridBackground = 'linear-gradient(90deg, var(--claude-border-color) 1px, transparent 1px)';
  // 오늘 열 배경 — 라이트: 키 컬러(파랑), 다크: 기존 인디고 (index.css 테마 변수)
  const todayBackgroundStart = 'var(--today-overlay-start)';
  const todayBackgroundEnd = 'var(--today-overlay-end)';
  const todayBackground = `linear-gradient(90deg, ${todayBackgroundStart}, ${todayBackgroundEnd})`;
  const todayIndex = timelineDates.findIndex((d) => normalizeDate(d).getTime() === today.getTime());
  const todayStart = todayIndex >= 0 ? todayIndex * DAY_WIDTH : null;
  const todayEnd = todayStart !== null ? todayStart + DAY_WIDTH : null;
  const todayOverlay =
    todayStart !== null
      ? `linear-gradient(90deg,
        transparent ${todayStart}px,
        ${todayBackgroundStart} ${todayStart}px,
        ${todayBackgroundEnd} ${todayEnd}px,
        transparent ${todayEnd}px)`
      : null;
  const mergedBackground = todayOverlay ? `${todayOverlay}, ${gridBackground}` : gridBackground;

  // 채팅 안 읽음 카운트 — 전체 채팅 / DM 합산 / 총합
  const projectChatUnread = useMemo(() => {
    if (!projectChatReadAt) return 0;
    const meId = user?.id;
    const readMs = new Date(projectChatReadAt).getTime();
    return chatMessages.reduce(
      (s, m) => s + (m.authorId !== meId && new Date(m.createdAt).getTime() > readMs ? 1 : 0),
      0
    );
  }, [chatMessages, projectChatReadAt, user?.id]);
  // 작업별 unread 분해 — { checklistCount, postCount, total } 형태로 반환.
  // 칸반 작업명 옆 빨간 뱃지 = total. 작업 팝업 탭 라벨 옆 빨간 원 = 각각 checklistCount / postCount.
  const taskUnreadById = useMemo(() => {
    const map = {};
    if (!activeProject?.categories) return map;
    // read state 가 아직 fetch 안 됐거나 다른 프로젝트의 stale data면 unread 계산 skip → 깜빡임 방지
    if (taskReadStateByTask === null || projectSnsReadAt === null) return map;
    if (readStateProjectId !== activeProject.id) return map;
    if (readStateTaskIdsSig !== taskIdsSignature) return map;
    const meId = user?.id;
    const snsCutoff = new Date(projectSnsReadAt || 0).getTime();
    activeProject.categories.forEach((cat) => {
      (cat.tasks || []).forEach((task) => {
        const rs = taskReadStateByTask[task.id] || {};
        const checklistReadMs = new Date(rs.checklistAt || 0).getTime();
        const postReadMs = new Date(rs.postAt || 0).getTime();
        // 게시물/댓글 cutoff = MAX(프로젝트 SNS read, 해당 task 게시물 탭 read)
        const postCommentCutoff = Math.max(snsCutoff, postReadMs);
        let postCount = 0;
        (task.posts || []).forEach((p) => {
          if (
            p.authorId !== meId &&
            p.createdAt &&
            new Date(p.createdAt).getTime() > postCommentCutoff
          ) {
            postCount += 1;
          }
          (p.comments || []).forEach((c) => {
            if (
              c.authorId !== meId &&
              c.createdAt &&
              new Date(c.createdAt).getTime() > postCommentCutoff
            ) {
              postCount += 1;
            }
          });
        });
        let checklistCount = 0;
        (task.checklist || []).forEach((cl) => {
          const ts = cl.updatedAt || cl.createdAt;
          if (cl.createdBy !== meId && ts && new Date(ts).getTime() > checklistReadMs) {
            checklistCount += 1;
          }
        });
        map[task.id] = { checklistCount, postCount, total: checklistCount + postCount };
      });
    });
    return map;
  }, [
    activeProject,
    projectSnsReadAt,
    taskReadStateByTask,
    user?.id,
    readStateProjectId,
    readStateTaskIdsSig,
    taskIdsSignature,
  ]);

  // SNS 스택 뷰 버튼 우상단 뱃지 — 프로젝트 전체에서 본인이 안 쓴 게시물+댓글 unread 합
  const snsTotalUnread = useMemo(() => {
    if (!activeProject?.categories) return 0;
    if (projectSnsReadAt === null || taskReadStateByTask === null) return 0;
    if (readStateProjectId !== activeProject.id) return 0;
    if (readStateTaskIdsSig !== taskIdsSignature) return 0;
    const meId = user?.id;
    const snsCutoff = new Date(projectSnsReadAt || 0).getTime();
    let n = 0;
    activeProject.categories.forEach((cat) => {
      (cat.tasks || []).forEach((task) => {
        const taskPostReadMs = new Date((taskReadStateByTask[task.id] || {}).postAt || 0).getTime();
        // 작업 팝업 게시물 탭을 본 시각도 cutoff 에 합산 → 작업 안에서 읽은 게시물/댓글은 SNS 뱃지에서도 제외
        const cutoff = Math.max(snsCutoff, taskPostReadMs);
        (task.posts || []).forEach((p) => {
          if (p.authorId !== meId && p.createdAt && new Date(p.createdAt).getTime() > cutoff) {
            n += 1;
          }
          (p.comments || []).forEach((c) => {
            if (c.authorId !== meId && c.createdAt && new Date(c.createdAt).getTime() > cutoff) {
              n += 1;
            }
          });
        });
      });
    });
    return n;
  }, [
    activeProject,
    projectSnsReadAt,
    taskReadStateByTask,
    user?.id,
    readStateProjectId,
    readStateTaskIdsSig,
    taskIdsSignature,
  ]);

  // 프로젝트 전체 task unread 합 — 칸반 토글 버튼 위 빨간 점 표시용
  const projectTaskTotalUnread = useMemo(
    () => Object.values(taskUnreadById).reduce((s, x) => s + (x?.total || 0), 0),
    [taskUnreadById]
  );

  // 활성 프로젝트의 스레드만 합산 — 이전 프로젝트 스레드가 state 에 남아 있어도 새지 않도록 강제
  const dmUnreadTotal = useMemo(
    () =>
      dmThreads.reduce(
        (s, t) => s + (t.projectId === activeProject?.id ? t.unreadCount || 0 : 0),
        0
      ),
    [dmThreads, activeProject?.id]
  );
  const totalChatUnread = projectChatUnread + dmUnreadTotal;

  const calendarWeekCells = useMemo(() => buildCalendarWeekCells(currentMonth), [currentMonth]);
  const calendarWeekBarSegmentsByRow = useMemo(() => {
    if (!activeProject?.categories?.length || !calendarWeekCells.length) return [];
    const weekCount = Math.ceil(calendarWeekCells.length / 7) || 0;
    const rows = [];
    for (let row = 0; row < weekCount; row++) {
      const slice = calendarWeekCells.slice(row * 7, row * 7 + 7);
      rows.push(buildCalendarWeekBarSegments(slice, activeProject.categories));
    }
    return rows;
  }, [calendarWeekCells, activeProject?.categories]);

  const overallProgress = calculateOverallProgress(activeProject);
  const totalChecklistCount = activeProject
    ? activeProject.categories.reduce(
        (sum, category) => sum + category.tasks.reduce((s, task) => s + task.checklist.length, 0),
        0
      )
    : 0;
  const completedChecklistCount = activeProject
    ? activeProject.categories.reduce(
        (sum, category) =>
          sum +
          category.tasks.reduce(
            (s, task) => s + task.checklist.filter((i) => i.completed).length,
            0
          ),
        0
      )
    : 0;
  const renderTextWithLinks = (text) => {
    if (!text || typeof text !== 'string') return text;

    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const matchedUrlRegex = /^https?:\/\/[^\s]+$/;
    const parts = text.split(urlRegex);

    return parts.map((part, index) => {
      if (!matchedUrlRegex.test(part)) {
        return <span key={`text-${index}`}>{part}</span>;
      }

      return (
        <a
          key={`link-${index}`}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: '#2563eb',
            textDecoration: 'underline',
            cursor: 'pointer',
            wordBreak: 'break-all',
          }}
        >
          {part}
        </a>
      );
    });
  };
  /* 상세 내용 = tasks.description만 표시. 게시물(posts) 본문은 아래 게시물 목록에서만 노출 */
  const selectedTaskDetail = selectedTask?.description?.trim() ? selectedTask.description : '';

  useEffect(() => {
    setTaskDetailExpanded(false);
  }, [selectedTask?.id, showTaskPopup]);

  useEffect(() => {
    const next = taskModalNextTabRef.current;
    const initialTab = next || 'checklist';
    setTaskModalTab(initialTab);
    taskModalNextTabRef.current = null;
    // 작업 팝업 열릴 때 초기 탭에 해당하는 read 만 마크 (체크리스트 / 게시물 분리)
    if (showTaskPopup && selectedTask?.id) {
      if (initialTab === 'checklist') {
        markTaskChecklistRead(selectedTask.id);
      } else if (initialTab === 'posts') {
        markTaskPostRead(selectedTask.id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTask?.id, showTaskPopup]);

  // 작업 팝업 탭 전환 핸들러 — 선택한 탭의 read 도 함께 마크
  const handleSelectTaskModalTab = (tab) => {
    setTaskModalTab(tab);
    if (!selectedTask?.id) return;
    if (tab === 'checklist') markTaskChecklistRead(selectedTask.id);
    else if (tab === 'posts') markTaskPostRead(selectedTask.id);
  };

  useLayoutEffect(() => {
    if (!showTaskPopup || editingTaskField === 'detail') {
      setTaskDetailNeedsToggle(false);
      return;
    }
    if (!selectedTaskDetail) {
      setTaskDetailNeedsToggle(false);
      return;
    }
    if (taskDetailExpanded) {
      setTaskDetailNeedsToggle(true);
      return;
    }
    let cancelled = false;
    const id = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (cancelled) return;
        const el = taskDetailTextRef.current;
        if (!el) return;
        const needs = el.scrollHeight > el.clientHeight + 1;
        setTaskDetailNeedsToggle(needs);
      });
    });
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [showTaskPopup, selectedTask?.id, selectedTaskDetail, editingTaskField, taskDetailExpanded]);

  const selectedTaskTotalChecklistCount = selectedTask?.checklist?.length || 0;
  const selectedTaskCompletedChecklistCount = selectedTask
    ? selectedTask.checklist.filter((item) => item.completed).length
    : 0;
  const taskModalAiGuideButtonCount = (() => {
    if (!selectedTask?.checklist?.length) return 0;
    const hasHints = selectedTask.checklist.some((item) => item.hint);
    const hasMissingHints = selectedTask.checklist.some((item) => !item.hint);
    let n = 0;
    if (hasMissingHints) n += 1;
    if (hasHints) n += 1;
    return n;
  })();
  const checklistItems = activeProject
    ? activeProject.categories
        .flatMap((category) =>
          category.tasks.flatMap((task) =>
            task.checklist.map((item) => ({
              ...item,
              categoryId: category.id,
              taskId: task.id,
              taskName: task.name,
              categoryName: category.name,
            }))
          )
        )
        .sort((a, b) => {
          const ta = a.createdAt != null ? new Date(a.createdAt).getTime() : 0;
          const tb = b.createdAt != null ? new Date(b.createdAt).getTime() : 0;
          if (tb !== ta) return tb - ta;
          return String(b.id).localeCompare(String(a.id));
        })
    : [];
  const visibleChecklistItems = hideCompletedChecklist
    ? checklistItems.filter((item) => !item.completed)
    : checklistItems;
  const projectActivityFeed = useMemo(
    () => buildProjectActivityFeed(activeProject, t),
    [activeProject, t]
  );
  calculateTotalHours(activeProject);

  const refreshSelectedTask = (categoryId, taskId, sourceProjects = projects) => {
    const proj = sourceProjects[activeProjectIndex];
    if (!proj) return;
    const category = proj.categories.find((p) => p.id === categoryId);
    const task = category?.tasks.find((t) => t.id === taskId);
    if (category && task) {
      setSelectedCategory(category);
      setSelectedTask(task);
    }
  };

  const aiQuestions = [
    t('ai.q1'),
    t('ai.q2'),
    t('ai.q3'),
    t('ai.q4'),
  ];

  // === AI 에이전트 대화 ===
  const aiCanUse = canUserManageProjectAi(activeProject, user);

  const handleAiSend = async (text) => {
    const content = (text ?? aiInput).trim();
    if (!content || aiBusy || !activeProject) return;
    if (!aiCanUse) {
      setAiError(t('ai.adminOnlyError'));
      return;
    }
    setAiError('');
    setAiInput('');
    setAiShowExamples(false); // 대화 시작 시 예시 접기
    const nextMessages = [...aiMessages, { role: 'user', content }];
    setAiMessages(nextMessages);
    setAiBusy(true);
    setAiStatus(t('ai.status'));

    // 진행 단계 스무딩: 각 라벨을 최소 시간 동안 노출해 깜빡임 방지.
    // 짧은 간격에 여러 단계가 오면 마지막 단계만 보여줌.
    const MIN_DWELL = 600;
    let lastAt = Date.now();
    let pendingLabel = null;
    let dwellTimer = null;
    const pushStatus = (label) => {
      if (!label) return;
      const elapsed = Date.now() - lastAt;
      if (elapsed >= MIN_DWELL) {
        lastAt = Date.now();
        pendingLabel = null;
        setAiStatus(label);
      } else {
        pendingLabel = label;
        if (!dwellTimer) {
          dwellTimer = setTimeout(() => {
            dwellTimer = null;
            if (pendingLabel) {
              lastAt = Date.now();
              setAiStatus(pendingLabel);
              pendingLabel = null;
            }
          }, MIN_DWELL - elapsed);
        }
      }
    };

    try {
      const { message, stagedChanges } = await aiChat(
        activeProject.id,
        user.id,
        nextMessages,
        pushStatus
      );
      setAiMessages((prev) => [...prev, { role: 'assistant', content: message }]);
      setAiStaged(stagedChanges || []);
    } catch (e) {
      setAiMessages((prev) => [
        ...prev,
        { role: 'assistant', content: t('ai.errorPrefix', { message: e.message }) },
      ]);
    } finally {
      if (dwellTimer) clearTimeout(dwellTimer);
      setAiBusy(false);
      setAiStatus('');
    }
  };

  const handleAiApply = async () => {
    if (aiBusy || !activeProject || aiStaged.length === 0) return;
    setAiBusy(true);
    setAiError('');
    try {
      await aiApply(activeProject.id, user.id, aiStaged);
      setAiStaged([]);
      setAiMessages((prev) => [...prev, { role: 'assistant', content: t('ai.applied') }]);
      await fetchProjectDetail(activeProject.id, true); // 무음 갱신
    } catch (e) {
      setAiError(t('ai.applyFailed', { message: e.message }));
    } finally {
      setAiBusy(false);
    }
  };

  const handleAiCancelStaged = () => {
    setAiStaged([]);
    setAiMessages((prev) => [
      ...prev,
      { role: 'assistant', content: t('ai.stagedCanceled') },
    ]);
  };

  const handleRecalculateCost = async () => {
    if (isAiLoading || !activeProject) return;
    setIsAiLoading(true);

    try {
      const result = await recalculateCost(activeProject, user?.id || 'anonymous');

      // 프로젝트 상태 업데이트
      setProjects((prev) =>
        prev.map((p) =>
          p.id === activeProject.id
            ? {
                ...p,
                ai_estimated_cost: result.cost,
                ai_estimated_cost_reasons: result.reasons,
              }
            : p
        )
      );

      // Supabase에도 저장
      await supabase
        .from('projects')
        .update({
          ai_estimated_cost: result.cost,
          ai_estimated_cost_reasons: result.reasons,
          updated_at: new Date().toISOString(),
        })
        .eq('id', activeProject.id);
    } catch (error) {
      console.error('[Dashboard] 비용 재산출 오류:', error);
      alert(t('cost.recalcError'));
    } finally {
      setIsAiLoading(false);
    }
  };

  const handleLeaveProject = async (index) => {
    const targetProject = projects[index];
    if (!targetProject || !user) return;

    if (window.confirm(t('project.leaveConfirm', { name: targetProject.name }))) {
      try {
        const { error } = await supabase
          .from('project_members')
          .update({
            left_at: new Date().toISOString(),
          })
          .eq('project_id', targetProject.id)
          .eq('user_id', user.id);

        if (error) throw error;

        const remainingProjects = projects.filter((_, i) => i !== index);

        // 나간 후 남은 프로젝트들에 대해 isLimitReached 다시 계산
        const updatedProjects = remainingProjects.map((p) => {
          let isLimitReached = false;
          if (!p.isFirstProject) {
            if (!p.owner_subscription || p.isExpired) {
              isLimitReached = true;
            }
          }
          return { ...p, isLimitReached };
        });
        setProjects(updatedProjects);

        // 인덱스 보정
        if (index === activeProjectIndex) {
          setActiveProjectIndex(Math.max(0, activeProjectIndex - 1));
        } else if (index < activeProjectIndex) {
          setActiveProjectIndex(activeProjectIndex - 1);
        }
      } catch (error) {
        console.error('프로젝트 나가기 에러:', error.message);
        alert(t('project.leaveError'));
      }
    }
  };

  // 현재 선택된 task가 AI 가이드 생성 중인지 파생 변수
  const generatingHints = generatingHintsTaskId && selectedTask?.id === generatingHintsTaskId;

  useEffect(() => {
    let cancelled = false;
    setTemplateCategoriesLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from('template_categories')
        .select('id, name')
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });
      if (cancelled) return;
      setTemplateCategoriesLoading(false);
      if (error) {
        console.error('[Dashboard] template_categories 조회 실패:', error);
        setTemplateCategoriesList([]);
        return;
      }
      setTemplateCategoriesList(
        Array.isArray(data) ? data.filter((row) => row?.name && row?.id) : []
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setChallengeCategoriesLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from('challenge_categories')
        .select('id, name')
        .order('sort_order', { ascending: true })
        .order('name', { ascending: true });
      if (cancelled) return;
      setChallengeCategoriesLoading(false);
      if (error) {
        console.error('[Dashboard] challenge_categories 조회 실패:', error);
        setChallengeCategoriesList([]);
        return;
      }
      setChallengeCategoriesList(
        Array.isArray(data) ? data.filter((row) => row?.name && row?.id) : []
      );
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const templateCategoryNameById = useMemo(() => {
    const m = {};
    for (const row of templateCategoriesList) {
      if (row?.id != null) m[String(row.id)] = row.name;
    }
    return m;
  }, [templateCategoriesList]);

  const templateCategorySelectOptions = useMemo(() => {
    const rows = templateCategoriesList;
    const hasEtc = rows.some((r) => r.name === '기타');
    if (hasEtc) return rows;
    return [...rows, { id: TEMPLATE_CATEGORY_SYNTHETIC_ETC, name: '기타' }];
  }, [templateCategoriesList]);

  const closeTemplatePublishModal = useCallback(() => {
    setShowTemplatePublishModal(false);
    setTemplatePublishDescription('');
    setTemplatePublishCategoryId('');
    setTemplatePublishDescriptionError('');
    setTemplatePublishCategoryError('');
    setTemplatePublishSubmitting(false);
  }, []);

  const handleTemplatePublishSubmit = useCallback(async () => {
    if (templateCategoriesLoading || templatePublishSubmitting) return;
    if (!user?.id) {
      alert(t('common.loginRequired'));
      return;
    }

    const descOk = Boolean(templatePublishDescription.trim());
    const catOk = Boolean(templatePublishCategoryId);

    setTemplatePublishDescriptionError(descOk ? '' : t('template.descRequired'));
    setTemplatePublishCategoryError(catOk ? '' : t('template.categoryRequired'));

    if (!descOk || !catOk) return;

    const projectId = activeProject?.id;
    if (!projectId) {
      alert(t('project.notFound'));
      return;
    }

    const trimmedDesc = templatePublishDescription.trim();
    const nowIso = new Date().toISOString();

    setTemplatePublishSubmitting(true);
    try {
      let resolvedCategoryId = templatePublishCategoryId;
      if (resolvedCategoryId === TEMPLATE_CATEGORY_SYNTHETIC_ETC) {
        const { data: etcRow, error: etcErr } = await supabase
          .from('template_categories')
          .select('id')
          .eq('name', '기타')
          .maybeSingle();
        if (etcErr) {
          console.error('[Dashboard] 기타 카테고리 조회 실패:', etcErr);
          setTemplatePublishCategoryError(
            t('template.etcLoadError')
          );
          return;
        }
        if (!etcRow?.id) {
          setTemplatePublishCategoryError(
            t('template.etcMissing')
          );
          return;
        }
        resolvedCategoryId = etcRow.id;
      }

      const { error: updateError } = await supabase
        .from('projects')
        .update({
          is_template: true,
          template_desc: trimmedDesc,
          template_category_id: resolvedCategoryId,
          updated_at: nowIso,
          updated_by: user.id,
        })
        .eq('id', projectId);

      if (updateError) {
        console.error('[Dashboard] 템플릿 등록(프로젝트 업데이트) 실패:', updateError);
        alert(updateError.message || t('template.publishError'));
        return;
      }

      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId
            ? {
                ...p,
                is_template: true,
                template_desc: trimmedDesc,
                template_category_id: resolvedCategoryId,
                updated_at: nowIso,
                updated_by: user.id,
              }
            : p
        )
      );

      closeTemplatePublishModal();
    } finally {
      setTemplatePublishSubmitting(false);
    }
  }, [
    activeProject?.id,
    closeTemplatePublishModal,
    templateCategoriesLoading,
    templatePublishCategoryId,
    templatePublishDescription,
    templatePublishSubmitting,
    user?.id,
    t,
  ]);

  const closeChallengePublishModal = useCallback(() => {
    setShowChallengePublishModal(false);
    setChallengePublishCategoryId('');
    setChallengePublishDescription('');
    setChallengePublishPrice('');
    setChallengePublishCategoryError('');
    setChallengePublishDescriptionError('');
    setChallengePublishPriceError('');
    setChallengePublishSubmitting(false);
    setChallengeEditMode(false);
  }, []);

  // 발행 전(승인 전) 월간 챌린지 수정 — 발행 모달을 프리필해서 재사용
  const openChallengeEdit = useCallback(() => {
    if (!activeProject?.is_challenge) return;
    setChallengePublishCategoryId(
      activeProject.challenge_category_id ? String(activeProject.challenge_category_id) : ''
    );
    setChallengePublishDescription(activeProject.challenge_desc || '');
    setChallengePublishPrice(
      activeProject.challenge_price != null ? String(activeProject.challenge_price) : ''
    );
    setChallengePublishCategoryError('');
    setChallengePublishDescriptionError('');
    setChallengePublishPriceError('');
    setChallengeEditMode(true);
    setShowChallengePublishModal(true);
  }, [activeProject]);

  const handleChallengePublishSubmit = useCallback(async () => {
    if (challengeCategoriesLoading || challengePublishSubmitting) return;
    if (!user?.id) {
      alert(t('common.loginRequired'));
      return;
    }

    const descOk = Boolean(challengePublishDescription.trim());
    const catOk = Boolean(challengePublishCategoryId);
    // 최소 1000원 이상 정수(원). 소수점 불가.
    const priceNum = Number(challengePublishPrice);
    const priceOk =
      challengePublishPrice !== '' &&
      Number.isInteger(priceNum) &&
      priceNum >= 1000;

    setChallengePublishDescriptionError(descOk ? '' : t('challenge.descRequired'));
    setChallengePublishCategoryError(catOk ? '' : t('challenge.categoryRequired'));
    setChallengePublishPriceError(priceOk ? '' : t('challenge.priceInvalid'));

    if (!descOk || !catOk || !priceOk) return;

    const projectId = activeProject?.id;
    if (!projectId) {
      alert(t('project.notFound'));
      return;
    }

    const trimmedDesc = challengePublishDescription.trim();
    const nowIso = new Date().toISOString();

    setChallengePublishSubmitting(true);
    const fields = {
      is_challenge: true,
      challenge_approved: false,
      challenge_desc: trimmedDesc,
      challenge_price: priceNum,
      challenge_category_id: challengePublishCategoryId,
      updated_at: nowIso,
      updated_by: user.id,
    };
    // 신규 발행 때만 신청 시각 기록. 수정 모드에선 최초 신청 시각 유지.
    if (!challengeEditMode) fields.challenge_submitted_at = nowIso;
    try {
      const { error: updateError } = await supabase
        .from('projects')
        .update(fields)
        .eq('id', projectId);

      if (updateError) {
        console.error('[Dashboard] 챌린지 등록 실패:', updateError);
        alert(updateError.message || t('challenge.publishError'));
        return;
      }

      setProjects((prev) => prev.map((p) => (p.id === projectId ? { ...p, ...fields } : p)));

      closeChallengePublishModal();
    } finally {
      setChallengePublishSubmitting(false);
    }
  }, [
    activeProject?.id,
    closeChallengePublishModal,
    challengeCategoriesLoading,
    challengeEditMode,
    challengePublishCategoryId,
    challengePublishDescription,
    challengePublishPrice,
    challengePublishSubmitting,
    user?.id,
    t,
  ]);

  const closeTemplateEditModal = useCallback(() => {
    setShowTemplateEditModal(false);
    setTemplateEditDescription('');
    setTemplateEditCategoryId('');
    setTemplateEditDescriptionError('');
    setTemplateEditCategoryError('');
    setTemplateEditSubmitting(false);
  }, []);

  const openTemplateEditModal = useCallback(() => {
    if (!activeProject?.is_template) return;
    setTemplateEditDescription(activeProject.template_desc || '');
    setTemplateEditCategoryId(
      activeProject.template_category_id ? String(activeProject.template_category_id) : ''
    );
    setTemplateEditDescriptionError('');
    setTemplateEditCategoryError('');
    setShowTemplateEditModal(true);
  }, [activeProject]);

  const handleTemplateEditSubmit = useCallback(async () => {
    if (templateCategoriesLoading || templateEditSubmitting) return;
    if (!user?.id) {
      alert(t('common.loginRequired'));
      return;
    }

    const descOk = Boolean(templateEditDescription.trim());
    const catOk = Boolean(templateEditCategoryId);

    setTemplateEditDescriptionError(descOk ? '' : t('template.descRequired'));
    setTemplateEditCategoryError(catOk ? '' : t('template.categoryRequired'));

    if (!descOk || !catOk) return;

    const projectId = activeProject?.id;
    if (!projectId) {
      alert(t('project.notFound'));
      return;
    }

    const trimmedDesc = templateEditDescription.trim();
    const nowIso = new Date().toISOString();

    setTemplateEditSubmitting(true);
    try {
      let resolvedCategoryId = templateEditCategoryId;
      if (resolvedCategoryId === TEMPLATE_CATEGORY_SYNTHETIC_ETC) {
        const { data: etcRow, error: etcErr } = await supabase
          .from('template_categories')
          .select('id')
          .eq('name', '기타')
          .maybeSingle();
        if (etcErr || !etcRow?.id) {
          setTemplateEditCategoryError(
            etcErr
              ? t('template.etcLoadErrorShort')
              : t('template.etcMissingShort')
          );
          return;
        }
        resolvedCategoryId = etcRow.id;
      }

      const { error: updateError } = await supabase
        .from('projects')
        .update({
          template_desc: trimmedDesc,
          template_category_id: resolvedCategoryId,
          updated_at: nowIso,
          updated_by: user.id,
        })
        .eq('id', projectId);

      if (updateError) {
        console.error('[Dashboard] 템플릿 정보 수정 실패:', updateError);
        alert(updateError.message || t('template.saveError'));
        return;
      }

      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId
            ? {
                ...p,
                template_desc: trimmedDesc,
                template_category_id: resolvedCategoryId,
                updated_at: nowIso,
                updated_by: user.id,
              }
            : p
        )
      );

      closeTemplateEditModal();
    } finally {
      setTemplateEditSubmitting(false);
    }
  }, [
    activeProject?.id,
    closeTemplateEditModal,
    templateCategoriesLoading,
    templateEditCategoryId,
    templateEditDescription,
    templateEditSubmitting,
    user?.id,
    t,
  ]);

  const handleTemplateRevertToProject = useCallback(async () => {
    if (!user?.id || templateRevertSubmitting) return;
    const projectId = activeProject?.id;
    if (!projectId || !activeProject?.is_template) return;

    const ok = window.confirm(
      t('template.revertConfirm')
    );
    if (!ok) return;

    setTemplateRevertSubmitting(true);
    const nowIso = new Date().toISOString();
    try {
      const { error: updateError } = await supabase
        .from('projects')
        .update({
          is_template: false,
          template_desc: null,
          template_category_id: null,
          updated_at: nowIso,
          updated_by: user.id,
        })
        .eq('id', projectId);

      if (updateError) {
        console.error('[Dashboard] 템플릿 해제 실패:', updateError);
        alert(updateError.message || t('template.revertError'));
        return;
      }

      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId
            ? {
                ...p,
                is_template: false,
                template_desc: null,
                template_category_id: null,
                updated_at: nowIso,
                updated_by: user.id,
              }
            : p
        )
      );
    } finally {
      setTemplateRevertSubmitting(false);
    }
  }, [activeProject?.id, activeProject?.is_template, templateRevertSubmitting, user?.id, t]);

  // 챌린지 발행 취소 (admin 승인 전까지만 가능) — is_challenge 및 챌린지 필드 해제
  const handleChallengeRevert = useCallback(async () => {
    if (!user?.id || challengeRevertSubmitting) return;
    const projectId = activeProject?.id;
    if (!projectId || !activeProject?.is_challenge) return;
    // 참여자(참가자)가 있으면 되돌릴 수 없음 — 나 말고 참여자가 없을 때만 가능
    const participantCount = (activeProject?.members || []).filter(
      (m) => m.role === '참가자'
    ).length;
    if (participantCount > 0) {
      alert(t('challenge.revertBlocked'));
      return;
    }

    const ok = window.confirm(t('challenge.revertConfirm'));
    if (!ok) return;

    setChallengeRevertSubmitting(true);
    const nowIso = new Date().toISOString();
    try {
      const { error: updateError } = await supabase
        .from('projects')
        .update({
          is_challenge: false,
          challenge_approved: false,
          challenge_submitted_at: null,
          challenge_approved_at: null,
          challenge_desc: null,
          challenge_price: null,
          challenge_category_id: null,
          challenge_hidden: false,
          updated_at: nowIso,
          updated_by: user.id,
        })
        .eq('id', projectId);

      if (updateError) {
        console.error('[Dashboard] 챌린지 발행 취소 실패:', updateError);
        alert(updateError.message || t('challenge.revertError'));
        return;
      }

      setProjects((prev) =>
        prev.map((p) =>
          p.id === projectId
            ? {
                ...p,
                is_challenge: false,
                challenge_approved: false,
                challenge_submitted_at: null,
                challenge_approved_at: null,
                challenge_desc: null,
                challenge_price: null,
                challenge_category_id: null,
                challenge_hidden: false,
                updated_at: nowIso,
                updated_by: user.id,
              }
            : p
        )
      );
    } finally {
      setChallengeRevertSubmitting(false);
    }
  }, [
    activeProject?.id,
    activeProject?.is_challenge,
    activeProject?.members,
    challengeRevertSubmitting,
    user?.id,
    t,
  ]);

  return (
    <>
      {/* Header는 .claude-dashboard 밖에 둠 — Dashboard.css의 .claude-dashboard * 규칙이 헤더에 상속·적용되지 않도록 (다른 페이지와 동일한 헤더 높이/스타일) */}
      <Header
        onMenuClick={() => setShowComingSoon(true)}
        showPopup={showComingSoon}
        onClosePopup={() => setShowComingSoon(false)}
        onLoginClick={() => {}} // 이미 로그인 상태이므로 필요 없음
      />

      <div
        className={`claude-dashboard${!isLoading && (projectSidebarOpen || dashboardSidebarOpen) ? ' claude-dashboard-mobile-drawer-open' : ''}`}
      >
        {isLoading ? (
          <div className="claude-empty-wrapper claude-dashboard-loading-fill">
            <div className="claude-loading-content">
              <div className="claude-loading-spinner"></div>
            </div>
          </div>
        ) : (
          <div className="claude-dashboard-content">
            <button
              type="button"
              className="claude-project-sidebar-mobile-btn"
              onClick={() => {
                setDashboardSidebarOpen(false);
                setProjectSidebarOpen(true);
              }}
              aria-label={t('sidebar.openProjectList')}
            >
              <span className="claude-project-sidebar-mobile-btn-icon" aria-hidden />
              {t('sidebar.title')}
            </button>
            {projectSidebarOpen && (
              <button
                type="button"
                className="claude-project-sidebar-backdrop"
                aria-label={t('sidebar.closeProjectList')}
                onClick={() => setProjectSidebarOpen(false)}
              />
            )}
            <aside className={`claude-project-sidebar${projectSidebarOpen ? ' is-open' : ''}`}>
              <div className="claude-project-sidebar-header">
                <h3 className="claude-project-sidebar-title">{t('sidebar.title')}</h3>
                <span className="claude-project-add-wrap">
                  <button
                    type="button"
                    className="claude-project-add"
                    disabled={!isMySubActive && myProjectCount >= 1}
                    onClick={() => setShowCreatePopup(true)}
                  >
                    {t('sidebar.newProject')}
                  </button>
                  {!isMySubActive && myProjectCount >= 1 && (
                    <span
                      className="claude-disabled-control-hit-layer claude-disabled-control-hit-layer--cta tooltip-below tooltip-right"
                      data-tooltip={t('sidebar.newProjectLimit')}
                      aria-hidden="true"
                    />
                  )}
                </span>
                <button
                  type="button"
                  className="claude-chat-close claude-project-sidebar-close"
                  onClick={() => setProjectSidebarOpen(false)}
                  aria-label={t('common.close')}
                >
                  ×
                </button>
              </div>

              <div className="claude-project-list">
                {projects.length > 0 ? (
                  <>
                    {/* 내 프로젝트 (템플릿·월간 챌린지로 내놓은 것 제외) */}
                    {projects.some(
                      (p) => p.owner_id === user?.id && !p.is_template && !p.is_challenge
                    ) && (
                      <div
                        className={`claude-project-group${collapsedGroups.myProjects ? ' is-collapsed' : ''}`}
                      >
                        <button
                          type="button"
                          className="claude-project-group-title claude-project-group-toggle"
                          onClick={() => toggleGroupCollapsed('myProjects')}
                          aria-expanded={!collapsedGroups.myProjects}
                        >
                          <svg
                            className={`claude-group-chevron${collapsedGroups.myProjects ? ' is-collapsed' : ''}`}
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                          <span>{t('sidebar.myProjects')}</span>
                          <span className="claude-group-count-badge">
                            {
                              projects.filter(
                                (p) =>
                                  p.owner_id === user?.id && !p.is_template && !p.is_challenge
                              ).length
                            }
                          </span>
                        </button>
                        <div className="claude-project-group-scroll">
                          {projects.map(
                            (project, index) =>
                              project.owner_id === user?.id &&
                              !project.is_template &&
                              !project.is_challenge && (
                                <div
                                  key={project.id}
                                  className={`claude-project-item ${index === activeProjectIndex ? 'active' : ''} ${project.isLimitReached ? 'claude-disabled' : ''}`}
                                  onClick={() => selectProjectIndex(index)}
                                >
                                  <span className="claude-project-name">{project.name}</span>
                                  {project.owner_id === user?.id || project.myRole === '관리자' ? (
                                    <span
                                      className="claude-project-actions"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <button
                                        type="button"
                                        className="claude-project-edit"
                                        onClick={() => handleOpenEditProject(index)}
                                      >
                                        ✎
                                      </button>
                                      <button
                                        type="button"
                                        className="claude-project-delete"
                                        onClick={() => handleDeleteProject(index)}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ) : (
                                    <span
                                      className="claude-project-actions"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <button
                                        type="button"
                                        className="claude-project-leave"
                                        onClick={() => handleLeaveProject(index)}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  )}
                                </div>
                              )
                          )}
                        </div>
                      </div>
                    )}

                    {/* 참여 중인 프로젝트 */}
                    {projects.some((p) => p.owner_id !== user?.id) && (
                      <div
                        className={`claude-project-group${collapsedGroups.joinedProjects ? ' is-collapsed' : ''}`}
                      >
                        <button
                          type="button"
                          className="claude-project-group-title claude-project-group-toggle"
                          onClick={() => toggleGroupCollapsed('joinedProjects')}
                          aria-expanded={!collapsedGroups.joinedProjects}
                        >
                          <svg
                            className={`claude-group-chevron${collapsedGroups.joinedProjects ? ' is-collapsed' : ''}`}
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                          <span>{t('sidebar.joinedProjects')}</span>
                          <span className="claude-group-count-badge">
                            {projects.filter((p) => p.owner_id !== user?.id).length}
                          </span>
                        </button>
                        <div className="claude-project-group-scroll">
                          {projects.map(
                            (project, index) =>
                              project.owner_id !== user?.id && (
                                <div
                                  key={project.id}
                                  className={`claude-project-item ${index === activeProjectIndex ? 'active' : ''} ${project.needsAdminSub ? 'claude-disabled' : ''}`}
                                  onClick={() => selectProjectIndex(index)}
                                >
                                  <span className="claude-project-name">{project.name}</span>
                                  {project.owner_id === user?.id || project.myRole === '관리자' ? (
                                    <span
                                      className="claude-project-actions"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <button
                                        type="button"
                                        className="claude-project-edit"
                                        onClick={() => handleOpenEditProject(index)}
                                      >
                                        ✎
                                      </button>
                                      <button
                                        type="button"
                                        className="claude-project-delete"
                                        onClick={() => handleDeleteProject(index)}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  ) : (
                                    <span
                                      className="claude-project-actions"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <button
                                        type="button"
                                        className="claude-project-leave"
                                        onClick={() => handleLeaveProject(index)}
                                      >
                                        ×
                                      </button>
                                    </span>
                                  )}
                                </div>
                              )
                          )}
                        </div>
                      </div>
                    )}

                    {/* 내놓은 템플릿 */}
                    {projects.some((p) => p.owner_id === user?.id && p.is_template) && (
                      <div
                        className={`claude-project-group${collapsedGroups.publishedTemplates ? ' is-collapsed' : ''}`}
                      >
                        <button
                          type="button"
                          className="claude-project-group-title claude-project-group-toggle"
                          onClick={() => toggleGroupCollapsed('publishedTemplates')}
                          aria-expanded={!collapsedGroups.publishedTemplates}
                        >
                          <svg
                            className={`claude-group-chevron${collapsedGroups.publishedTemplates ? ' is-collapsed' : ''}`}
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                          <span>{t('sidebar.publishedTemplates')}</span>
                          <span className="claude-group-count-badge">
                            {
                              projects.filter(
                                (p) => p.owner_id === user?.id && p.is_template
                              ).length
                            }
                          </span>
                        </button>
                        <div className="claude-project-group-scroll">
                          {projects.map(
                            (project, index) =>
                              project.owner_id === user?.id &&
                              project.is_template && (
                                <div
                                  key={project.id}
                                  className={`claude-project-item ${index === activeProjectIndex ? 'active' : ''} ${project.isLimitReached ? 'claude-disabled' : ''}`}
                                  onClick={() => selectProjectIndex(index)}
                                >
                                  <span className="claude-project-name">{project.name}</span>
                                  <span
                                    className="claude-project-actions"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <button
                                      type="button"
                                      className="claude-project-edit"
                                      onClick={() => handleOpenEditProject(index)}
                                    >
                                      ✎
                                    </button>
                                    <button
                                      type="button"
                                      className="claude-project-delete"
                                      onClick={() => handleDeleteProject(index)}
                                    >
                                      ×
                                    </button>
                                  </span>
                                </div>
                              )
                          )}
                        </div>
                      </div>
                    )}

                    {/* 내놓은 월간 챌린지 */}
                    {projects.some((p) => p.owner_id === user?.id && p.is_challenge) && (
                      <div
                        className={`claude-project-group${collapsedGroups.publishedChallenges ? ' is-collapsed' : ''}`}
                      >
                        <button
                          type="button"
                          className="claude-project-group-title claude-project-group-toggle"
                          onClick={() => toggleGroupCollapsed('publishedChallenges')}
                          aria-expanded={!collapsedGroups.publishedChallenges}
                        >
                          <svg
                            className={`claude-group-chevron${collapsedGroups.publishedChallenges ? ' is-collapsed' : ''}`}
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            aria-hidden="true"
                          >
                            <polyline points="6 9 12 15 18 9" />
                          </svg>
                          <span>{t('sidebar.publishedChallenges')}</span>
                          <span className="claude-group-count-badge">
                            {
                              projects.filter(
                                (p) => p.owner_id === user?.id && p.is_challenge
                              ).length
                            }
                          </span>
                        </button>
                        <div className="claude-project-group-scroll">
                          {projects.map(
                            (project, index) =>
                              project.owner_id === user?.id &&
                              project.is_challenge && (
                                <div
                                  key={project.id}
                                  className={`claude-project-item ${index === activeProjectIndex ? 'active' : ''} ${project.isLimitReached ? 'claude-disabled' : ''}`}
                                  onClick={() => selectProjectIndex(index)}
                                >
                                  <span className="claude-project-name">{project.name}</span>
                                  <span
                                    className="claude-project-actions"
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <button
                                      type="button"
                                      className="claude-project-edit"
                                      onClick={() => handleOpenEditProject(index)}
                                    >
                                      ✎
                                    </button>
                                    <button
                                      type="button"
                                      className="claude-project-delete"
                                      onClick={() => handleDeleteProject(index)}
                                    >
                                      ×
                                    </button>
                                  </span>
                                </div>
                              )
                          )}
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="claude-project-empty">
                    <p>{t('sidebar.addProjectPrompt')}</p>
                  </div>
                )}
              </div>

              {/* 구독 상태 */}
              <div className="claude-subscription-status">
                {isMySubActive ? (
                  <div className="claude-sub-active">
                    <span className="claude-sub-badge">Pro</span>
                    <span className="claude-sub-text">
                      {subscription?.interval === 'year'
                        ? t('sidebar.subscribedYearly')
                        : t('sidebar.subscribedMonthly')}
                    </span>
                  </div>
                ) : (
                  <div className="claude-sub-free">
                    <span className="claude-sub-text">{t('sidebar.freePlan')}</span>
                    <button
                      className="claude-sub-upgrade"
                      onClick={() => setShowPricingPopup(true)}
                    >
                      {t('sidebar.upgrade')}
                    </button>
                  </div>
                )}
              </div>

              {/* <PromoBanner className="promo-banner-dashboard" /> */}
            </aside>

            {/* 메인 영역 */}
            <main
              className={`claude-dashboard-main ${activeProject?.isLimitReached || activeProject?.needsAdminSub ? 'claude-disabled' : ''}`}
            >
              {/* 빈 상태 */}
              {projects.length === 0 && (
                <div className="claude-empty-wrapper">
                  <div className="claude-empty-state">
                    <div className="claude-empty-icon">🚀</div>
                    <h3>{t('empty.title')}</h3>
                    <p>{t('empty.desc')}</p>
                    <button
                      className="claude-btn-primary"
                      onClick={() => {
                        if (!isMySubActive && myProjectCount >= 1) {
                          setShowPricingPopup(true);
                          return;
                        }
                        setShowCreatePopup(true);
                      }}
                    >
                      {t('empty.createFirst')}
                    </button>
                  </div>
                </div>
              )}

              {activeProject && !activeProject.detailLoaded && projects.length > 0 && (
                <div className="claude-dashboard-detail-loading claude-empty-wrapper">
                  <div className="claude-loading-content">
                    <div className="claude-loading-spinner" />
                  </div>
                </div>
              )}

              {/* 타임라인 */}
              {activeProject && activeProject.detailLoaded && (
                <div className="claude-timeline-section">
                  <div className="claude-timeline-header">
                    <div className="claude-timeline-month-nav">
                      <button
                        type="button"
                        className="claude-timeline-nav"
                        onClick={handlePrevMonth}
                        aria-label={t('timeline.prevMonth')}
                      >
                        <HiOutlineChevronLeft aria-hidden />
                      </button>
                      <span className="claude-timeline-month">
                        {currentMonth
                          ? `${currentMonth.start.getFullYear()}.${String(currentMonth.start.getMonth() + 1).padStart(2, '0')}`
                          : ''}
                      </span>
                      <button
                        type="button"
                        className="claude-timeline-nav"
                        onClick={handleNextMonth}
                        aria-label={t('timeline.nextMonth')}
                      >
                        <HiOutlineChevronRight aria-hidden />
                      </button>
                      <div
                        className="claude-view-toggle"
                        role="tablist"
                        aria-label={t('timeline.viewModeLabel')}
                      >
                        <button
                          type="button"
                          role="tab"
                          aria-selected={timelineView === 'kanban'}
                          aria-label={
                            projectTaskTotalUnread > 0
                              ? t('timeline.kanbanViewUnread', { n: projectTaskTotalUnread })
                              : t('timeline.kanbanView')
                          }
                          title={t('timeline.kanbanView')}
                          className={`claude-view-toggle-btn claude-view-toggle-btn--has-badge ${
                            timelineView === 'kanban' ? 'is-active' : ''
                          }`}
                          onClick={() => {
                            if (timelineView !== 'kanban') setTimelineView('kanban');
                          }}
                        >
                          <HiOutlineViewColumns className="claude-view-toggle-icon" aria-hidden />
                          {projectTaskTotalUnread > 0 && (
                            <span className="claude-view-toggle-dot" aria-hidden />
                          )}
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={timelineView === 'calendar'}
                          aria-label={t('timeline.calendarView')}
                          title={t('timeline.calendarView')}
                          className={`claude-view-toggle-btn ${
                            timelineView === 'calendar' ? 'is-active' : ''
                          }`}
                          onClick={() => setTimelineView('calendar')}
                        >
                          <MdOutlineCalendarMonth className="claude-view-toggle-icon" aria-hidden />
                        </button>
                        <button
                          type="button"
                          role="tab"
                          aria-selected={timelineView === 'stack'}
                          aria-label={t('timeline.snsView')}
                          title={t('timeline.snsView')}
                          className={`claude-view-toggle-btn claude-view-toggle-btn--has-badge ${
                            timelineView === 'stack' ? 'is-active' : ''
                          }`}
                          onClick={() => setTimelineView('stack')}
                        >
                          <HiOutlineBars4 className="claude-view-toggle-icon" aria-hidden />
                          {snsTotalUnread > 0 && (
                            <span className="claude-view-toggle-dot" aria-hidden />
                          )}
                        </button>
                      </div>
                    </div>
                    <div className="claude-timeline-actions">
                      <button type="button" className="claude-timeline-btn" onClick={handleGoToday}>
                        {t('timeline.today')}
                      </button>
                      <button
                        type="button"
                        className="claude-timeline-btn"
                        onClick={handleGoFirstTask}
                      >
                        {t('timeline.firstTask')}
                      </button>
                    </div>
                  </div>

                  {timelineView === 'kanban' && (
                    <div ref={timelineContainerRef} className="claude-timeline-container">
                      <div className="claude-timeline-table" style={{ minWidth: tableMinWidth }}>
                        {/* 열 리사이즈 라인 (전체 높이) */}
                        <div
                          ref={resizeLineRef}
                          className={`claude-column-resize-line ${isResizing ? 'active' : ''}`}
                          onMouseDown={handleResizeStart}
                          title={t('timeline.resizeColumn')}
                        />
                        {/* 테이블 헤더 */}
                        <div className="claude-timeline-row claude-header">
                          <div
                            className="claude-timeline-cell claude-category"
                            style={{ width: categoryColumnWidth, minWidth: categoryColumnWidth }}
                          >
                            <span>{t('timeline.taskCategory')}</span>
                            {(() => {
                              const isAdmin = canUserManageProjectAi(activeProject, user);
                              const canAddCategory =
                                isAdmin || !activeProject?.admin_only_add_category;
                              return (
                                <span className="claude-project-actions">
                                  <span className="claude-info-help-tooltip-host">
                                    <button
                                      type="button"
                                      className="claude-project-edit"
                                      aria-label={
                                        canAddCategory
                                          ? t('timeline.addTaskCategory')
                                          : t('timeline.addTaskCategoryAdmin')
                                      }
                                      disabled={!canAddCategory}
                                      onClick={() => {
                                        if (!canAddCategory) return;
                                        setNewCategoryName('');
                                        setNewCategoryError('');
                                        setShowAddCategoryPopup(true);
                                      }}
                                    >
                                      +
                                    </button>
                                    {!canAddCategory && (
                                      <span
                                        className="claude-disabled-control-hit-layer claude-disabled-control-hit-layer--circle tooltip-below"
                                        data-tooltip={t('common.adminOnly')}
                                        aria-hidden="true"
                                      />
                                    )}
                                  </span>
                                </span>
                              );
                            })()}
                          </div>
                          <div className="claude-timeline-cell claude-assignee-col">{t('timeline.assignee')}</div>
                          <div
                            className="claude-timeline-grid-header"
                            style={{
                              minWidth: gridWidth,
                              '--day-width': `${DAY_WIDTH}px`,
                              backgroundImage: mergedBackground,
                            }}
                          >
                            <div className="claude-month-label">
                              {currentMonth ? t('timeline.month', { n: currentMonth.start.getMonth() + 1 }) : ''}
                            </div>
                            <div className="claude-date-cells">
                              {timelineDates.map((date, i) => {
                                const dayIndex = date.getDay(); // 0: 일요일, 1: 월요일, ..., 6: 토요일
                                const isWeekend = dayIndex === 0 || dayIndex === 6;
                                const dayClass = isWeekend ? 'claude-date-weekend' : '';

                                return (
                                  <div
                                    key={i}
                                    className={`claude-date-cell ${todayIndex === i ? 'claude-date-today' : ''} ${dayClass}`}
                                  >
                                    {date.getDate()}
                                  </div>
                                );
                              })}
                            </div>
                            <div className="claude-day-cells">
                              {timelineDates.map((date, i) => {
                                const dayIndex = date.getDay(); // 0: 일요일, 1: 월요일, ..., 6: 토요일
                                const dayNames = t('timeline.weekdays', { returnObjects: true });
                                const dayClass =
                                  dayIndex === 0
                                    ? 'claude-day-sunday'
                                    : dayIndex === 6
                                      ? 'claude-day-saturday'
                                      : 'claude-day-weekday';

                                return (
                                  <div key={`day-${i}`} className={`claude-day-cell ${dayClass}`}>
                                    {dayNames[dayIndex]}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>

                        {/* 카테고리별 작업 */}
                        {activeProject.categories.map((category) => (
                          <div key={category.id} className="claude-category-group">
                            {/* 카테고리 헤더 */}
                            <div className="claude-timeline-row claude-category-header">
                              <div
                                className="claude-timeline-cell claude-category"
                                style={{
                                  width: categoryColumnWidth,
                                  minWidth: categoryColumnWidth,
                                }}
                              >
                                <div className="claude-category-content">
                                  <HiOutlineChevronDown
                                    className="claude-expand-icon"
                                    aria-hidden
                                  />
                                  <strong
                                    className="claude-category-name-clickable claude-category-name-text"
                                    onClick={() => {
                                      setEditCategoryId(category.id);
                                      setEditCategoryName(category.name);
                                      setShowEditCategoryPopup(true);
                                    }}
                                    title={category.name}
                                  >
                                    {category.name}
                                  </strong>
                                </div>
                                <div className="claude-project-actions">
                                  {(() => {
                                    const isAdmin = canUserManageProjectAi(activeProject, user);
                                    const canAddTask =
                                      isAdmin || !activeProject?.admin_only_add_task;
                                    return (
                                      <span className="claude-info-help-tooltip-host">
                                        <button
                                          type="button"
                                          className="claude-project-edit"
                                          onClick={() => {
                                            if (!canAddTask) return;
                                            handleAddTaskToCategory(category.id);
                                          }}
                                          disabled={!canAddTask}
                                          title={canAddTask ? t('task.addTaskTooltip') : t('common.adminOnly')}
                                        >
                                          +
                                        </button>
                                        {!canAddTask && (
                                          <span
                                            className="claude-disabled-control-hit-layer claude-disabled-control-hit-layer--circle"
                                            data-tooltip={t('common.adminOnly')}
                                            aria-hidden="true"
                                          />
                                        )}
                                      </span>
                                    );
                                  })()}
                                  {(activeProject.owner_id === user?.id ||
                                    activeProject.members?.find((m) => m.id === user?.id)?.role ===
                                      '관리자' ||
                                    category.createdBy === user?.id) && (
                                    <button
                                      type="button"
                                      className="claude-project-delete"
                                      onClick={() => handleDeleteCategory(category.id)}
                                      title={t('category.deleteTitle')}
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              </div>
                              <div className="claude-timeline-cell claude-assignee-col"></div>
                              <div
                                className="claude-timeline-grid"
                                style={{ minWidth: gridWidth, '--day-width': `${DAY_WIDTH}px` }}
                              ></div>
                            </div>

                            {/* 작업들 — '지나간 작업 안 보기' 켜져있으면 현재 보고 있는 달력 월에
                                포함되지 않은(= 기간이 겹치지 않는) 작업만 필터 */}
                            {category.tasks
                              .filter((task) => {
                                if (!activeProject?.hide_past_tasks) return true;
                                if (!currentMonth) return true;
                                if (!task.endDate || !task.startDate) return true;
                                const taskStart = new Date(task.startDate);
                                taskStart.setHours(0, 0, 0, 0);
                                const taskEnd = new Date(task.endDate);
                                taskEnd.setHours(23, 59, 59, 999);
                                const monthStart = new Date(currentMonth.start);
                                monthStart.setHours(0, 0, 0, 0);
                                const monthEnd = new Date(currentMonth.end);
                                monthEnd.setHours(23, 59, 59, 999);
                                // 작업 기간이 현재 달과 한 번이라도 겹치면 표시
                                return (
                                  taskEnd.getTime() >= monthStart.getTime() &&
                                  taskStart.getTime() <= monthEnd.getTime()
                                );
                              })
                              .map((task) => {
                                const position = getTaskPosition(task, currentMonth);
                                return (
                                  <div
                                    key={task.id}
                                    className="claude-timeline-row claude-task-row"
                                  >
                                    <div
                                      className="claude-timeline-cell claude-category claude-task-name"
                                      style={{
                                        width: categoryColumnWidth,
                                        minWidth: categoryColumnWidth,
                                      }}
                                    >
                                      <span
                                        className="claude-task-name-clickable claude-flex-1"
                                        onClick={() => {
                                          setSelectedTask(task);
                                          setSelectedCategory(category);
                                          setShowTaskPopup(true);
                                        }}
                                      >
                                        {task.name}
                                        {(taskUnreadById[task.id]?.total || 0) > 0 && (
                                          <span
                                            className="claude-task-unread-badge"
                                            aria-label={t('timeline.unreadCount', { n: taskUnreadById[task.id].total })}
                                          >
                                            {taskUnreadById[task.id].total > 99
                                              ? '99+'
                                              : taskUnreadById[task.id].total}
                                          </span>
                                        )}
                                        {generatingHintsTaskId === task.id && (
                                          <span
                                            className="claude-task-generating-indicator"
                                            title={t('aiGuide.generating')}
                                          >
                                            <span className="claude-task-generating-dot"></span>
                                          </span>
                                        )}
                                      </span>
                                      <div className="claude-project-actions">
                                        {(activeProject.owner_id === user?.id ||
                                          activeProject.members?.find((m) => m.id === user?.id)
                                            ?.role === '관리자' ||
                                          task.createdBy === user?.id) && (
                                          <button
                                            type="button"
                                            className="claude-project-delete"
                                            onClick={() => handleDeleteTask(category.id, task.id)}
                                            title={t('task.deleteTitle')}
                                          >
                                            ×
                                          </button>
                                        )}
                                      </div>
                                    </div>
                                    <div className="claude-timeline-cell claude-assignee-col">
                                      {(() => {
                                        const canAssign = canUserManageProjectAi(
                                          activeProject,
                                          user
                                        );
                                        const openAssign = () => {
                                          if (!canAssign) return;
                                          setAssignTarget({
                                            categoryId: category.id,
                                            taskId: task.id,
                                          });
                                          // 현재 담당자로 임시 선택 상태 초기화
                                          setAssignDraft(task.assignees.map((a) => a.id));
                                          setShowAssignPopup(true);
                                        };
                                        return (
                                          <>
                                            {task.assignees.length === 0 && (
                                              <span className="claude-info-help-tooltip-host">
                                                <button
                                                  className="claude-assign-btn"
                                                  onClick={openAssign}
                                                  disabled={!canAssign}
                                                  aria-label={
                                                    canAssign
                                                      ? t('timeline.assignMember')
                                                      : t('timeline.assignMemberAdmin')
                                                  }
                                                >
                                                  <PersonIcon />
                                                </button>
                                                {!canAssign && (
                                                  <span
                                                    className="claude-disabled-control-hit-layer claude-disabled-control-hit-layer--circle"
                                                    title={t('common.adminOnly')}
                                                    aria-hidden="true"
                                                  />
                                                )}
                                              </span>
                                            )}
                                            {task.assignees.length > 0 &&
                                              (() => {
                                                const totalMembers =
                                                  activeProject?.members?.length || 0;
                                                const assignedCount = task.assignees.length;
                                                const isAllAssigned =
                                                  totalMembers >= 2 &&
                                                  assignedCount === totalMembers;
                                                const MAX_VISIBLE = 3;
                                                const visible = isAllAssigned
                                                  ? []
                                                  : assignedCount > MAX_VISIBLE
                                                    ? task.assignees.slice(0, MAX_VISIBLE - 1)
                                                    : task.assignees;
                                                const overflow = isAllAssigned
                                                  ? 0
                                                  : assignedCount > MAX_VISIBLE
                                                    ? assignedCount - (MAX_VISIBLE - 1)
                                                    : 0;
                                                return (
                                                  <span className="claude-info-help-tooltip-host">
                                                    <div
                                                      className={`claude-assignee-avatars${canAssign ? ' claude-assignee-clickable' : ''}${visible.length > 1 ? ' claude-assignee-avatars--stack' : ''}`}
                                                      onClick={canAssign ? openAssign : undefined}
                                                      style={
                                                        canAssign
                                                          ? undefined
                                                          : { cursor: 'not-allowed' }
                                                      }
                                                    >
                                                      {isAllAssigned ? (
                                                        <span
                                                          className="claude-assignee-all-pill"
                                                          title={t('timeline.allMembers', { n: totalMembers })}
                                                        >
                                                          {t('timeline.all')}
                                                        </span>
                                                      ) : (
                                                        <>
                                                          {visible.map((a) => (
                                                            <SafeAvatar
                                                              key={a.id}
                                                              src={a.avatar}
                                                              alt="Avatar"
                                                              className="claude-avatar-circle-img"
                                                              fallbackClassName="claude-avatar-circle"
                                                            />
                                                          ))}
                                                          {overflow > 0 && (
                                                            <span
                                                              className="claude-avatar-circle claude-avatar-circle--more"
                                                              title={t('timeline.moreMembers', { n: overflow })}
                                                            >
                                                              +{overflow}
                                                            </span>
                                                          )}
                                                        </>
                                                      )}
                                                    </div>
                                                    {!canAssign && (
                                                      <span
                                                        className="claude-disabled-control-hit-layer"
                                                        title={t('common.adminOnly')}
                                                        aria-hidden="true"
                                                      />
                                                    )}
                                                  </span>
                                                );
                                              })()}
                                          </>
                                        );
                                      })()}
                                    </div>
                                    <div
                                      className="claude-timeline-grid"
                                      style={{
                                        minWidth: gridWidth,
                                        '--day-width': `${DAY_WIDTH}px`,
                                      }}
                                    >
                                      {!position.hidden && (
                                        <div
                                          className="claude-task-bar"
                                          style={{
                                            left: position.left,
                                            width: position.width,
                                            backgroundColor: getProgressColor(task.progress),
                                            opacity: Math.max(
                                              0.5,
                                              Math.min(1, 0.5 + (task.progress / 100) * 0.5)
                                            ),
                                          }}
                                          onClick={() => {
                                            setSelectedTask(task);
                                            setSelectedCategory(category);
                                            setShowTaskPopup(true);
                                          }}
                                        >
                                          <span
                                            className="claude-task-progress"
                                            style={{ color: getProgressTextColor(task.progress) }}
                                          >
                                            {task.progress}%
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {timelineView === 'stack' && activeProject && (
                    <div ref={timelineContainerRef} className="claude-stack-feed-wrap">
                      <p className="claude-stack-feed-hint">
                        {t('feed.info')}
                      </p>
                      {projectActivityFeed.length === 0 ? (
                        <p className="claude-stack-feed-empty">{t('feed.empty')}</p>
                      ) : (
                        <ul className="claude-stack-feed-list">
                          {projectActivityFeed.map((evt) => {
                            const typeLabel =
                              evt.type === 'post' ? t('feed.postCreated') : t('feed.commentCreated');
                            const TypeIconCmp =
                              evt.type === 'post'
                                ? HiOutlinePencilSquare
                                : HiOutlineChatBubbleLeftEllipsis;
                            const openTask = () => {
                              const cat = activeProject.categories.find(
                                (c) => c.id === evt.categoryId
                              );
                              const tsk = cat?.tasks.find((t) => t.id === evt.taskId);
                              if (cat && tsk) {
                                setSelectedCategory(cat);
                                setSelectedTask(tsk);
                                taskModalNextTabRef.current = 'posts';
                                setShowTaskPopup(true);
                              }
                            };
                            return (
                              <li key={evt.id} className="claude-stack-feed-li">
                                {/* 카드 외곽은 div+role=button — 안쪽 아바타 버튼과의 nested-button 문제 회피 */}
                                <div
                                  role="button"
                                  tabIndex={0}
                                  className={`claude-stack-feed-card type-${evt.type}`}
                                  onClick={openTask}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      openTask();
                                    }
                                  }}
                                >
                                  <div className="claude-stack-feed-avatar-wrap">
                                    <MemberAvatarButton
                                      id={evt.actor.id}
                                      name={evt.actor.name}
                                      avatar={evt.actor.avatar}
                                      onOpen={setActivityMember}
                                    >
                                      <SafeAvatar
                                        src={evt.actor.avatar}
                                        alt=""
                                        className="claude-stack-feed-avatar-img"
                                        fallbackClassName="claude-stack-feed-avatar-fallback"
                                      />
                                    </MemberAvatarButton>
                                  </div>
                                  <div className="claude-stack-feed-body">
                                    <div className="claude-stack-feed-meta-top">
                                      <span className="claude-stack-feed-name">
                                        {evt.actor.name}
                                      </span>
                                      <span className="claude-stack-feed-time">
                                        {formatStackFeedDate(new Date(evt.sortAt).toISOString())}
                                      </span>
                                    </div>
                                    <div className="claude-stack-feed-meta-sub">
                                      <span
                                        className={`claude-stack-feed-type claude-stack-feed-type--${evt.type}`}
                                        aria-label={typeLabel}
                                      >
                                        <span className="claude-stack-feed-type-icon" aria-hidden>
                                          <TypeIconCmp />
                                        </span>
                                      </span>
                                      <span className="claude-stack-feed-type-label">
                                        {typeLabel}
                                      </span>
                                      <span className="claude-stack-feed-dot">·</span>
                                      <span className="claude-stack-feed-path-seg">
                                        {evt.categoryName}
                                      </span>
                                      <span className="claude-stack-feed-dot">·</span>
                                      <span className="claude-stack-feed-path-seg">
                                        {evt.taskName}
                                      </span>
                                    </div>
                                    {evt.type === 'comment' &&
                                      (evt.parentCommentId ? (
                                        <div className="claude-stack-feed-quote">
                                          {evt.parentHiddenAt ? (
                                            <Trans
                                              t={t}
                                              i18nKey="feed.quoteCommentHidden"
                                              components={{ em: <em /> }}
                                            />
                                          ) : (
                                            <>{t('feed.quoteComment', { text: evt.parentSnippet })}</>
                                          )}
                                        </div>
                                      ) : evt.postHiddenAt ? (
                                        <div className="claude-stack-feed-quote">
                                          <Trans
                                            t={t}
                                            i18nKey="feed.quotePostHidden"
                                            components={{ em: <em /> }}
                                          />
                                        </div>
                                      ) : (
                                        evt.postSnippet && (
                                          <div className="claude-stack-feed-quote">
                                            {t('feed.quotePost', { text: evt.postSnippet })}
                                          </div>
                                        )
                                      ))}
                                    {evt.hiddenAt ? (
                                      canUserManageProjectAi(activeProject, user) ? (
                                        (() => {
                                          const key = `feed:${evt.id}`;
                                          const expanded = hiddenRevealed.has(key);
                                          return (
                                            <div className="claude-chat-hidden-block">
                                              <button
                                                type="button"
                                                className={`claude-chat-hidden-banner claude-chat-hidden-banner--toggle${expanded ? ' is-expanded' : ''}`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  toggleHiddenRevealed(key);
                                                }}
                                                aria-expanded={expanded}
                                              >
                                                <span>
                                                  {t('feed.hiddenBadge', {
                                                    type:
                                                      evt.type === 'post'
                                                        ? t('common.post')
                                                        : t('common.comment'),
                                                  })}
                                                </span>
                                                <HiOutlineChevronDown
                                                  className="claude-chat-hidden-chevron"
                                                  aria-hidden
                                                />
                                              </button>
                                              {expanded && (
                                                <div className="claude-chat-hidden-revealed">
                                                  {evt.body && (
                                                    <p className="claude-stack-feed-content">
                                                      {renderTextWithLinks(evt.body)}
                                                    </p>
                                                  )}
                                                  {evt.attachments &&
                                                    evt.attachments.length > 0 && (
                                                      <ChatAttachments
                                                        attachments={evt.attachments}
                                                        onOpenLightbox={(items, idx) =>
                                                          setChatLightbox({
                                                            items,
                                                            index: idx,
                                                          })
                                                        }
                                                      />
                                                    )}
                                                </div>
                                              )}
                                            </div>
                                          );
                                        })()
                                      ) : (
                                        <p className="claude-stack-feed-content claude-chat-hidden-placeholder">
                                          {t('feed.hiddenNotice', {
                                            type:
                                              evt.type === 'post'
                                                ? t('common.post')
                                                : t('common.comment'),
                                          })}
                                        </p>
                                      )
                                    ) : (
                                      <>
                                        {evt.body && (
                                          <p className="claude-stack-feed-content">
                                            {renderTextWithLinks(evt.body)}
                                          </p>
                                        )}
                                        {evt.attachments && evt.attachments.length > 0 && (
                                          <ChatAttachments
                                            attachments={evt.attachments}
                                            onOpenLightbox={(items, idx) =>
                                              setChatLightbox({ items, index: idx })
                                            }
                                          />
                                        )}
                                      </>
                                    )}
                                    {(() => {
                                      const cat = activeProject?.categories.find(
                                        (c) => c.id === evt.categoryId
                                      );
                                      const tsk = cat?.tasks.find((t) => t.id === evt.taskId);
                                      if (!tsk) return null;
                                      let likedByMe = false;
                                      let likeCount = 0;
                                      let replyCount = 0;
                                      let onLike;
                                      let onComment;
                                      if (evt.type === 'post') {
                                        const p = tsk.posts.find((p) => p.id === evt.postId);
                                        if (!p) return null;
                                        likedByMe = !!p.likedByMe;
                                        likeCount = p.likeCount || 0;
                                        replyCount = p.comments?.length || 0;
                                        onLike = () =>
                                          handleTogglePostLike(
                                            evt.categoryId,
                                            evt.taskId,
                                            evt.postId
                                          );
                                        onComment = () => {
                                          setSelectedCategory(cat);
                                          setSelectedTask(tsk);
                                          setReplyTargetComment(null);
                                          setCommentComposerPostId(evt.postId);
                                        };
                                      } else {
                                        const p = tsk.posts.find((p) => p.id === evt.postId);
                                        const c = p?.comments.find((c) => c.id === evt.commentId);
                                        if (!c) return null;
                                        likedByMe = !!c.likedByMe;
                                        likeCount = c.likeCount || 0;
                                        replyCount = c.replyCount || 0;
                                        onLike = () =>
                                          handleToggleCommentLike(
                                            evt.categoryId,
                                            evt.taskId,
                                            evt.postId,
                                            evt.commentId
                                          );
                                        onComment = () => {
                                          setSelectedCategory(cat);
                                          setSelectedTask(tsk);
                                          setReplyTargetComment(c);
                                          setCommentComposerPostId(evt.postId);
                                        };
                                      }
                                      return (
                                        <div
                                          className="claude-stack-feed-actions"
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          <button
                                            type="button"
                                            className={`claude-post-react-btn claude-post-react-btn--sm${likedByMe ? ' is-active' : ''}`}
                                            onClick={onLike}
                                            aria-pressed={likedByMe}
                                            aria-label={likedByMe ? t('common.unlike') : t('common.like')}
                                          >
                                            {likedByMe ? (
                                              <HiHeart aria-hidden />
                                            ) : (
                                              <HiOutlineHeart aria-hidden />
                                            )}
                                            <span className="claude-post-react-count">
                                              {likeCount}
                                            </span>
                                          </button>
                                          <button
                                            type="button"
                                            className="claude-post-react-btn claude-post-react-btn--sm"
                                            onClick={onComment}
                                            aria-label={
                                              evt.type === 'comment'
                                                ? t('comment.replyWrite')
                                                : t('comment.write')
                                            }
                                          >
                                            <HiOutlineChatBubbleLeftEllipsis aria-hidden />
                                            <span className="claude-post-react-count">
                                              {replyCount}
                                            </span>
                                          </button>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  )}

                  {timelineView === 'calendar' && (
                    <div
                      ref={timelineContainerRef}
                      className="claude-timeline-container claude-timeline-calendar-wrap"
                    >
                      <div
                        className="claude-calendar-month"
                        style={{ '--calendar-today-background': todayBackground }}
                      >
                        <div className="claude-calendar-weekday-row">
                          {t('timeline.weekdays', { returnObjects: true }).map((name, idx) => (
                            <div
                              key={name}
                              className={`claude-calendar-weekday ${
                                idx === 0
                                  ? 'is-sun claude-day-sunday'
                                  : idx === 6
                                    ? 'is-sat claude-day-saturday'
                                    : 'claude-day-weekday'
                              }`}
                            >
                              {name}
                            </div>
                          ))}
                        </div>
                        {Array.from(
                          { length: Math.ceil(calendarWeekCells.length / 7) || 0 },
                          (_, row) => {
                            const weekSlice = calendarWeekCells.slice(row * 7, row * 7 + 7);
                            const barSegments = calendarWeekBarSegmentsByRow[row] || [];
                            const maxLane = barSegments.reduce((m, s) => Math.max(m, s.lane), -1);
                            const hasBars = maxLane >= 0;
                            const canAddTask =
                              canUserManageProjectAi(activeProject, user) ||
                              !activeProject?.admin_only_add_task;

                            return (
                              <div
                                key={row}
                                className="claude-calendar-week-wrap"
                                style={{
                                  '--calendar-week-height': `${Math.max(
                                    88,
                                    32 + (maxLane + 1) * 22
                                  )}px`,
                                }}
                              >
                                <div className="claude-calendar-week-row claude-calendar-week-days">
                                  {weekSlice.map((day, col) => {
                                    const isToday =
                                      day && normalizeDate(day).getTime() === today.getTime();
                                    return (
                                      <div
                                        key={`${row}-${col}`}
                                        className={`claude-calendar-day ${!day ? 'is-empty' : ''} ${isToday ? 'is-today' : ''}`}
                                      >
                                        {day ? (
                                          <>
                                            {canAddTask && (
                                              <button
                                                type="button"
                                                className="claude-calendar-day-add"
                                                aria-label={t('timeline.newTask')}
                                                title={t('timeline.newTask')}
                                                onClick={() =>
                                                  handleAddTaskToCategory(
                                                    activeProject?.categories?.[0]?.id ?? null,
                                                    day
                                                  )
                                                }
                                              >
                                                +
                                              </button>
                                            )}
                                            <span className="claude-calendar-day-num claude-day-weekday">
                                              {day.getDate()}
                                            </span>
                                          </>
                                        ) : null}
                                      </div>
                                    );
                                  })}
                                </div>
                                {hasBars ? (
                                  <div className="claude-calendar-week-bars">
                                    {barSegments.map((seg) => (
                                      <button
                                        key={`${row}-${seg.task.id}-L${seg.lane}`}
                                        type="button"
                                        className={`claude-calendar-task-bar ${
                                          seg.continuesFromPrev ? 'is-continued-from-prev' : ''
                                        } ${seg.continuesToNext ? 'is-continued-to-next' : ''}`}
                                        style={{
                                          gridColumn: `${seg.startCol + 1} / ${seg.endCol + 2}`,
                                          gridRow: seg.lane + 1,
                                          backgroundColor: getProgressColor(seg.task.progress),
                                          opacity: Math.max(
                                            0.5,
                                            Math.min(1, 0.5 + (seg.task.progress / 100) * 0.5)
                                          ),
                                        }}
                                        title={`${seg.category.name} · ${seg.task.name}`}
                                        onClick={() => {
                                          setSelectedTask(seg.task);
                                          setSelectedCategory(seg.category);
                                          setShowTaskPopup(true);
                                        }}
                                      >
                                        <span
                                          className="claude-calendar-task-bar-text"
                                          style={{
                                            color: getProgressTextColor(seg.task.progress),
                                          }}
                                        >
                                          {`${seg.task.name} (${seg.task.progress}%)`}
                                        </span>
                                      </button>
                                    ))}
                                  </div>
                                ) : null}
                              </div>
                            );
                          }
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </main>

            {/* 오른쪽 사이드바 (모바일: 오른쪽 슬라이드 패널) */}
            {activeProject && activeProject.detailLoaded && (
              <>
                <button
                  type="button"
                  className="claude-dashboard-sidebar-mobile-btn"
                  onClick={() => {
                    setProjectSidebarOpen(false);
                    setDashboardSidebarOpen(true);
                  }}
                  aria-label={t('panel.summaryOpen')}
                >
                  <span className="claude-dashboard-sidebar-mobile-btn-icon" aria-hidden />
                  {t('panel.summary')}
                </button>
                {dashboardSidebarOpen && (
                  <button
                    type="button"
                    className="claude-dashboard-sidebar-backdrop"
                    aria-label={t('panel.closePanel')}
                    onClick={() => setDashboardSidebarOpen(false)}
                  />
                )}
                <aside
                  className={`claude-dashboard-sidebar${dashboardSidebarOpen ? ' is-open' : ''}`}
                >
                  <div className="claude-dashboard-sidebar-header">
                    <h3 className="claude-dashboard-sidebar-title">{t('panel.summaryTitle')}</h3>
                    <button
                      type="button"
                      className="claude-chat-close claude-dashboard-sidebar-close"
                      onClick={() => setDashboardSidebarOpen(false)}
                      aria-label={t('common.close')}
                    >
                      ×
                    </button>
                  </div>
                  {(activeProject.owner_id === user?.id ||
                    activeProject.members?.find((m) => m.id === user?.id)?.role === '관리자') &&
                    !activeProject.is_template &&
                    !activeProject.is_challenge && (
                      <div className="claude-cta-buttons">
                        <span className="claude-cta-btn-wrap">
                          <button
                            type="button"
                            className="claude-cta-btn"
                            disabled={!isMySubActive}
                            onClick={() => {
                              setTemplatePublishDescription('');
                              setTemplatePublishCategoryId('');
                              setTemplatePublishDescriptionError('');
                              setTemplatePublishCategoryError('');
                              setShowTemplatePublishModal(true);
                            }}
                          >
                            {t('template.publishTitle')}
                          </button>
                          {!isMySubActive && (
                            <span
                              className="claude-disabled-control-hit-layer claude-disabled-control-hit-layer--cta tooltip-below"
                              data-tooltip={t('template.premiumOnly')}
                              aria-hidden="true"
                            />
                          )}
                        </span>
                        <span className="claude-cta-btn-wrap">
                          <button
                            type="button"
                            className="claude-cta-btn"
                            disabled={!isMySubActive}
                            onClick={() => {
                              setChallengePublishCategoryId('');
                              setChallengePublishDescription('');
                              setChallengePublishPrice('');
                              setChallengePublishCategoryError('');
                              setChallengePublishDescriptionError('');
                              setChallengePublishPriceError('');
                              setShowChallengePublishModal(true);
                            }}
                          >
                            {t('challenge.publishTitle')}
                          </button>
                          {!isMySubActive && (
                            <span
                              className="claude-disabled-control-hit-layer claude-disabled-control-hit-layer--cta tooltip-below"
                              data-tooltip={t('challenge.premiumOnly')}
                              aria-hidden="true"
                            />
                          )}
                        </span>
                      </div>
                    )}

                  {activeProject.is_template && (
                    <div className="claude-sidebar-card claude-template-info-card">
                      <div className="claude-project-info-header">
                        <h3>{t('template.infoTitle')}</h3>
                        {(activeProject.owner_id === user?.id ||
                          activeProject.members?.find((m) => m.id === user?.id)?.role ===
                            '관리자') && (
                          <button
                            type="button"
                            className="claude-btn-outline claude-btn-sm"
                            onClick={openTemplateEditModal}
                          >
                            {t('template.edit')}
                          </button>
                        )}
                      </div>
                      <div className="claude-project-info claude-template-info-body">
                        <div className="claude-info-row">
                          <span className="claude-info-label">{t('template.categoryInfoLabel')}</span>
                          <span className="claude-info-value">
                            {activeProject.template_category_id
                              ? templateCategoryNameById[
                                  String(activeProject.template_category_id)
                                ] || '—'
                              : '—'}
                          </span>
                        </div>
                      </div>
                      {(activeProject.owner_id === user?.id ||
                        activeProject.members?.find((m) => m.id === user?.id)?.role ===
                          '관리자') && (
                        <div className="claude-template-revert-wrap">
                          <button
                            type="button"
                            className="claude-btn-outline claude-template-revert-btn"
                            onClick={handleTemplateRevertToProject}
                            disabled={templateRevertSubmitting}
                          >
                            {templateRevertSubmitting ? t('common.processing') : t('template.revert')}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {activeProject.is_challenge && (
                    <div className="claude-sidebar-card claude-template-info-card">
                      <div className="claude-project-info-header">
                        <h3>{t('challenge.infoTitle')}</h3>
                        {!activeProject.challenge_approved &&
                          (activeProject.owner_id === user?.id ||
                            activeProject.members?.find((m) => m.id === user?.id)?.role ===
                              '관리자') && (
                            <button
                              type="button"
                              className="claude-btn-outline claude-btn-sm"
                              onClick={openChallengeEdit}
                            >
                              {t('challenge.edit')}
                            </button>
                          )}
                      </div>
                      <div className="claude-project-info claude-template-info-body">
                        <div className="claude-info-row">
                          <span className="claude-info-label">{t('challenge.statusLabel')}</span>
                          <span className="claude-info-value">
                            {!activeProject.challenge_approved
                              ? t('challenge.statusPending')
                              : activeProject.challenge_hidden
                                ? t('challenge.statusHidden')
                                : t('challenge.statusApproved')}
                          </span>
                        </div>
                        <div className="claude-info-row">
                          <span className="claude-info-label">{t('challenge.priceLabel')}</span>
                          <span className="claude-info-value">
                            {activeProject.challenge_price != null
                              ? t('challenge.priceValue', {
                                  price: activeProject.challenge_price.toLocaleString('ko-KR'),
                                })
                              : '—'}
                          </span>
                        </div>
                      </div>
                      {(activeProject.owner_id === user?.id ||
                        activeProject.members?.find((m) => m.id === user?.id)?.role ===
                          '관리자') && (
                        <div className="claude-template-revert-wrap">
                          {!activeProject.challenge_approved && (
                            <p className="claude-challenge-pending-note">
                              {t('challenge.pendingNote')}
                            </p>
                          )}
                          {/* 참여자(참가자)가 없을 때만 내 프로젝트로 되돌리기 (다시 승인 필요) */}
                          {(activeProject.members || []).filter((m) => m.role === '참가자')
                            .length === 0 && (
                            <button
                              type="button"
                              className="claude-btn-outline claude-template-revert-btn"
                              onClick={handleChallengeRevert}
                              disabled={challengeRevertSubmitting}
                            >
                              {challengeRevertSubmitting
                                ? t('common.processing')
                                : t('challenge.revert')}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="claude-sidebar-card claude-project-info-card">
                    <div className="claude-project-info-header">
                      <h3>{t('panel.projectInfoTitle')}</h3>
                    </div>

                    <div className="claude-project-info">
                      <div className="claude-info-row">
                        <span className="claude-info-label">{t('panel.period')}</span>
                        <span className="claude-info-value">{projectDateRange}</span>
                      </div>
                      <div className="claude-info-row">
                        <span className="claude-info-label">{t('panel.cost')}</span>
                        <span className="claude-info-value">
                          {activeProject.updated_cost
                            ? `${parseInt(activeProject.updated_cost).toLocaleString()}`
                            : activeProject.ai_estimated_cost
                              ? `${parseInt(activeProject.ai_estimated_cost).toLocaleString()}`
                              : '?'}
                          {activeProject.cost_unit}
                          {user && (
                            <span className="claude-info-help-tooltip-host">
                              <button
                                type="button"
                                className="claude-circle-btn claude-btn-edit-circle"
                                aria-label={
                                  canUserManageProjectAi(activeProject, user)
                                    ? t('cost.edit')
                                    : t('cost.editAdmin')
                                }
                                title={
                                  canUserManageProjectAi(activeProject, user)
                                    ? t('cost.edit')
                                    : t('common.adminOnly')
                                }
                                onClick={handleCostEditOpen}
                                disabled={!canUserManageProjectAi(activeProject, user)}
                              >
                                ✎
                              </button>
                              {!canUserManageProjectAi(activeProject, user) && (
                                <span
                                  className="claude-disabled-control-hit-layer claude-disabled-control-hit-layer--circle"
                                  data-tooltip={t('common.adminOnly')}
                                  aria-hidden="true"
                                />
                              )}
                            </span>
                          )}
                          {user && (
                            <span className="claude-info-help-tooltip-host">
                              <button
                                type="button"
                                className="claude-info-help"
                                aria-label={
                                  canUserManageProjectAi(activeProject, user)
                                    ? t('cost.viewReason')
                                    : t('cost.viewReasonAdmin')
                                }
                                onClick={() => setShowCostReason(true)}
                                disabled={!canUserManageProjectAi(activeProject, user)}
                              >
                                AI
                              </button>
                              {!canUserManageProjectAi(activeProject, user) && (
                                <span
                                  className="claude-disabled-control-hit-layer claude-disabled-control-hit-layer--circle"
                                  data-tooltip={t('common.adminOnly')}
                                  aria-hidden="true"
                                />
                              )}
                            </span>
                          )}
                        </span>
                      </div>
                      {/* 관리자 전용 설정 토글들 — 관리자만 변경 가능, 프로젝트 단위 DB 저장 */}
                      {(() => {
                        const canToggle = canUserManageProjectAi(activeProject, user);
                        const renderToggle = (label, column) => (
                          <div className="claude-info-row" key={column}>
                            <span className="claude-info-label">{label}</span>
                            <span className="claude-info-toggle-host">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={!!activeProject[column]}
                                className={`claude-info-toggle${activeProject[column] ? ' is-on' : ''}`}
                                onClick={() =>
                                  canToggle &&
                                  handleToggleProjectFlag(column, !activeProject[column])
                                }
                                disabled={!canToggle}
                                aria-label={t('settings.toggleLabel', { label })}
                              >
                                <span className="claude-info-toggle-thumb" aria-hidden />
                              </button>
                              {!canToggle && (
                                <span
                                  className="claude-disabled-control-hit-layer claude-disabled-control-hit-layer--toggle"
                                  data-tooltip={t('common.adminOnly')}
                                  aria-hidden="true"
                                />
                              )}
                            </span>
                          </div>
                        );
                        return (
                          <>
                            <button
                              type="button"
                              className={`claude-info-collapse-btn${showAdminSettings ? ' is-open' : ''}`}
                              onClick={() => setShowAdminSettings((v) => !v)}
                              aria-expanded={showAdminSettings}
                              aria-controls="claude-admin-only-settings"
                            >
                              <span>{t('settings.adminHeading')}</span>
                              <HiOutlineChevronDown
                                className="claude-info-collapse-chevron"
                                aria-hidden
                              />
                            </button>
                            {showAdminSettings && (
                              <div id="claude-admin-only-settings">
                                {renderToggle(t('settings.hidePastTasks'), 'hide_past_tasks')}
                                {renderToggle(t('settings.adminOnlyAddCategory'), 'admin_only_add_category')}
                                {renderToggle(t('settings.adminOnlyAddTask'), 'admin_only_add_task')}
                                {renderToggle(
                                  t('settings.adminOnlyAddChecklist'),
                                  'admin_only_add_checklist'
                                )}
                                {renderToggle(
                                  t('settings.adminOnlyCheckChecklist'),
                                  'admin_only_check_checklist'
                                )}
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>

                  <div className="claude-sidebar-card claude-team-card">
                    {(() => {
                      const currentPendingInvites = pendingInvites[activeProject.id] || [];
                      const canManageTeam = canUserManageProjectAi(activeProject, user);
                      return (
                        <>
                          <div className="claude-team-card-header">
                            <h3>
                              {t('panel.collaborators', { n: activeProject.members.length + currentPendingInvites.length })}
                            </h3>
                            <span className="claude-team-invite-btn-tooltip-host">
                              <button
                                type="button"
                                className="claude-cta-btn claude-team-invite-btn"
                                onClick={() => {
                                  if (!canManageTeam || !isMySubActive) return;
                                  setShowInvitePopup(true);
                                }}
                                disabled={!canManageTeam || !isMySubActive}
                              >
                                {t('panel.inviteManage')}
                              </button>
                              {!canManageTeam ? (
                                <span
                                  className="claude-disabled-control-hit-layer claude-disabled-control-hit-layer--cta"
                                  data-tooltip={t('common.adminOnly')}
                                  aria-hidden="true"
                                />
                              ) : !isMySubActive ? (
                                <span
                                  className="claude-disabled-control-hit-layer claude-disabled-control-hit-layer--cta"
                                  data-tooltip={t('common.premiumOnly')}
                                  aria-hidden="true"
                                />
                              ) : null}
                            </span>
                          </div>
                          {(() => {
                            const sortedMembers = [...activeProject.members].sort((a, b) => {
                              const aJoinedAt = new Date(a.joined_at || 0).getTime();
                              const bJoinedAt = new Date(b.joined_at || 0).getTime();
                              return aJoinedAt - bJoinedAt;
                            });
                            const combinedList = [
                              ...sortedMembers.map((member) => ({
                                kind: 'member',
                                id: member.id,
                                avatar: member.avatar,
                                name: member.name,
                                email: member.email,
                                role: member.role,
                                joined_at: member.joined_at,
                              })),
                              ...currentPendingInvites.map((invite) => ({
                                kind: 'invite',
                                id: invite.id,
                                email: invite.email,
                              })),
                            ];
                            const showAll = showAllMembers[activeProject.id];
                            const visibleList = showAll ? combinedList : combinedList.slice(0, 2);

                            return (
                              <>
                                <div className="claude-team-list">
                                  {visibleList.map((item) => {
                                    if (item.kind === 'member') {
                                      // item.avatar 는 mapper 의 pickAvatar 결과 (url 또는 닉네임 첫 글자)
                                      // 본인이라고 OAuth 로 override 하지 않음 — 사용자가 지운 썸네일이 다시 보이는 것 방지
                                      const currentAvatar = item.avatar;

                                      return (
                                        <div key={item.id} className="claude-team-item">
                                          <button
                                            type="button"
                                            className="claude-team-avatar-btn"
                                            onClick={() =>
                                              setActivityMember({
                                                id: item.id,
                                                name: item.name,
                                                avatar: currentAvatar,
                                              })
                                            }
                                            aria-label={t('common.viewActivity', { name: item.name || t('common.member') })}
                                            title={t('common.viewActivity', { name: item.name || t('common.member') })}
                                          >
                                            <span className="claude-presence-wrap">
                                              <SafeAvatar
                                                src={currentAvatar}
                                                alt="Profile"
                                                className="claude-team-avatar-img claude-team-avatar-sm"
                                                fallbackClassName="claude-team-avatar claude-team-avatar-sm"
                                                as="div"
                                              />
                                              {onlineUserIds.has(item.id) && (
                                                <span className="claude-presence-dot" />
                                              )}
                                            </span>
                                          </button>
                                          <div className="claude-team-info">
                                            <div className="claude-team-top">
                                              <div className="claude-team-text">
                                                <strong>{item.name}</strong>
                                                <span className="claude-team-email">
                                                  {t('invite.joinedDate', {
                                                    date: new Date(item.joined_at).toLocaleDateString(
                                                      i18n.language
                                                    ),
                                                  })}
                                                </span>
                                              </div>
                                              <span className="claude-team-role">{item.role}</span>
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    }
                                    return (
                                      <div
                                        key={item.id}
                                        className="claude-team-item claude-team-pending"
                                      >
                                        <span className="claude-team-avatar claude-team-avatar-sm">
                                          📧
                                        </span>
                                        <div className="claude-team-info">
                                          <div className="claude-team-top">
                                            <div className="claude-team-text">
                                              <strong>{item.email}</strong>
                                              <span className="claude-team-email">
                                                {t('invite.inviteSent')}
                                              </span>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                                {combinedList.length > 2 && (
                                  <div className="claude-team-toggle">
                                    <button
                                      type="button"
                                      onClick={() =>
                                        setShowAllMembers((prev) => ({
                                          ...prev,
                                          [activeProject.id]: !showAll,
                                        }))
                                      }
                                    >
                                      {showAll ? t('common.collapse') : t('panel.expandMore', { n: combinedList.length - 2 })}
                                      <HiOutlineChevronDown
                                        className={`claude-chevron-toggle${showAll ? ' is-open' : ''}`}
                                        aria-hidden
                                      />
                                    </button>
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </>
                      );
                    })()}
                  </div>

                  {/* 진행상황 */}
                  <div className="claude-sidebar-card claude-progress-card">
                    <div className="claude-progress-header-row">
                      <h3>{t('panel.overallProgress')}</h3>
                      <span className="claude-progress-percent">{overallProgress}%</span>
                    </div>
                    <div className="claude-progress-bar-container">
                      <div
                        className="claude-progress-bar-fill"
                        style={{ width: `${overallProgress}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* 오늘 작업/체크리스트 */}
                  <div className="claude-sidebar-card claude-checklist-card">
                    <div className="claude-checklist-card-head">
                      <h3 className="claude-checklist-card-title">
                        {t('panel.checklistCount', { done: completedChecklistCount, total: totalChecklistCount })}
                      </h3>
                      <button
                        type="button"
                        className={`claude-checklist-filter-btn${hideCompletedChecklist ? ' is-active' : ''}`}
                        onClick={() => setHideCompletedChecklist((v) => !v)}
                        aria-pressed={hideCompletedChecklist}
                        title={
                          hideCompletedChecklist
                            ? t('checklist.showAllTooltip')
                            : t('checklist.todoOnlyTooltip')
                        }
                      >
                        {t('checklist.todoOnly')}
                      </button>
                    </div>
                    {hideCompletedChecklist &&
                      visibleChecklistItems.length === 0 &&
                      checklistItems.length > 0 && (
                        <p className="claude-checklist-empty">{t('checklist.allDone')}</p>
                      )}
                    <div className="claude-checklist-items claude-checklist-scroll">
                      {visibleChecklistItems.map((item) => (
                        <div key={item.id} className="claude-checklist-item">
                          <div className="claude-flex-align-center-gap-8" style={{ flex: 1 }}>
                            <input
                              type="checkbox"
                              className="claude-checkbox-input"
                              checked={item.completed}
                              disabled={
                                !canUserManageProjectAi(activeProject, user) &&
                                !!activeProject?.admin_only_check_checklist
                              }
                              title={
                                !canUserManageProjectAi(activeProject, user) &&
                                activeProject?.admin_only_check_checklist
                                  ? t('common.adminOnly')
                                  : undefined
                              }
                              onChange={() => {
                                if (
                                  !canUserManageProjectAi(activeProject, user) &&
                                  activeProject?.admin_only_check_checklist
                                ) {
                                  return;
                                }
                                // Find category and task for this checklist item
                                activeProject.categories.forEach((category) => {
                                  category.tasks.forEach((task) => {
                                    const found = task.checklist.find((cl) => cl.id === item.id);
                                    if (found) {
                                      handleToggleChecklist(category.id, task.id, item.id);
                                    }
                                  });
                                });
                              }}
                            />
                            <span
                              className={`claude-checklist-task-link ${
                                item.completed ? 'claude-completed' : ''
                              }`}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                const category = activeProject?.categories.find(
                                  (c) => c.id === item.categoryId
                                );
                                const task = category?.tasks.find((t) => t.id === item.taskId);
                                if (!category || !task) return;
                                setSelectedCategory(category);
                                setSelectedTask(task);
                                setShowTaskPopup(true);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  const category = activeProject?.categories.find(
                                    (c) => c.id === item.categoryId
                                  );
                                  const task = category?.tasks.find((t) => t.id === item.taskId);
                                  if (!category || !task) return;
                                  setSelectedCategory(category);
                                  setSelectedTask(task);
                                  setShowTaskPopup(true);
                                }
                              }}
                            >
                              {item.text}
                            </span>
                          </div>
                          {item.completed && item.completedBy && (
                            <div className="claude-checklist-completion-info">
                              <span className="claude-checklist-completed-by">
                                <MemberAvatarButton
                                  id={item.completedBy.id}
                                  name={item.completedBy.name}
                                  avatar={item.completedBy.avatar}
                                  onOpen={setActivityMember}
                                >
                                  <SafeAvatar
                                    src={item.completedBy.avatar}
                                    alt="Avatar"
                                    className="claude-avatar-circle-img"
                                    fallbackClassName="claude-avatar-circle"
                                  />
                                </MemberAvatarButton>
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </aside>
                {/* FAB는 aside 밖에 두어 모바일 drawer transform 시에도 viewport 고정 유지 */}
                <div className="claude-fab-container" ref={chatFabContainerRef}>
                  <button
                    type="button"
                    className="claude-chat-fab"
                    onClick={() => {
                      setShowChatPopup((prev) => {
                        const next = !prev;
                        if (next) setShowAiPopup(false);
                        return next;
                      });
                    }}
                  >
                    {t('chat.title')}
                    {totalChatUnread > 0 && (
                      <span
                        className="claude-chat-fab-badge"
                        aria-label={t('timeline.unreadCount', { n: totalChatUnread })}
                      >
                        {totalChatUnread > 99 ? '99+' : totalChatUnread}
                      </span>
                    )}
                  </button>
                  <span className="claude-info-edit-btn-tooltip-host">
                    <button
                      className="claude-ai-float-btn"
                      type="button"
                      disabled={!canUserManageProjectAi(activeProject, user)}
                      aria-label={
                        canUserManageProjectAi(activeProject, user)
                          ? t('ai.assistant')
                          : t('panel.aiAssistantAdmin')
                      }
                      onClick={() => {
                        setShowAiPopup((prev) => {
                          const next = !prev;
                          if (next) setShowChatPopup(false);
                          return next;
                        });
                      }}
                    >
                      AI
                    </button>
                    {!canUserManageProjectAi(activeProject, user) && (
                      <span
                        className="claude-disabled-control-hit-layer claude-disabled-control-hit-layer--circle"
                        data-tooltip={t('common.adminOnly')}
                        aria-hidden="true"
                      />
                    )}
                  </span>
                </div>
              </>
            )}
          </div>
        )}

        {/* 플로팅 채팅 팝업 */}
        {showChatPopup &&
          (() => {
            const activeDmThread = dmThreads.find((t) => t.id === activeDmThreadId);
            const threadByOther = new Map(dmThreads.map((t) => [t.otherId, t]));
            // 나(자기 자신)를 제외한 멤버를 '최근 메시지순'으로 정렬 (카카오톡식).
            // 메시지가 있는 대화가 위, 둘 다 없으면 기존 순서 유지.
            const membersWithoutMe = (activeProject?.members || [])
              .filter((m) => m.id !== user?.id)
              .sort((a, b) => {
                const ta = threadByOther.get(a.id)?.lastMessageAt;
                const tb = threadByOther.get(b.id)?.lastMessageAt;
                if (ta && tb) return new Date(tb) - new Date(ta);
                if (ta) return -1;
                if (tb) return 1;
                return 0;
              });
            // 맨 위 1열은 '나'(나와의 채팅) 고정.
            const meMember = (activeProject?.members || []).find((m) => m.id === user?.id);
            const selfRow = {
              id: user?.id,
              name: t('common.me'),
              avatar: meMember?.avatar,
              isSelf: true,
            };
            const dmListRows = user?.id ? [selfRow, ...membersWithoutMe] : membersWithoutMe;
            // 전체 채팅에서만 적용 — 관리자/소유자면 다른 사람 메시지 가리기 가능
            const isProjectAdmin = canUserManageProjectAi(activeProject, user);
            return (
              <div className="claude-chat-fab-popup" ref={chatPopupRef}>
                <div className="claude-chat-header">
                  {chatMode === 'dm' && activeDmThreadId && activeDmThread ? (
                    <button
                      type="button"
                      className="claude-chat-back-btn"
                      onClick={() => setActiveDmThreadId(null)}
                      aria-label={t('chat.dmBackToList')}
                      title={t('chat.dmBackToList')}
                    >
                      <HiOutlineChevronLeft aria-hidden />
                    </button>
                  ) : null}
                  {chatMode === 'dm' && activeDmThreadId && activeDmThread ? (
                    <div className="claude-chat-header-dm-title">
                      <MemberAvatarButton
                        id={activeDmThread.otherId}
                        name={activeDmThread.otherName}
                        avatar={activeDmThread.otherAvatar}
                        onOpen={setActivityMember}
                      >
                        <span className="claude-presence-wrap">
                          <SafeAvatar
                            src={activeDmThread.otherAvatar}
                            alt=""
                            className="claude-chat-header-avatar-img"
                            fallbackClassName="claude-chat-header-avatar"
                          />
                          {activeDmThread.otherId &&
                            onlineUserIds.has(activeDmThread.otherId) && (
                              <span className="claude-presence-dot" />
                            )}
                        </span>
                      </MemberAvatarButton>
                      <strong>{activeDmThread.otherName}</strong>
                    </div>
                  ) : (
                    <div className="claude-chat-tabs" role="tablist">
                      <button
                        type="button"
                        role="tab"
                        aria-selected={chatMode === 'project'}
                        className={`claude-chat-tab${chatMode === 'project' ? ' is-active' : ''}`}
                        onClick={() => setChatMode('project')}
                      >
                        <HiOutlineChatBubbleLeftRight aria-hidden />
                        {t('chat.all')}
                        {projectChatUnread > 0 && (
                          <span className="claude-chat-tab-badge">
                            {projectChatUnread > 99 ? '99+' : projectChatUnread}
                          </span>
                        )}
                      </button>
                      <button
                        type="button"
                        role="tab"
                        aria-selected={chatMode === 'dm'}
                        className={`claude-chat-tab${chatMode === 'dm' ? ' is-active' : ''}`}
                        onClick={() => setChatMode('dm')}
                      >
                        <HiOutlineUser aria-hidden />
                        DM
                        {dmUnreadTotal > 0 && (
                          <span className="claude-chat-tab-badge">
                            {dmUnreadTotal > 99 ? '99+' : dmUnreadTotal}
                          </span>
                        )}
                      </button>
                    </div>
                  )}
                  <button
                    type="button"
                    className="claude-chat-close"
                    onClick={() => setShowChatPopup(false)}
                    aria-label={t('chat.closeChat')}
                  >
                    ×
                  </button>
                </div>
                <div className="claude-chat-card">
                  {chatMode === 'dm' && !activeDmThreadId ? (
                    <div className="claude-chat-dm-list-wrap">
                      <ul className="claude-chat-dm-list">
                        {dmListRows.map((m) => {
                          const thread = threadByOther.get(m.id);
                            return (
                              <li key={m.id}>
                                <div
                                  role="button"
                                  tabIndex={0}
                                  className="claude-chat-dm-row"
                                  onClick={() => openDmWithMember(m.id)}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      openDmWithMember(m.id);
                                    }
                                  }}
                                >
                                  <MemberAvatarButton
                                    id={m.id}
                                    name={m.name}
                                    avatar={m.avatar}
                                    onOpen={setActivityMember}
                                  >
                                    <span className="claude-presence-wrap">
                                      <SafeAvatar
                                        src={m.avatar}
                                        alt=""
                                        className="claude-chat-dm-row-avatar-img"
                                        fallbackClassName="claude-chat-dm-row-avatar"
                                      />
                                      {onlineUserIds.has(m.id) && (
                                        <span className="claude-presence-dot" />
                                      )}
                                    </span>
                                  </MemberAvatarButton>
                                  <div className="claude-chat-dm-row-body">
                                    <div className="claude-chat-dm-row-top">
                                      <strong className="claude-chat-dm-row-name">{m.name}</strong>
                                      {thread?.lastMessageAt && (
                                        <span className="claude-chat-dm-row-time">
                                          {formatChatDate(thread.lastMessageAt)}
                                        </span>
                                      )}
                                    </div>
                                    <span
                                      className={`claude-chat-dm-row-snippet${
                                        !thread?.lastMessage ? ' is-empty' : ''
                                      }`}
                                    >
                                      {thread?.lastMessage || t('chat.startMessage')}
                                    </span>
                                  </div>
                                  {thread?.unreadCount > 0 && (
                                    <span
                                      className="claude-chat-dm-row-badge"
                                      aria-label={t('timeline.unreadCount', { n: thread.unreadCount })}
                                    >
                                      {thread.unreadCount > 99 ? '99+' : thread.unreadCount}
                                    </span>
                                  )}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                    </div>
                  ) : chatMode === 'dm' && activeDmThreadId ? (
                    <div className="claude-chat-dm-thread">
                      <div className="claude-chat-messages">
                        {dmMessages.map((msg) => {
                          const repliedTo = msg.replyToId
                            ? dmMessages.find((m) => m.id === msg.replyToId)
                            : null;
                          return (
                            <div
                              key={msg.id}
                              ref={(el) => {
                                if (el) dmMessageRefs.current.set(msg.id, el);
                                else dmMessageRefs.current.delete(msg.id);
                              }}
                              className={`claude-chat-message ${
                                msg.authorId === user?.id ? 'claude-mine' : ''
                              }${highlightedDmMessageId === msg.id ? ' is-highlighted' : ''}`}
                            >
                              {msg.authorId !== user?.id && (
                                <MemberAvatarButton
                                  id={msg.authorId}
                                  name={msg.author}
                                  avatar={msg.avatar}
                                  onOpen={setActivityMember}
                                >
                                  <span className="claude-presence-wrap">
                                    <SafeAvatar
                                      src={msg.avatar}
                                      alt="Avatar"
                                      className="claude-chat-avatar-img"
                                      fallbackClassName="claude-chat-avatar"
                                    />
                                    {onlineUserIds.has(msg.authorId) && (
                                      <span className="claude-presence-dot" />
                                    )}
                                  </span>
                                </MemberAvatarButton>
                              )}
                              <div className="claude-chat-bubble">
                                <div className="claude-flex-align-center-gap-8 claude-chat-bubble-top">
                                  <span className="claude-chat-author">
                                    {msg.authorId === user?.id ? t('common.me') : msg.author}
                                  </span>
                                  {editingDmMessageId !== msg.id && (
                                    <div className="claude-flex-align-center claude-margin-left-auto claude-chat-bubble-actions">
                                      {msg.authorId !== user?.id && (
                                        <button
                                          type="button"
                                          className="claude-circle-btn claude-btn-reply-circle"
                                          onClick={() => setDmReplyingToId(msg.id)}
                                          aria-label={t('common.reply')}
                                          title={t('common.reply')}
                                        >
                                          <HiOutlineArrowUturnLeft aria-hidden />
                                        </button>
                                      )}
                                      {msg.authorId === user?.id && (
                                        <>
                                          <button
                                            type="button"
                                            className="claude-circle-btn claude-btn-edit-circle"
                                            onClick={() => {
                                              setEditingDmMessageId(msg.id);
                                              setEditingDmMessageText(msg.message || '');
                                              setEditingDmAttachments(msg.attachments || []);
                                            }}
                                            aria-label={t('chat.editMessage')}
                                          >
                                            ✎
                                          </button>
                                          <button
                                            type="button"
                                            className="claude-circle-btn claude-btn-delete-circle"
                                            onClick={() => handleDeleteDmMessage(msg.id)}
                                            aria-label={t('chat.deleteMessage')}
                                          >
                                            ×
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                                {msg.replyToId && (
                                  <button
                                    type="button"
                                    className="claude-chat-reply-quote"
                                    onClick={() => jumpToDmMessage(msg.replyToId)}
                                    title={t('common.jumpToOriginal')}
                                    disabled={!repliedTo}
                                  >
                                    <span className="claude-chat-reply-quote-body">
                                      {!repliedTo ? (
                                        <span className="claude-chat-reply-quote-deleted">
                                          {t('chat.deletedMessage')}
                                        </span>
                                      ) : repliedTo.hiddenAt ? (
                                        <>
                                          <span className="claude-chat-reply-quote-author">
                                            {repliedTo.authorId === user?.id
                                              ? t('common.me')
                                              : repliedTo.author}
                                          </span>
                                          <span className="claude-chat-reply-quote-text claude-chat-reply-quote-text--hidden">
                                            {t('chat.hiddenMessage')}
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <span className="claude-chat-reply-quote-author">
                                            {repliedTo.authorId === user?.id
                                              ? t('common.me')
                                              : repliedTo.author}
                                          </span>
                                          <span className="claude-chat-reply-quote-text">
                                            {repliedTo.message ||
                                              (repliedTo.attachments?.length
                                                ? t('common.attachmentCount', { n: repliedTo.attachments.length })
                                                : '') ||
                                              ''}
                                          </span>
                                        </>
                                      )}
                                    </span>
                                  </button>
                                )}
                                {editingDmMessageId === msg.id ? (
                                  <div className="claude-chat-edit-wrapper">
                                    <textarea
                                      className="claude-chat-edit-input"
                                      value={editingDmMessageText}
                                      onChange={(e) => setEditingDmMessageText(e.target.value)}
                                      autoFocus
                                    />
                                    {editingDmAttachments.length > 0 && (
                                      <ChatAttachments
                                        attachments={editingDmAttachments}
                                        onOpenLightbox={(items, index) =>
                                          setChatLightbox({ items, index })
                                        }
                                        onRemove={(key) =>
                                          setEditingDmAttachments((prev) =>
                                            prev.filter((a) => a.key !== key)
                                          )
                                        }
                                      />
                                    )}
                                    <div className="claude-flex-gap-4 claude-justify-flex-end">
                                      <button
                                        type="button"
                                        className="claude-btn-save-text claude-chat-bubble-edit-actions"
                                        onClick={handleUpdateDmMessage}
                                      >
                                        {t('common.save')}
                                      </button>
                                      <button
                                        type="button"
                                        className="claude-btn-cancel-text claude-chat-bubble-edit-actions"
                                        onClick={() => {
                                          setEditingDmMessageId(null);
                                          setEditingDmMessageText('');
                                          setEditingDmAttachments([]);
                                        }}
                                      >
                                        {t('common.cancel')}
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <>
                                    {msg.message && (
                                      <p className="claude-chat-text">
                                        {renderTextWithLinks(msg.message)}
                                      </p>
                                    )}
                                    <ChatAttachments
                                      attachments={msg.attachments}
                                      onOpenLightbox={(items, index) =>
                                        setChatLightbox({ items, index })
                                      }
                                    />
                                  </>
                                )}
                                <span className="claude-chat-time">{msg.time}</span>
                              </div>
                              {!activeDmThread?.isSelf &&
                                msg.authorId === user?.id &&
                                dmOtherReadLoaded &&
                                new Date(msg.createdAt) > new Date(dmOtherReadAt || 0) && (
                                  <span className="claude-dm-unread-label">{t('chat.unread')}</span>
                                )}
                            </div>
                          );
                        })}
                        <div ref={dmEndRef} />
                      </div>
                      {dmReplyingToId &&
                        (() => {
                          const target = dmMessages.find((m) => m.id === dmReplyingToId);
                          return (
                            <div className="claude-chat-reply-bar" role="status">
                              <span className="claude-chat-reply-bar-icon" aria-hidden>
                                <HiOutlineArrowUturnLeft />
                              </span>
                              <div className="claude-chat-reply-bar-body">
                                {target ? (
                                  <>
                                    <span className="claude-chat-reply-bar-label">
                                      <Trans
                                        t={t}
                                        i18nKey="chat.replyTo"
                                        values={{ name: target.author }}
                                        components={{ strong: <strong /> }}
                                      />
                                    </span>
                                    <span className="claude-chat-reply-bar-snippet">
                                      {target.message ||
                                        (target.attachments?.length
                                          ? t('common.attachmentCount', { n: target.attachments.length })
                                          : '') ||
                                        ''}
                                    </span>
                                  </>
                                ) : (
                                  <span className="claude-chat-reply-bar-label">
                                    {t('chat.replyToDeleted')}
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                className="claude-chat-reply-bar-close"
                                onClick={() => setDmReplyingToId(null)}
                                aria-label={t('common.replyCancel')}
                                title={t('common.replyCancel')}
                              >
                                ×
                              </button>
                            </div>
                          );
                        })()}
                      {dmFiles.length > 0 && (
                        <div className="claude-chat-selected-files">
                          {dmFiles.map((item) => (
                            <span
                              key={item.id}
                              className={`claude-attachment-chip status-${item.status}`}
                              title={item.error || item.file.name}
                            >
                              <span className="claude-attachment-chip-name">
                                <HiOutlinePaperClip aria-hidden />
                                {item.file.name}
                              </span>
                              {(item.status === 'pending' || item.status === 'uploading') && (
                                <span className="claude-attachment-chip-progress">
                                  <span
                                    className="claude-attachment-chip-progress-bar"
                                    style={{ width: `${item.progress || 0}%` }}
                                  />
                                </span>
                              )}
                              {item.status === 'error' && (
                                <span className="claude-attachment-chip-error" title={item.error}>
                                  ⚠
                                </span>
                              )}
                              <button
                                type="button"
                                className="claude-chip-remove"
                                onClick={() => removeAttachmentEntry(dmFiles, item.id, setDmFiles)}
                                title={t('common.removeFile')}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="claude-chat-composer">
                        <input
                          type="file"
                          multiple
                          ref={dmFileInputRef}
                          onChange={(e) => {
                            const picked = Array.from(e.target.files || []);
                            if (picked.length === 0) return;
                            const entries = buildPendingEntries(picked);
                            setDmFiles((prev) => [...prev, ...entries]);
                            startUploadBatch({
                              kind: 'dm',
                              threadId: activeDmThreadId,
                              items: entries,
                              setItems: setDmFiles,
                            });
                            e.target.value = '';
                          }}
                          className="claude-display-none"
                          accept="image/*,video/*,application/pdf,application/zip,application/x-zip-compressed,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/*,.hwp,.hwpx,.hwt,.hpt"
                        />
                        <button
                          type="button"
                          className="claude-chat-attach-btn"
                          onClick={() => dmFileInputRef.current?.click()}
                          aria-label={t('common.attachFile')}
                          title={t('common.attachFile')}
                        >
                          <HiOutlinePaperClip />
                        </button>
                        <textarea
                          className="claude-chat-textarea"
                          placeholder={t('chat.messagePlaceholder')}
                          value={dmInput}
                          onChange={(e) => setDmInput(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
                              e.preventDefault();
                              handleSendDmMessage();
                            }
                          }}
                          rows={1}
                          aria-label={t('chat.dmInputLabel')}
                        />
                        <button
                          type="button"
                          className="claude-chat-send"
                          onClick={handleSendDmMessage}
                          disabled={
                            dmFiles.some(
                              (f) => f.status === 'pending' || f.status === 'uploading'
                            ) ||
                            (!dmInput.trim() && !dmFiles.some((f) => f.status === 'done'))
                          }
                          aria-label={t('common.send')}
                        >
                          <HiOutlinePaperAirplane />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="claude-chat-messages" ref={chatMessagesRef}>
                        {chatMessages.map((msg) => {
                          const repliedTo = msg.replyToId
                            ? chatMessages.find((m) => m.id === msg.replyToId)
                            : null;
                          return (
                            <div
                              key={msg.id}
                              ref={(el) => {
                                if (el) chatMessageRefs.current.set(msg.id, el);
                                else chatMessageRefs.current.delete(msg.id);
                              }}
                              className={`claude-chat-message ${msg.authorId === user?.id ? 'claude-mine' : ''}${
                                highlightedChatMessageId === msg.id ? ' is-highlighted' : ''
                              }`}
                            >
                              {msg.authorId !== user?.id && (
                                <MemberAvatarButton
                                  id={msg.authorId}
                                  name={msg.author}
                                  avatar={msg.avatar}
                                  onOpen={setActivityMember}
                                >
                                  <span className="claude-presence-wrap">
                                    <SafeAvatar
                                      src={msg.avatar}
                                      alt="Avatar"
                                      className="claude-chat-avatar-img"
                                      fallbackClassName="claude-chat-avatar"
                                    />
                                    {onlineUserIds.has(msg.authorId) && (
                                      <span className="claude-presence-dot" />
                                    )}
                                  </span>
                                </MemberAvatarButton>
                              )}
                              <div className="claude-chat-bubble">
                                <div className="claude-flex-align-center-gap-8 claude-chat-bubble-top">
                                  <span className="claude-chat-author">
                                    {msg.authorId === user?.id ? t('common.me') : msg.author}
                                  </span>
                                  {editingChatMessageId !== msg.id && (
                                    <div className="claude-flex-align-center claude-margin-left-auto claude-chat-bubble-actions">
                                      {msg.authorId !== user?.id && (
                                        <button
                                          type="button"
                                          className="claude-circle-btn claude-btn-reply-circle"
                                          onClick={() => startReplyTo(msg.id)}
                                          aria-label={t('common.reply')}
                                          title={t('common.reply')}
                                        >
                                          <HiOutlineArrowUturnLeft aria-hidden />
                                        </button>
                                      )}
                                      {msg.authorId !== user?.id && isProjectAdmin && (
                                        <button
                                          type="button"
                                          className={`claude-circle-btn claude-btn-hide-circle${msg.hiddenAt ? ' is-active' : ''}`}
                                          onClick={() => handleToggleHideChatMessage(msg.id)}
                                          aria-label={msg.hiddenAt ? t('common.unhide') : t('chat.hideMessage')}
                                          title={
                                            msg.hiddenAt ? t('common.unhide') : t('chat.hideMessageAdmin')
                                          }
                                        >
                                          {msg.hiddenAt ? (
                                            <HiOutlineEye aria-hidden />
                                          ) : (
                                            <HiOutlineEyeSlash aria-hidden />
                                          )}
                                        </button>
                                      )}
                                      {msg.authorId === user?.id && (
                                        <>
                                          <button
                                            type="button"
                                            className="claude-circle-btn claude-btn-edit-circle"
                                            onClick={() => {
                                              setEditingChatMessageId(msg.id);
                                              setEditingChatMessageText(msg.message || '');
                                              setEditingChatAttachments(msg.attachments || []);
                                            }}
                                            aria-label={t('chat.editMessage')}
                                          >
                                            ✎
                                          </button>
                                          <button
                                            type="button"
                                            className="claude-circle-btn claude-btn-delete-circle"
                                            onClick={() => handleDeleteChatMessage(msg.id)}
                                            aria-label={t('chat.deleteMessage')}
                                          >
                                            ×
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>
                                {msg.replyToId && (
                                  <button
                                    type="button"
                                    className="claude-chat-reply-quote"
                                    onClick={() => jumpToChatMessage(msg.replyToId)}
                                    title={t('common.jumpToOriginal')}
                                    disabled={!repliedTo}
                                  >
                                    <span className="claude-chat-reply-quote-body">
                                      {!repliedTo ? (
                                        <span className="claude-chat-reply-quote-deleted">
                                          {t('chat.deletedMessage')}
                                        </span>
                                      ) : repliedTo.hiddenAt ? (
                                        <>
                                          <span className="claude-chat-reply-quote-author">
                                            {repliedTo.authorId === user?.id
                                              ? t('common.me')
                                              : repliedTo.author}
                                          </span>
                                          <span className="claude-chat-reply-quote-text claude-chat-reply-quote-text--hidden">
                                            {t('chat.hiddenMessage')}
                                          </span>
                                        </>
                                      ) : (
                                        <>
                                          <span className="claude-chat-reply-quote-author">
                                            {repliedTo.authorId === user?.id
                                              ? t('common.me')
                                              : repliedTo.author}
                                          </span>
                                          <span className="claude-chat-reply-quote-text">
                                            {repliedTo.message ||
                                              (repliedTo.attachments?.length
                                                ? t('common.attachmentCount', { n: repliedTo.attachments.length })
                                                : '') ||
                                              ''}
                                          </span>
                                        </>
                                      )}
                                    </span>
                                  </button>
                                )}
                                {editingChatMessageId === msg.id ? (
                                  <div className="claude-chat-edit-wrapper">
                                    <textarea
                                      className="claude-chat-edit-input"
                                      value={editingChatMessageText}
                                      onChange={(e) => setEditingChatMessageText(e.target.value)}
                                      autoFocus
                                    />
                                    {editingChatAttachments.length > 0 && (
                                      <ChatAttachments
                                        attachments={editingChatAttachments}
                                        onOpenLightbox={(items, index) =>
                                          setChatLightbox({ items, index })
                                        }
                                        onRemove={(key) =>
                                          setEditingChatAttachments((prev) =>
                                            prev.filter((a) => a.key !== key)
                                          )
                                        }
                                      />
                                    )}
                                    <div className="claude-flex-gap-4 claude-justify-flex-end">
                                      <button
                                        type="button"
                                        className="claude-btn-save-text claude-chat-bubble-edit-actions"
                                        onClick={handleUpdateChatMessage}
                                      >
                                        {t('common.save')}
                                      </button>
                                      <button
                                        type="button"
                                        className="claude-btn-cancel-text claude-chat-bubble-edit-actions"
                                        onClick={() => {
                                          setEditingChatMessageId(null);
                                          setEditingChatMessageText('');
                                          setEditingChatAttachments([]);
                                        }}
                                      >
                                        {t('common.cancel')}
                                      </button>
                                    </div>
                                  </div>
                                ) : msg.hiddenAt ? (
                                  isProjectAdmin ? (
                                    (() => {
                                      const key = `chat:${msg.id}`;
                                      const expanded = hiddenRevealed.has(key);
                                      return (
                                        <div className="claude-chat-hidden-block">
                                          <button
                                            type="button"
                                            className={`claude-chat-hidden-banner claude-chat-hidden-banner--toggle${expanded ? ' is-expanded' : ''}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleHiddenRevealed(key);
                                            }}
                                            aria-expanded={expanded}
                                          >
                                            <span>{t('chat.hiddenMessageBadge')}</span>
                                            <HiOutlineChevronDown
                                              className="claude-chat-hidden-chevron"
                                              aria-hidden
                                            />
                                          </button>
                                          {expanded && (
                                            <div className="claude-chat-hidden-revealed">
                                              {msg.message && (
                                                <p className="claude-chat-text">
                                                  {renderTextWithLinks(msg.message)}
                                                </p>
                                              )}
                                              <ChatAttachments
                                                attachments={msg.attachments}
                                                onOpenLightbox={(items, index) =>
                                                  setChatLightbox({ items, index })
                                                }
                                              />
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()
                                  ) : (
                                    <p className="claude-chat-text claude-chat-hidden-placeholder">
                                      {t('chat.hiddenMessageNotice')}
                                    </p>
                                  )
                                ) : (
                                  <>
                                    {msg.message && (
                                      <p className="claude-chat-text">
                                        {renderTextWithLinks(msg.message)}
                                      </p>
                                    )}
                                    <ChatAttachments
                                      attachments={msg.attachments}
                                      onOpenLightbox={(items, index) =>
                                        setChatLightbox({ items, index })
                                      }
                                    />
                                  </>
                                )}
                                <span className="claude-chat-time">{msg.time}</span>
                              </div>
                            </div>
                          );
                        })}
                        <div ref={chatEndRef} />
                      </div>
                      {replyingToId &&
                        (() => {
                          const target = chatMessages.find((m) => m.id === replyingToId);
                          return (
                            <div className="claude-chat-reply-bar" role="status">
                              <span className="claude-chat-reply-bar-icon" aria-hidden>
                                <HiOutlineArrowUturnLeft />
                              </span>
                              <div className="claude-chat-reply-bar-body">
                                {target ? (
                                  <>
                                    <span className="claude-chat-reply-bar-label">
                                      <Trans
                                        t={t}
                                        i18nKey="chat.replyTo"
                                        values={{ name: target.author }}
                                        components={{ strong: <strong /> }}
                                      />
                                    </span>
                                    <span className="claude-chat-reply-bar-snippet">
                                      {target.message ||
                                        (target.attachments?.length
                                          ? t('common.attachmentCount', { n: target.attachments.length })
                                          : '') ||
                                        ''}
                                    </span>
                                  </>
                                ) : (
                                  <span className="claude-chat-reply-bar-label">
                                    {t('chat.replyToDeleted')}
                                  </span>
                                )}
                              </div>
                              <button
                                type="button"
                                className="claude-chat-reply-bar-close"
                                onClick={() => setReplyingToId(null)}
                                aria-label={t('common.replyCancel')}
                                title={t('common.replyCancel')}
                              >
                                ×
                              </button>
                            </div>
                          );
                        })()}
                      {chatFiles.length > 0 && (
                        <div className="claude-chat-selected-files">
                          {chatFiles.map((item) => (
                            <span
                              key={item.id}
                              className={`claude-attachment-chip status-${item.status}`}
                              title={item.error || item.file.name}
                            >
                              <span className="claude-attachment-chip-name">
                                <HiOutlinePaperClip aria-hidden />
                                {item.file.name}
                              </span>
                              {(item.status === 'pending' || item.status === 'uploading') && (
                                <span className="claude-attachment-chip-progress">
                                  <span
                                    className="claude-attachment-chip-progress-bar"
                                    style={{ width: `${item.progress || 0}%` }}
                                  />
                                </span>
                              )}
                              {item.status === 'error' && (
                                <span className="claude-attachment-chip-error" title={item.error}>
                                  ⚠
                                </span>
                              )}
                              <button
                                type="button"
                                className="claude-chip-remove"
                                onClick={() =>
                                  removeAttachmentEntry(chatFiles, item.id, setChatFiles)
                                }
                                title={t('common.removeFile')}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                      <div className="claude-chat-composer">
                        <input
                          type="file"
                          multiple
                          ref={chatFileInputRef}
                          onChange={(e) => {
                            const picked = capPickedFiles(
                              chatFiles.length,
                              Array.from(e.target.files || [])
                            );
                            if (picked.length === 0) {
                              e.target.value = '';
                              return;
                            }
                            const entries = buildPendingEntries(picked);
                            setChatFiles((prev) => [...prev, ...entries]);
                            startUploadBatch({
                              kind: 'project_chat',
                              projectId: activeProject?.id,
                              items: entries,
                              setItems: setChatFiles,
                            });
                            // 같은 파일 다시 고를 수 있도록 input 초기화
                            e.target.value = '';
                          }}
                          className="claude-display-none"
                          accept="image/*,video/*,application/pdf,application/zip,application/x-zip-compressed,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/*,.hwp,.hwpx,.hwt,.hpt"
                        />
                        <button
                          type="button"
                          className="claude-chat-attach-btn"
                          onClick={() => chatFileInputRef.current?.click()}
                          aria-label={t('common.attachFile')}
                          title={t('common.attachFile')}
                        >
                          <HiOutlinePaperClip />
                        </button>
                        <textarea
                          className="claude-chat-textarea"
                          placeholder={t('chat.messagePlaceholder')}
                          value={newMessage}
                          onChange={(e) => setNewMessage(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
                              e.preventDefault();
                              handleSendMessage();
                            }
                          }}
                          rows={1}
                          aria-label={t('chat.inputLabel')}
                        />
                        <button
                          type="button"
                          className="claude-chat-send"
                          onClick={handleSendMessage}
                          disabled={
                            chatFiles.some(
                              (f) => f.status === 'pending' || f.status === 'uploading'
                            ) ||
                            (!newMessage.trim() && !chatFiles.some((f) => f.status === 'done'))
                          }
                          aria-label={t('common.send')}
                        >
                          <HiOutlinePaperAirplane />
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })()}

        {/* AI 에이전트 대화 패널 (데스크톱 도크 / 모바일 바텀시트) */}
        {showAiPopup && (
          <>
            <div
              className="claude-ai-scrim"
              onClick={() => {
                if (!aiBusy) setShowAiPopup(false);
              }}
              aria-hidden="true"
            />
            <section
              className="claude-ai-panel"
              ref={aiPopupRef}
              role="dialog"
              aria-label={t('ai.assistant')}
            >
              {/* <div className="claude-ai-grabber" aria-hidden="true" /> */}

              <header className="claude-ai-topbar">
                <div className="claude-ai-brand">
                  <span className="claude-ai-brand-badge">
                    <SparkleIcon />
                  </span>
                  <div className="claude-ai-brand-text">
                    <strong>{t('ai.assistant')}</strong>
                  </div>
                </div>
                <button
                  type="button"
                  className="claude-chat-close"
                  onClick={() => {
                    if (!aiBusy) setShowAiPopup(false);
                  }}
                  aria-label={t('common.close')}
                  disabled={aiBusy}
                >
                  ×
                </button>
              </header>

              <div className="claude-ai-thread" ref={aiThreadRef}>
                {aiMessages.length === 0 && !aiBusy && (
                  <div className="claude-ai-empty">
                    <span className="claude-ai-empty-badge">
                      <SparkleIcon />
                    </span>
                    <p className="claude-ai-empty-title">{t('ai.emptyTitle')}</p>
                    <p className="claude-ai-empty-sub claude-pre-line">{t('ai.emptyDesc')}</p>
                  </div>
                )}

                {aiMessages.map((m, idx) => (
                  <div
                    key={idx}
                    className={`claude-ai-row ${m.role === 'user' ? 'is-user' : 'is-ai'}`}
                  >
                    {m.role === 'assistant' && (
                      <span className="claude-ai-avatar">
                        <SparkleIcon />
                      </span>
                    )}
                    <div className="claude-ai-bubble">
                      {m.role === 'assistant' ? (
                        <div className="claude-markdown">
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.content}</ReactMarkdown>
                        </div>
                      ) : (
                        m.content
                      )}
                    </div>
                  </div>
                ))}

                {aiBusy && (
                  <div className="claude-ai-row is-ai">
                    <span className="claude-ai-avatar">
                      <SparkleIcon />
                    </span>
                    <div className="claude-ai-statusbubble" aria-live="polite">
                      <HiOutlineArrowPath className="claude-ai-spin" />
                      <span className="claude-ai-status-label">
                        {aiStatus || t('ai.thinking')}
                        <span className="claude-ai-dots" aria-hidden="true" />
                      </span>
                    </div>
                  </div>
                )}

                {aiError && (
                  <div className="claude-ai-alert" role="alert">
                    {aiError}
                  </div>
                )}
              </div>

              {/* 변경 검토: 대화 영역 밖에 고정 노출 → 응답이 길어도 적용/취소가 항상 보임 */}
              {aiStaged.length > 0 && !aiBusy && (
                <div className="claude-ai-review">
                  <div className="claude-ai-review-head">
                    <span>{t('ai.proposedChanges')}</span>
                    <span className="claude-ai-review-count">{aiStaged.length}</span>
                  </div>
                  <div className="claude-ai-review-list">
                    {aiStaged.map((s) => {
                      const tone =
                        s.type && s.type.startsWith('add_')
                          ? 'add'
                          : s.type === 'invite_member'
                            ? 'add'
                            : s.type && s.type.startsWith('delete_')
                              ? 'delete'
                              : s.type === 'toggle_checklist'
                                ? 'neutral'
                                : 'edit';
                      const title = (s.preview && s.preview['작업']) || t('ai.changeFallbackTitle');
                      const lines = Object.entries(s.preview || {}).filter(([k]) => k !== '작업');
                      return (
                        <div key={s.id} className={`claude-ai-change tone-${tone}`}>
                          <span className="claude-ai-change-icon">
                            {s.type === 'invite_member' ? (
                              <HiOutlineUserPlus />
                            ) : tone === 'add' ? (
                              <HiOutlinePlus />
                            ) : tone === 'delete' ? (
                              <HiOutlineTrash />
                            ) : tone === 'neutral' ? (
                              <HiOutlineCheckCircle />
                            ) : (
                              <HiOutlinePencilSquare />
                            )}
                          </span>
                          <div className="claude-ai-change-body">
                            <p className="claude-ai-change-title">{title}</p>
                            {lines.map(([k, v]) => (
                              <p key={k} className="claude-ai-change-line">
                                <span className="claude-ai-change-key">{k}</span>
                                {String(v)}
                              </p>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="claude-ai-review-actions">
                    <button
                      type="button"
                      className="claude-ai-btn-ghost"
                      onClick={handleAiCancelStaged}
                    >
                      {t('common.cancel')}
                    </button>
                    <button type="button" className="claude-ai-btn-primary" onClick={handleAiApply}>
                      {t('ai.apply')}
                    </button>
                  </div>
                </div>
              )}

              {aiShowExamples && (
                <div className="claude-ai-suggest">
                  <p className="claude-ai-suggest-title">{t('ai.exampleQuestions')}</p>
                  <div className="claude-ai-suggest-list">
                    {aiQuestions.map((q, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className="claude-ai-chip"
                        onClick={() => handleAiSend(q)}
                        disabled={aiBusy}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="claude-ai-composer">
                <button
                  type="button"
                  className={`claude-ai-iconbtn claude-ai-suggest-toggle${aiShowExamples ? ' is-active' : ''}`}
                  onClick={() => setAiShowExamples((v) => !v)}
                  aria-label={t('ai.exampleQuestions')}
                  aria-pressed={aiShowExamples}
                  title={t('ai.exampleQuestions')}
                  disabled={aiBusy}
                >
                  <SparkleIcon />
                </button>
                <textarea
                  className="claude-ai-textarea"
                  placeholder={t('ai.composerPlaceholder')}
                  value={aiInput}
                  onChange={(e) => setAiInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
                      e.preventDefault();
                      handleAiSend();
                    }
                  }}
                  rows={1}
                  disabled={aiBusy}
                />
                <button
                  type="button"
                  className="claude-ai-send"
                  onClick={() => handleAiSend()}
                  disabled={aiBusy || !aiInput.trim()}
                  aria-label={t('common.send')}
                >
                  <HiOutlinePaperAirplane />
                </button>
              </div>
            </section>
          </>
        )}

        {/* 멤버 활동 캘린더 팝업 */}
        {activityMember && activeProject && (
          <MemberActivityPopup
            member={activityMember}
            project={activeProject}
            isOnline={activityMember.id ? onlineUserIds.has(activityMember.id) : false}
            isAdmin={canUserManageProjectAi(activeProject, user)}
            onClose={() => setActivityMember(null)}
            onSendDm={
              activityMember.id && activityMember.id !== user?.id
                ? (memberId) => openDmWithMember(memberId)
                : undefined
            }
          />
        )}

        {/* 채팅 첨부 라이트박스 */}
        {chatLightbox && (
          <ChatLightbox
            items={chatLightbox.items}
            index={chatLightbox.index}
            onChangeIndex={(i) => setChatLightbox((prev) => (prev ? { ...prev, index: i } : prev))}
            onClose={() => setChatLightbox(null)}
          />
        )}

        {/* 댓글 작성 팝업 — 작업 팝업 위에 떠야 하므로 별도 overlay 클래스로 z-index 더 위로 */}
        {commentComposerPostId && selectedTask && selectedCategory && (
          <div
            className="claude-popup-overlay claude-comment-composer-overlay"
            onClick={closeCommentComposer}
          >
            <div
              className="claude-popup-modal claude-comment-composer-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className="claude-popup-close"
                onClick={closeCommentComposer}
                aria-label={t('common.close')}
              >
                ×
              </button>
              <h2>{t('comment.writeTitle')}</h2>
              <div className="claude-post-composer-wrap claude-comment-composer-wrap">
                {(commentFiles[commentComposerPostId] || []).length > 0 && (
                  <div className="claude-chat-selected-files">
                    {(commentFiles[commentComposerPostId] || []).map((f) => (
                      <span
                        key={f.id}
                        className={`claude-attachment-chip claude-attachment-chip-sm${
                          f.status === 'uploading' ? ' is-uploading' : ''
                        }${f.status === 'error' ? ' is-error' : ''}`}
                      >
                        <HiOutlinePaperClip aria-hidden /> {f.file.name}
                        {f.status === 'uploading' && <span> · {f.progress}%</span>}
                        {f.status === 'error' && <span>{t('common.failedSuffix')}</span>}
                        <button
                          type="button"
                          className="claude-chip-remove"
                          onClick={() =>
                            removeAttachmentEntry(
                              commentFiles[commentComposerPostId] || [],
                              f.id,
                              (updater) =>
                                setCommentFiles((prev) => ({
                                  ...prev,
                                  [commentComposerPostId]:
                                    typeof updater === 'function'
                                      ? updater(prev[commentComposerPostId] || [])
                                      : updater,
                                }))
                            )
                          }
                          title={t('common.removeFile')}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="claude-chat-composer">
                  <input
                    type="file"
                    multiple
                    id={`file-input-${commentComposerPostId}`}
                    className="claude-display-none"
                    onChange={(e) => {
                      const picked = capPickedFiles(
                        (commentFiles[commentComposerPostId] || []).length,
                        Array.from(e.target.files || [])
                      );
                      if (picked.length === 0) {
                        e.target.value = '';
                        return;
                      }
                      const entries = buildPendingEntries(picked);
                      setCommentFiles((prev) => ({
                        ...prev,
                        [commentComposerPostId]: [
                          ...(prev[commentComposerPostId] || []),
                          ...entries,
                        ],
                      }));
                      startUploadBatch({
                        kind: 'comment',
                        projectId: activeProject?.id,
                        taskId: selectedTask.id,
                        postId: commentComposerPostId,
                        items: entries,
                        setItems: (updater) =>
                          setCommentFiles((prev) => ({
                            ...prev,
                            [commentComposerPostId]:
                              typeof updater === 'function'
                                ? updater(prev[commentComposerPostId] || [])
                                : updater,
                          })),
                      });
                      e.target.value = '';
                    }}
                    accept="image/*,video/*,application/pdf,application/zip,application/x-zip-compressed,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/*,.hwp,.hwpx,.hwt,.hpt"
                  />
                  <button
                    type="button"
                    className="claude-chat-attach-btn"
                    onClick={() =>
                      document.getElementById(`file-input-${commentComposerPostId}`)?.click()
                    }
                    aria-label={t('common.attachFile')}
                    title={t('common.attachFile')}
                  >
                    <HiOutlinePaperClip />
                  </button>
                  <textarea
                    rows={1}
                    autoFocus
                    placeholder={t('comment.placeholder')}
                    value={newComments[commentComposerPostId] || ''}
                    onFocus={scrollInputIntoCenterOnFocus}
                    onChange={(e) => {
                      setNewComments({
                        ...newComments,
                        [commentComposerPostId]: e.target.value,
                      });
                      if (commentErrors[commentComposerPostId]) {
                        setCommentErrors((prev) => ({
                          ...prev,
                          [commentComposerPostId]: '',
                        }));
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
                        e.preventDefault();
                        handleAddComment(
                          selectedCategory.id,
                          selectedTask.id,
                          commentComposerPostId,
                          replyTargetComment?.id || null
                        );
                      }
                    }}
                    className={`claude-chat-textarea${
                      commentErrors[commentComposerPostId] ? ' claude-input-error' : ''
                    }`}
                  />
                  <button
                    type="button"
                    className="claude-chat-send"
                    onClick={() =>
                      handleAddComment(
                        selectedCategory.id,
                        selectedTask.id,
                        commentComposerPostId,
                        replyTargetComment?.id || null
                      )
                    }
                    disabled={
                      (commentFiles[commentComposerPostId] || []).some(
                        (f) => f.status === 'pending' || f.status === 'uploading'
                      ) ||
                      (!(newComments[commentComposerPostId] || '').trim() &&
                        !(commentFiles[commentComposerPostId] || []).some(
                          (f) => f.status === 'done'
                        ))
                    }
                    aria-label={t('comment.sendLabel')}
                  >
                    <HiOutlinePaperAirplane />
                  </button>
                </div>
                {commentErrors[commentComposerPostId] && (
                  <div className="claude-error-text claude-error-text-composer">
                    {commentErrors[commentComposerPostId]}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* 프로젝트 제목 수정 팝업 */}
        {showEditProjectPopup && (
          <div className="claude-popup-overlay" onClick={() => setShowEditProjectPopup(false)}>
            <div className="claude-popup-modal claude-small" onClick={(e) => e.stopPropagation()}>
              <button className="claude-popup-close" onClick={() => setShowEditProjectPopup(false)}>
                ×
              </button>
              <h2>{t('project.editTitle')}</h2>
              <div className="claude-form-group">
                <div>
                  {t('project.nameLabel')} <span className="claude-color-required">*</span>
                </div>
                <input
                  type="text"
                  value={editProjectName}
                  className={editProjectError ? 'claude-input-error' : ''}
                  onChange={(e) => {
                    setEditProjectName(e.target.value);
                    if (editProjectError) setEditProjectError('');
                  }}
                  onFocus={scrollInputIntoCenterOnFocus}
                  placeholder={t('project.namePlaceholder')}
                />
                {editProjectError && <div className="claude-error-text">{editProjectError}</div>}
              </div>
              <div className="claude-template-publish-actions">
                <button
                  type="button"
                  className="claude-btn-outline"
                  onClick={() => setShowEditProjectPopup(false)}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="claude-btn-primary"
                  onClick={handleSaveProjectName}
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 작업 카테고리 추가 팝업 */}
        {showAddCategoryPopup && (
          <div
            className="claude-popup-overlay claude-popup-overlay--top"
            onClick={() => setShowAddCategoryPopup(false)}
          >
            <div className="claude-popup-modal claude-small" onClick={(e) => e.stopPropagation()}>
              <button className="claude-popup-close" onClick={() => setShowAddCategoryPopup(false)}>
                ×
              </button>
              <h2>{t('category.addTitle')}</h2>
              <div className="claude-form-group">
                <div>{t('category.nameLabel')}</div>
                <input
                  type="text"
                  value={newCategoryName}
                  className={newCategoryError ? 'claude-input-error' : ''}
                  onChange={(e) => {
                    setNewCategoryName(e.target.value);
                    if (newCategoryError) setNewCategoryError('');
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddCategory()}
                  onFocus={scrollInputIntoCenterOnFocus}
                  placeholder={t('category.addPlaceholder')}
                />
                {newCategoryError && <div className="claude-error-text">{newCategoryError}</div>}
              </div>
              <div className="claude-template-publish-actions">
                <button
                  type="button"
                  className="claude-btn-outline"
                  onClick={() => setShowAddCategoryPopup(false)}
                >
                  {t('common.cancel')}
                </button>
                <button type="button" className="claude-btn-primary" onClick={handleAddCategory}>
                  {t('common.add')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 작업 카테고리 수정 팝업 */}
        {showEditCategoryPopup && (
          <div className="claude-popup-overlay" onClick={() => setShowEditCategoryPopup(false)}>
            <div className="claude-popup-modal claude-small" onClick={(e) => e.stopPropagation()}>
              <button
                className="claude-popup-close"
                onClick={() => setShowEditCategoryPopup(false)}
              >
                ×
              </button>
              <h2>{t('category.editTitle')}</h2>
              <div className="claude-form-group">
                <div>{t('category.nameLabel')}</div>
                <input
                  type="text"
                  value={editCategoryName}
                  className={editCategoryError ? 'claude-input-error' : ''}
                  onChange={(e) => {
                    setEditCategoryName(e.target.value);
                    if (editCategoryError) setEditCategoryError('');
                  }}
                  onKeyPress={(e) => e.key === 'Enter' && handleEditCategory()}
                  onFocus={scrollInputIntoCenterOnFocus}
                  placeholder={t('category.editPlaceholder')}
                />
                {editCategoryError && <div className="claude-error-text">{editCategoryError}</div>}
              </div>
              <div className="claude-template-publish-actions">
                <button
                  type="button"
                  className="claude-btn-outline"
                  onClick={() => setShowEditCategoryPopup(false)}
                >
                  {t('common.cancel')}
                </button>
                <button type="button" className="claude-btn-primary" onClick={handleEditCategory}>
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 작업 수정 팝업 */}

        {/* 새 작업 생성 팝업 */}
        {showCreateTaskPopup && (
          <div className="claude-popup-overlay" onClick={closeCreateTaskPopup}>
            <div
              className="claude-popup-modal claude-create-task-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="claude-popup-close" onClick={closeCreateTaskPopup}>
                ×
              </button>
              <h2>{t('task.createTitle')}</h2>
              <div className="claude-create-task-scroll">
                <div className="claude-form-group">
                  <div>
                    {t('task.categoryLabel')} <span className="claude-color-required">*</span>
                  </div>
                  <div className="claude-task-category-row">
                    <select
                      value={createTaskCategoryId || ''}
                      onChange={(e) => {
                        setCreateTaskCategoryId(e.target.value || null);
                        if (newTaskErrors.category)
                          setNewTaskErrors({ ...newTaskErrors, category: '' });
                      }}
                      className={`claude-task-category-select${newTaskErrors.category ? ' claude-input-error' : ''}`}
                    >
                      <option value="">
                        {(activeProject?.categories || []).length === 0
                          ? t('task.noCategoryOption')
                          : t('task.selectCategory')}
                      </option>
                      {(activeProject?.categories || []).map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      className="claude-btn-primary claude-task-category-btn"
                      onClick={() => {
                        setNewCategoryName('');
                        setNewCategoryError('');
                        setShowAddCategoryPopup(true);
                      }}
                    >
                      {t('common.add')}
                    </button>
                  </div>
                  {newTaskErrors.category && (
                    <div className="claude-error-text">{newTaskErrors.category}</div>
                  )}
                </div>
                <div className="claude-form-group">
                  <div>
                    {t('task.nameLabel')} <span className="claude-color-required">*</span>
                  </div>
                  <input
                    type="text"
                    value={newTaskData.name}
                    onChange={(e) => {
                      setNewTaskData({ ...newTaskData, name: e.target.value });
                      if (newTaskErrors.name) setNewTaskErrors({ ...newTaskErrors, name: '' });
                    }}
                    className={newTaskErrors.name ? 'claude-input-error' : ''}
                    placeholder={t('task.namePlaceholder')}
                  />
                  {newTaskErrors.name && (
                    <div className="claude-error-text">{newTaskErrors.name}</div>
                  )}
                </div>

                <div className="claude-form-row claude-form-row--keep-cols">
                  <div className="claude-form-group">
                    <div>
                      {t('task.startDate')} <span className="claude-color-required">*</span>
                    </div>
                    <input
                      type="date"
                      value={newTaskData.startDate}
                      onChange={(e) => {
                        const nextStartDate = e.target.value;
                        setNewTaskData((prev) => {
                          if (!prev.startDate || !prev.endDate || !nextStartDate) {
                            return { ...prev, startDate: nextStartDate };
                          }

                          const parseDateInput = (value) => {
                            const [year, month, day] = value.split('-').map(Number);
                            return new Date(year, month - 1, day);
                          };
                          const formatDateInput = (date) => {
                            const y = date.getFullYear();
                            const m = String(date.getMonth() + 1).padStart(2, '0');
                            const d = String(date.getDate()).padStart(2, '0');
                            return `${y}-${m}-${d}`;
                          };

                          const prevStart = parseDateInput(prev.startDate);
                          const prevEnd = parseDateInput(prev.endDate);
                          const newStart = parseDateInput(nextStartDate);
                          const diffDays = Math.round((prevEnd - prevStart) / DAY_MS);
                          const nextEnd = new Date(newStart);
                          nextEnd.setDate(newStart.getDate() + diffDays);

                          return {
                            ...prev,
                            startDate: nextStartDate,
                            endDate: formatDateInput(nextEnd),
                          };
                        });
                        if (newTaskErrors.startDate)
                          setNewTaskErrors({ ...newTaskErrors, startDate: '' });
                      }}
                      className={newTaskErrors.startDate ? 'claude-input-error' : ''}
                    />
                    {newTaskErrors.startDate && (
                      <div className="claude-error-text">{newTaskErrors.startDate}</div>
                    )}
                  </div>
                  <div className="claude-form-group">
                    <div>
                      {t('task.endDate')} <span className="claude-color-required">*</span>
                    </div>
                    <input
                      type="date"
                      value={newTaskData.endDate}
                      onChange={(e) => {
                        setNewTaskData({ ...newTaskData, endDate: e.target.value });
                        if (newTaskErrors.endDate)
                          setNewTaskErrors({ ...newTaskErrors, endDate: '' });
                      }}
                      className={newTaskErrors.endDate ? 'claude-input-error' : ''}
                    />
                    {newTaskErrors.endDate && (
                      <div className="claude-error-text">{newTaskErrors.endDate}</div>
                    )}
                  </div>
                </div>

                <div className="claude-form-group">
                  <div>{t('task.detailLabel')}</div>
                  <textarea
                    value={newTaskData.description}
                    onChange={(e) =>
                      setNewTaskData({ ...newTaskData, description: e.target.value })
                    }
                    placeholder={t('task.detailPlaceholder')}
                    rows={3}
                    autoComplete="off"
                    name={`create-task-description-${createTaskCategoryId ?? 'new'}`}
                  />
                </div>

                <div className="claude-form-group claude-form-group--create-task-submit-gap">
                  <div>{t('task.checklistLabel')}</div>
                  <div className="claude-checklist-item-wrapper claude-flex-gap-4 claude-margin-bottom-8">
                    <input
                      type="text"
                      value={newChecklistInput}
                      onChange={(e) => setNewChecklistInput(e.target.value)}
                      placeholder={t('task.checklistPlaceholder')}
                      className="claude-input-checklist"
                      onKeyPress={(e) => e.key === 'Enter' && handleAddChecklistInPopup()}
                    />
                    <button
                      type="button"
                      onClick={handleAddChecklistInPopup}
                      disabled={!newChecklistInput.trim()}
                      className="claude-btn-primary claude-btn-inline-md"
                    >
                      {t('common.add')}
                    </button>
                  </div>
                  <div className="claude-checklist-preview">
                    {newTaskData.checklist.map((item) => (
                      <div key={item.id} className="claude-flex-align-center-margin-bottom">
                        {editingChecklistId === item.id ? (
                          <div className="claude-flex-1 claude-flex-gap-4">
                            <input
                              type="text"
                              value={editingChecklistText}
                              onChange={(e) => setEditingChecklistText(e.target.value)}
                              className="claude-input-inline"
                            />
                            <button
                              type="button"
                              onClick={handleUpdateChecklistInPopup}
                              disabled={!editingChecklistText.trim()}
                              className="claude-btn-primary claude-btn-inline-sm"
                            >
                              {t('common.save')}
                            </button>
                            <button
                              onClick={() => setEditingChecklistId(null)}
                              className="claude-btn-outline claude-btn-inline-sm"
                            >
                              {t('common.cancel')}
                            </button>
                          </div>
                        ) : (
                          <>
                            <span className="claude-flex-1 claude-text-inline">✓ {item.text}</span>
                            {(activeProject.owner_id === user?.id ||
                              activeProject.members?.find((m) => m.id === user?.id)?.role ===
                                '관리자' ||
                              item.createdBy === user?.id) && (
                              <div className="claude-flex-align-center">
                                <button
                                  className="claude-circle-btn claude-btn-edit-circle"
                                  onClick={() => {
                                    setEditingChecklistId(item.id);
                                    setEditingChecklistText(item.text);
                                  }}
                                >
                                  ✎
                                </button>
                                <button
                                  className="claude-circle-btn claude-btn-delete-circle"
                                  onClick={() => handleDeleteChecklistInPopup(item.id)}
                                >
                                  ×
                                </button>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                <button className="claude-btn-primary claude-full" onClick={handleSubmitNewTask}>
                  {t('task.create')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 프로젝트 생성 팝업 */}
        {showCreatePopup && (
          <div
            className="claude-popup-overlay"
            onClick={() => {
              if (isCreatingProject) return;
              setShowCreatePopup(false);
            }}
          >
            <div className="claude-popup-modal" onClick={(e) => e.stopPropagation()}>
              <button className="claude-popup-close" onClick={() => setShowCreatePopup(false)}>
                ×
              </button>
              <h2>{t('create.title')}</h2>
              <div className="claude-form-group">
                <div>
                  {t('project.nameLabel')} <span className="claude-color-required">*</span>
                </div>
                <input
                  type="text"
                  placeholder={t('create.namePlaceholder')}
                  value={formData.name}
                  className={formErrors.name ? 'claude-input-error' : ''}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    if (formErrors.name) setFormErrors({ ...formErrors, name: '' });
                  }}
                />
                {formErrors.name && <div className="claude-error-text">{formErrors.name}</div>}
              </div>
              <div className="claude-form-group">
                <div>{t('create.overviewLabel')}</div>
                <textarea
                  placeholder={t('create.overviewPlaceholder')}
                  value={formData.description}
                  className={formErrors.description ? 'claude-input-error' : ''}
                  onChange={(e) => {
                    setFormData({ ...formData, description: e.target.value });
                    if (formErrors.description) setFormErrors({ ...formErrors, description: '' });
                  }}
                />
                {formErrors.description && (
                  <div className="claude-error-text">{formErrors.description}</div>
                )}
              </div>
              <div className="claude-form-row claude-form-row--keep-cols">
                <div className="claude-form-group">
                  <div>{t('create.startDate')}</div>
                  <input
                    type="date"
                    value={formData.startDate}
                    className={formErrors.startDate ? 'claude-input-error' : ''}
                    onChange={(e) => {
                      setFormData({ ...formData, startDate: e.target.value });
                      if (formErrors.startDate) setFormErrors({ ...formErrors, startDate: '' });
                    }}
                  />
                  {formErrors.startDate && (
                    <div className="claude-error-text">{formErrors.startDate}</div>
                  )}
                </div>
                <div className="claude-form-group">
                  <div>{t('create.endDate')}</div>
                  <input
                    type="date"
                    value={formData.endDate}
                    className={formErrors.endDate ? 'claude-input-error' : ''}
                    onChange={(e) => {
                      setFormData({ ...formData, endDate: e.target.value });
                      if (formErrors.endDate) setFormErrors({ ...formErrors, endDate: '' });
                    }}
                  />
                  {formErrors.endDate && (
                    <div className="claude-error-text">{formErrors.endDate}</div>
                  )}
                </div>
              </div>
              <button
                className="claude-btn-primary claude-full"
                onClick={handleValidateProject}
                disabled={isValidating || isCreatingProject || isCreatingBlank}
              >
                {isValidating ? t('create.validating') : t('create.aiCreate')}
              </button>
              <button
                type="button"
                className="claude-btn-link claude-btn-link-underlined"
                onClick={handleCreateBlankProject}
                disabled={isCreatingProject || isValidating || isCreatingBlank}
              >
                {isCreatingBlank ? t('create.creating') : t('create.blankCreate')}
              </button>
              {isValidating && (
                <div className="claude-loading-overlay">
                  <div className="claude-loading-content">
                    <div className="claude-loading-spinner"></div>
                    <p className="claude-loading-message">
                      <span className="claude-loading-icon">🔍</span>
                      <span className="claude-loading-text">
                        {t('create.reviewing')}
                      </span>
                    </p>
                  </div>
                </div>
              )}
              {isCreatingProject && (
                <div className="claude-loading-overlay">
                  <div className="claude-loading-content">
                    <div className="claude-loading-spinner"></div>
                    <p className="claude-loading-message">
                      <span className="claude-loading-icon">{aiProgress?.icon || '🤖'}</span>
                      <span className="claude-loading-text">
                        {aiProgress?.text || t('project.aiScheduling')}
                      </span>
                    </p>
                    <p className="claude-loading-notice">{t('create.loadingNotice')}</p>
                    <button
                      className="claude-loading-cancel"
                      onClick={async () => {
                        // 1. Dify 서버에 스트리밍 중지 요청
                        if (createProjectTaskIdRef.current) {
                          await stopChatMessage(createProjectTaskIdRef.current, user.id);
                          createProjectTaskIdRef.current = null;
                        }
                        // 2. 클라이언트 측 fetch 중단
                        if (createProjectAbortRef.current) {
                          createProjectAbortRef.current.abort();
                          createProjectAbortRef.current = null;
                        }
                        setIsCreatingProject(false);
                        setAiProgress({ text: '', icon: '🤖' });
                      }}
                    >
                      {t('create.cancelCreate')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 템플릿으로 내놓기 */}
        {showTemplatePublishModal && (
          <div className="claude-popup-overlay" onClick={closeTemplatePublishModal}>
            <div
              className="claude-popup-modal claude-template-publish-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="claude-popup-close"
                onClick={closeTemplatePublishModal}
                aria-label={t('common.close')}
              >
                ×
              </button>
              <h2>{t('template.publishTitle')}</h2>
              <p className="claude-template-publish-lead">{t('template.publishLead')}</p>

              <div className="claude-form-group">
                <div htmlFor="template-publish-category">
                  {t('template.categoryLabel')} <span className="claude-color-required">*</span>
                </div>
                <select
                  id="template-publish-category"
                  className={`claude-template-publish-select${templatePublishCategoryError ? ' claude-input-error' : ''}`}
                  value={templatePublishCategoryId}
                  onChange={(e) => {
                    setTemplatePublishCategoryId(e.target.value);
                    if (templatePublishCategoryError) setTemplatePublishCategoryError('');
                  }}
                  disabled={templateCategoriesLoading}
                  aria-invalid={templatePublishCategoryError ? 'true' : 'false'}
                  aria-describedby={
                    templatePublishCategoryError ? 'template-publish-category-error' : undefined
                  }
                >
                  <option value="">
                    {templateCategoriesLoading ? t('template.categoryLoading') : t('template.selectCategory')}
                  </option>
                  {templateCategorySelectOptions.map((row, idx) => (
                    <option key={`${row.id}-${idx}`} value={row.id}>
                      {row.name}
                    </option>
                  ))}
                </select>
                {templatePublishCategoryError && (
                  <div id="template-publish-category-error" className="claude-error-text">
                    {templatePublishCategoryError}
                  </div>
                )}
              </div>

              <div className="claude-form-group">
                <div htmlFor="template-publish-description">
                  {t('template.descLabel')} <span className="claude-color-required">*</span>
                </div>
                <textarea
                  id="template-publish-description"
                  placeholder={t('template.descPlaceholder')}
                  value={templatePublishDescription}
                  onChange={(e) => {
                    setTemplatePublishDescription(e.target.value);
                    if (templatePublishDescriptionError) setTemplatePublishDescriptionError('');
                  }}
                  rows={4}
                  className={`claude-template-publish-textarea${templatePublishDescriptionError ? ' claude-input-error' : ''}`}
                  aria-invalid={templatePublishDescriptionError ? 'true' : 'false'}
                  aria-describedby={
                    templatePublishDescriptionError
                      ? 'template-publish-description-error'
                      : undefined
                  }
                />
                {templatePublishDescriptionError && (
                  <div id="template-publish-description-error" className="claude-error-text">
                    {templatePublishDescriptionError}
                  </div>
                )}
              </div>

              <div className="claude-form-note claude-form-note--muted" role="note">
                <p className="claude-challenge-caution-title">
                  <strong>{t('template.cautionTitle')}</strong>
                </p>
                <ul className="claude-challenge-caution-list">
                  <li>{t('template.publishNote1')}</li>
                  <li>{t('template.publishNote2')}</li>
                </ul>
              </div>

              <div className="claude-template-publish-actions">
                <button
                  type="button"
                  className="claude-btn-outline"
                  onClick={closeTemplatePublishModal}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="claude-btn-primary"
                  onClick={handleTemplatePublishSubmit}
                  disabled={templateCategoriesLoading || templatePublishSubmitting}
                >
                  {templatePublishSubmitting ? t('template.publishing') : t('template.publish')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 챌린지로 내놓기 */}
        {showChallengePublishModal && (
          <div className="claude-popup-overlay" onClick={closeChallengePublishModal}>
            <div
              className="claude-popup-modal claude-template-publish-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="claude-popup-close"
                onClick={closeChallengePublishModal}
                aria-label={t('common.close')}
              >
                ×
              </button>
              <h2>{challengeEditMode ? t('challenge.editTitle') : t('challenge.publishTitle')}</h2>
              <p className="claude-template-publish-lead">{t('challenge.publishLead')}</p>

              <div className="claude-form-group">
                <div>
                  {t('challenge.categoryLabel')} <span className="claude-color-required">*</span>
                </div>
                <select
                  className={`claude-template-publish-select${challengePublishCategoryError ? ' claude-input-error' : ''}`}
                  value={challengePublishCategoryId}
                  onChange={(e) => {
                    setChallengePublishCategoryId(e.target.value);
                    if (challengePublishCategoryError) setChallengePublishCategoryError('');
                  }}
                  disabled={challengeCategoriesLoading}
                >
                  <option value="">
                    {challengeCategoriesLoading
                      ? t('template.categoryLoading')
                      : t('template.selectCategory')}
                  </option>
                  {challengeCategoriesList.map((row, idx) => (
                    <option key={`${row.id}-${idx}`} value={row.id}>
                      {row.name}
                    </option>
                  ))}
                </select>
                {challengePublishCategoryError && (
                  <div className="claude-error-text">{challengePublishCategoryError}</div>
                )}
              </div>

              <div className="claude-form-group">
                <div>
                  {t('challenge.descLabel')} <span className="claude-color-required">*</span>
                </div>
                <textarea
                  placeholder={t('challenge.descPlaceholder')}
                  value={challengePublishDescription}
                  onChange={(e) => {
                    setChallengePublishDescription(e.target.value);
                    if (challengePublishDescriptionError) setChallengePublishDescriptionError('');
                  }}
                  rows={4}
                  className={`claude-template-publish-textarea${challengePublishDescriptionError ? ' claude-input-error' : ''}`}
                />
                {challengePublishDescriptionError && (
                  <div className="claude-error-text">{challengePublishDescriptionError}</div>
                )}
              </div>

              <div className="claude-form-group">
                <div>
                  {t('challenge.priceLabel')} <span className="claude-color-required">*</span>
                </div>
                <div className="claude-challenge-price-field">
                  <input
                    type="number"
                    inputMode="numeric"
                    min={1000}
                    step={1}
                    placeholder={t('challenge.pricePlaceholder')}
                    value={challengePublishPrice}
                    onChange={(e) => {
                      setChallengePublishPrice(e.target.value);
                      if (challengePublishPriceError) setChallengePublishPriceError('');
                    }}
                    className={`claude-challenge-price-input${challengePublishPriceError ? ' claude-input-error' : ''}`}
                  />
                  <span className="claude-challenge-price-unit">{t('challenge.priceUnit')}</span>
                </div>
                {challengePublishPriceError && (
                  <div className="claude-error-text">{challengePublishPriceError}</div>
                )}
              </div>

              <div className="claude-form-note claude-form-note--muted" role="note">
                <p className="claude-challenge-caution-title">
                  <strong>{t('challenge.cautionTitle')}</strong>
                </p>
                <ul className="claude-challenge-caution-list">
                  {(t('challenge.cautionItems', { returnObjects: true }) || []).map((line, i) => (
                    <li key={i}>{line}</li>
                  ))}
                </ul>
              </div>

              <div className="claude-template-publish-actions">
                <button
                  type="button"
                  className="claude-btn-outline"
                  onClick={closeChallengePublishModal}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="claude-btn-primary"
                  onClick={handleChallengePublishSubmit}
                  disabled={challengeCategoriesLoading || challengePublishSubmitting}
                >
                  {challengePublishSubmitting
                    ? t('challenge.publishing')
                    : challengeEditMode
                      ? t('challenge.editSave')
                      : t('challenge.publish')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 템플릿 정보 수정 */}
        {showTemplateEditModal && (
          <div className="claude-popup-overlay" onClick={closeTemplateEditModal}>
            <div
              className="claude-popup-modal claude-template-publish-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                className="claude-popup-close"
                onClick={closeTemplateEditModal}
                aria-label={t('common.close')}
              >
                ×
              </button>
              <h2>{t('template.editTitle')}</h2>

              <div className="claude-form-group">
                <div htmlFor="template-edit-category">
                  {t('template.categoryLabel')} <span className="claude-color-required">*</span>
                </div>
                <select
                  id="template-edit-category"
                  className={`claude-template-publish-select${templateEditCategoryError ? ' claude-input-error' : ''}`}
                  value={templateEditCategoryId}
                  onChange={(e) => {
                    setTemplateEditCategoryId(e.target.value);
                    if (templateEditCategoryError) setTemplateEditCategoryError('');
                  }}
                  disabled={templateCategoriesLoading}
                  aria-invalid={templateEditCategoryError ? 'true' : 'false'}
                  aria-describedby={
                    templateEditCategoryError ? 'template-edit-category-error' : undefined
                  }
                >
                  <option value="">
                    {templateCategoriesLoading ? t('template.categoryLoading') : t('template.selectCategory')}
                  </option>
                  {templateCategorySelectOptions.map((row, idx) => (
                    <option key={`edit-${row.id}-${idx}`} value={row.id}>
                      {row.name}
                    </option>
                  ))}
                </select>
                {templateEditCategoryError && (
                  <div id="template-edit-category-error" className="claude-error-text">
                    {templateEditCategoryError}
                  </div>
                )}
              </div>

              <div className="claude-form-group">
                <div htmlFor="template-edit-description">
                  {t('template.descLabel')} <span className="claude-color-required">*</span>
                </div>
                <textarea
                  id="template-edit-description"
                  placeholder={t('template.descPlaceholderEdit')}
                  value={templateEditDescription}
                  onChange={(e) => {
                    setTemplateEditDescription(e.target.value);
                    if (templateEditDescriptionError) setTemplateEditDescriptionError('');
                  }}
                  rows={4}
                  className={`claude-template-publish-textarea${templateEditDescriptionError ? ' claude-input-error' : ''}`}
                  aria-invalid={templateEditDescriptionError ? 'true' : 'false'}
                  aria-describedby={
                    templateEditDescriptionError ? 'template-edit-description-error' : undefined
                  }
                />
                {templateEditDescriptionError && (
                  <div id="template-edit-description-error" className="claude-error-text">
                    {templateEditDescriptionError}
                  </div>
                )}
              </div>

              <div className="claude-template-publish-actions">
                <button
                  type="button"
                  className="claude-btn-outline"
                  onClick={closeTemplateEditModal}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  className="claude-btn-primary"
                  onClick={handleTemplateEditSubmit}
                  disabled={templateCategoriesLoading || templateEditSubmitting}
                >
                  {templateEditSubmitting ? t('template.editSaving') : t('common.save')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 프로젝트 입력값 검증 경고 팝업 */}
        {showValidationWarning && (
          <div className="claude-popup-overlay" onClick={handleModifyInput}>
            <div
              className="claude-popup-modal claude-validation-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="claude-popup-close" onClick={handleModifyInput}>
                ×
              </button>
              <div className="claude-validation-header">
                <span className="claude-validation-icon">⚠️</span>
                <h2 className="claude-validation-title">{t('validation.title')}</h2>
              </div>
              <div className="claude-validation-content">
                {validationResult.warnings.length > 0 && (
                  <ul className="claude-validation-warnings">
                    {validationResult.warnings.map((warning, idx) => (
                      <li key={idx} className="claude-validation-warning-item">
                        {warning}
                      </li>
                    ))}
                  </ul>
                )}
                {validationResult.suggestions && (
                  <p className="claude-validation-suggestion">{validationResult.suggestions}</p>
                )}
              </div>
              <div className="claude-validation-actions">
                <button className="claude-btn-outline" onClick={handleModifyInput}>
                  {t('validation.editInput')}
                </button>
                <button className="claude-btn-primary" onClick={handleProceedAnyway}>
                  {t('validation.proceed')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 구독 플랜 선택 팝업 */}
        {showPricingPopup && (
          <div className="claude-popup-overlay" onClick={() => setShowPricingPopup(false)}>
            <div
              className="claude-popup-modal claude-pricing-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="claude-popup-close" onClick={() => setShowPricingPopup(false)}>
                ×
              </button>
              <div className="claude-pricing-header">
                <h2 className="claude-pricing-title">{t('pricing.title')}</h2>
                <p className="claude-pricing-subtitle">{t('pricing.subtitle')}</p>
              </div>
              <div className="claude-pricing-plans">
                <div className="claude-pricing-plan">
                  <div className="claude-plan-header">
                    <h3 className="claude-plan-name">{t('pricing.monthlyName')}</h3>
                    <div className="claude-plan-price">
                      <span className="claude-price-amount">₩12,000</span>
                      <span className="claude-price-period">{t('pricing.perMonth')}</span>
                    </div>
                    {/* <span className="claude-price-tax">+ Tax (국가별 상이)</span> */}
                  </div>
                  <ul className="claude-plan-features">
                    <li>{t('pricing.featProject')}</li>
                    <li>{t('pricing.featScheduling')}</li>
                    <li>{t('pricing.featDashboard')}</li>
                    <li>{t('pricing.featInvite')}</li>
                  </ul>
                  <button
                    className="claude-btn-outline claude-plan-btn"
                    onClick={() => handleCheckout('monthly')}
                  >
                    {t('pricing.monthlyCta')}
                  </button>
                </div>
                {SHOW_YEARLY && (
                  <div className="claude-pricing-plan claude-plan-recommended">
                    <div className="claude-plan-badge">{t('pricing.recommended')}</div>
                    <div className="claude-plan-header">
                      <h3 className="claude-plan-name">{t('pricing.yearlyName')}</h3>
                      <div className="claude-plan-price">
                        <span className="claude-price-amount">₩9,500</span>
                        <span className="claude-price-period">{t('pricing.perMonth')}</span>
                      </div>
                      {/* <span className="claude-price-tax">+ Tax (국가별 상이)</span> */}
                      <div className="claude-plan-savings">{t('pricing.yearlySavings')}</div>
                    </div>
                    <ul className="claude-plan-features">
                      <li>{t('pricing.featProject')}</li>
                      <li>{t('pricing.featScheduling')}</li>
                      <li>{t('pricing.featDashboard')}</li>
                      <li>{t('pricing.featInvite')}</li>
                    </ul>
                    <button
                      className="claude-btn-primary claude-plan-btn"
                      onClick={() => handleCheckout('yearly')}
                    >
                      {t('pricing.yearlyCta')}
                    </button>
                  </div>
                )}
              </div>
              <p className="claude-pricing-note">{t('pricing.note')}</p>
            </div>
          </div>
        )}

        {/* 작업 상세 팝업 */}
        {showTaskPopup && selectedTask && selectedCategory && (
          <div
            className="claude-popup-overlay claude-task-modal-overlay"
            onClick={() => setShowTaskPopup(false)}
          >
            <div
              className="claude-popup-modal claude-task-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="claude-popup-close" onClick={() => setShowTaskPopup(false)}>
                ×
              </button>
              <div className="claude-task-modal-fixed">
                <div className="claude-task-header">
                  {editingTaskField === 'name' ? (
                    <div className="claude-task-header-center">
                      <input
                        type="text"
                        value={editingTaskValue}
                        onChange={(e) => setEditingTaskValue(e.target.value)}
                        className="claude-task-input-title"
                      />
                      <button
                        type="button"
                        onClick={handleSaveTaskField}
                        disabled={!editingTaskValue.trim()}
                        className="claude-btn-primary claude-btn-inline-md"
                      >
                        {t('common.save')}
                      </button>
                      <button
                        onClick={() => setEditingTaskField(null)}
                        className="claude-btn-outline claude-btn-inline-md"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  ) : (
                    <div className="claude-task-header-center">
                      <h2 className="claude-margin-0">{selectedTask.name}</h2>
                      <button
                        onClick={() => handleStartEditTaskField('name', selectedTask.name)}
                        className="claude-circle-btn claude-btn-edit-circle"
                      >
                        ✎
                      </button>
                    </div>
                  )}
                </div>
              </div>

              <div className="claude-task-modal-body-scroll">
                {editingTaskField === 'dates' ? (
                  <div className="claude-task-dates-edit-wrapper">
                    <div className="claude-task-dates-inputs">
                      <input
                        type="date"
                        value={
                          editingTaskValue.startDate
                            ? new Date(editingTaskValue.startDate).toISOString().split('T')[0]
                            : ''
                        }
                        onChange={(e) =>
                          setEditingTaskValue((prev) => {
                            const nextStartDate = e.target.value;
                            if (!prev?.startDate || !prev?.endDate || !nextStartDate) {
                              return { ...prev, startDate: nextStartDate };
                            }

                            const parseDateInput = (value) => {
                              const [year, month, day] = value.split('-').map(Number);
                              return new Date(year, month - 1, day);
                            };
                            const formatDateInput = (date) => {
                              const y = date.getFullYear();
                              const m = String(date.getMonth() + 1).padStart(2, '0');
                              const d = String(date.getDate()).padStart(2, '0');
                              return `${y}-${m}-${d}`;
                            };

                            const prevStart = parseDateInput(
                              new Date(prev.startDate).toISOString().split('T')[0]
                            );
                            const prevEnd = parseDateInput(
                              new Date(prev.endDate).toISOString().split('T')[0]
                            );
                            const newStart = parseDateInput(nextStartDate);
                            const diffDays = Math.round((prevEnd - prevStart) / DAY_MS);
                            const nextEnd = new Date(newStart);
                            nextEnd.setDate(newStart.getDate() + diffDays);

                            return {
                              ...prev,
                              startDate: nextStartDate,
                              endDate: formatDateInput(nextEnd),
                            };
                          })
                        }
                        className="claude-input-date"
                      />
                      <span className="claude-date-separator">~</span>
                      <input
                        type="date"
                        value={
                          editingTaskValue.endDate
                            ? new Date(editingTaskValue.endDate).toISOString().split('T')[0]
                            : ''
                        }
                        onChange={(e) =>
                          setEditingTaskValue({ ...editingTaskValue, endDate: e.target.value })
                        }
                        className="claude-input-date"
                      />
                    </div>
                    <div className="claude-btn-actions-right">
                      <button
                        onClick={handleSaveTaskField}
                        className="claude-btn-primary claude-btn-sm"
                      >
                        {t('common.save')}
                      </button>
                      <button
                        onClick={() => setEditingTaskField(null)}
                        className="claude-btn-outline claude-btn-sm"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="claude-task-dates">
                    <span>{new Date(selectedTask.startDate).toLocaleDateString('ko-KR')}</span>
                    <span> ~ </span>
                    <span>{new Date(selectedTask.endDate).toLocaleDateString('ko-KR')}</span>
                    <button
                      onClick={() =>
                        handleStartEditTaskField('dates', {
                          startDate: selectedTask.startDate,
                          endDate: selectedTask.endDate,
                        })
                      }
                      className="claude-circle-btn claude-btn-edit-circle claude-ml-4"
                    >
                      ✎
                    </button>
                  </div>
                )}
                <div className="claude-task-detail-section">
                  <div className="claude-task-checklist-header-center">
                    <h3 className="claude-margin-0">{t('task.detailLabel')}</h3>
                    {editingTaskField !== 'detail' && (
                      <button
                        onClick={() =>
                          handleStartEditTaskField('detail', selectedTask.description || '')
                        }
                        className="claude-circle-btn claude-btn-edit-circle"
                      >
                        ✎
                      </button>
                    )}
                  </div>
                  {editingTaskField === 'detail' ? (
                    <div className="claude-task-detail-edit-wrap">
                      <textarea
                        value={editingTaskValue}
                        onChange={(e) => setEditingTaskValue(e.target.value)}
                        className="claude-textarea-edit"
                      />
                      <div className="claude-btn-actions-right">
                        <button
                          onClick={handleSaveTaskField}
                          className="claude-btn-primary claude-btn-sm"
                        >
                          {t('common.save')}
                        </button>
                        <button
                          onClick={() => setEditingTaskField(null)}
                          className="claude-btn-outline claude-btn-sm"
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="claude-task-detail-text-card">
                      <p
                        ref={taskDetailTextRef}
                        className={`claude-task-detail-text${
                          selectedTaskDetail && !taskDetailExpanded ? ' collapsed' : ''
                        }`}
                      >
                        {selectedTaskDetail
                          ? renderTextWithLinks(selectedTaskDetail)
                          : t('task.detailEmpty')}
                      </p>
                      {selectedTaskDetail && taskDetailNeedsToggle && (
                        <button
                          type="button"
                          className="claude-detail-toggle-btn claude-detail-toggle-btn--in-card"
                          onClick={() => setTaskDetailExpanded((v) => !v)}
                        >
                          {taskDetailExpanded ? t('common.collapse') : t('common.expand')}
                          <HiOutlineChevronDown
                            className={`claude-chevron-toggle${taskDetailExpanded ? ' is-open' : ''}`}
                            aria-hidden
                          />
                        </button>
                      )}
                    </div>
                  )}
                </div>

                <div className="claude-task-modal-tabs-wrap">
                  <div className="claude-task-modal-tabs" role="tablist" aria-label={t('task.detailTab')}>
                    <button
                      type="button"
                      role="tab"
                      id="task-modal-tab-checklist"
                      aria-controls="task-modal-panel-checklist"
                      aria-selected={taskModalTab === 'checklist'}
                      className={`claude-task-modal-tab${taskModalTab === 'checklist' ? ' is-active' : ''}`}
                      onClick={() => handleSelectTaskModalTab('checklist')}
                    >
                      {t('taskDetail.checklistTab')}
                      {(taskUnreadById[selectedTask.id]?.checklistCount || 0) > 0 && (
                        <span
                          className="claude-task-modal-tab-badge"
                          aria-label={t('taskDetail.checklistUnread', { n: taskUnreadById[selectedTask.id].checklistCount })}
                        >
                          {taskUnreadById[selectedTask.id].checklistCount > 99
                            ? '99+'
                            : taskUnreadById[selectedTask.id].checklistCount}
                        </span>
                      )}
                    </button>
                    <button
                      type="button"
                      role="tab"
                      id="task-modal-tab-posts"
                      aria-controls="task-modal-panel-posts"
                      aria-selected={taskModalTab === 'posts'}
                      className={`claude-task-modal-tab${taskModalTab === 'posts' ? ' is-active' : ''}`}
                      onClick={() => handleSelectTaskModalTab('posts')}
                    >
                      {t('taskDetail.postsTab')}
                      {(taskUnreadById[selectedTask.id]?.postCount || 0) > 0 && (
                        <span
                          className="claude-task-modal-tab-badge"
                          aria-label={t('taskDetail.postsUnread', { n: taskUnreadById[selectedTask.id].postCount })}
                        >
                          {taskUnreadById[selectedTask.id].postCount > 99
                            ? '99+'
                            : taskUnreadById[selectedTask.id].postCount}
                        </span>
                      )}
                    </button>
                  </div>

                  <div className="claude-task-modal-tab-panels-shell">
                    <div
                      id="task-modal-panel-checklist"
                      role="tabpanel"
                      aria-labelledby="task-modal-tab-checklist"
                      hidden={taskModalTab !== 'checklist'}
                      className="claude-task-modal-tab-panel"
                    >
                      <div className="claude-task-checklist-section">
                        <div className="claude-task-progress-section claude-task-progress-section--modal-tab">
                          <div className="claude-progress-header">
                            <span>{t('panel.progress')}</span>
                            <span>{selectedTask.progress}%</span>
                          </div>
                          <div className="claude-progress-bar-container">
                            <div
                              className="claude-progress-bar-fill"
                              style={{ width: `${selectedTask.progress}%` }}
                            ></div>
                          </div>
                        </div>
                        <div
                          className={`claude-checklist-header${
                            taskModalAiGuideButtonCount === 1
                              ? ' claude-checklist-header--ai-guide-single'
                              : ''
                          }${
                            taskModalAiGuideButtonCount >= 2
                              ? ' claude-checklist-header--ai-guide-double'
                              : ''
                          }`}
                        >
                          <h3 className="claude-margin-0">
                            {t('checklist.count', {
                              done: selectedTaskCompletedChecklistCount,
                              total: selectedTaskTotalChecklistCount,
                            })}
                          </h3>
                          {selectedTask.checklist.length > 0 &&
                            (() => {
                              const hasHints = selectedTask.checklist.some((item) => item.hint);
                              const hasMissingHints = selectedTask.checklist.some(
                                (item) => !item.hint
                              );
                              const canManageAiGuide = canUserManageProjectAi(activeProject, user);
                              return (
                                <div className="claude-ai-guide-actions">
                                  {hasMissingHints && (
                                    <span className="claude-ai-guide-btn-tooltip-host">
                                      <button
                                        type="button"
                                        className={`claude-ai-guide-btn generate${generatingHints ? ' generating' : ''}`}
                                        onClick={handleGenerateHints}
                                        disabled={generatingHints || !canManageAiGuide}
                                      >
                                        {generatingHints ? (
                                          <>
                                            <span className="claude-ai-guide-spinner"></span>
                                            <span className="claude-ai-guide-label">
                                              {t('aiGuide.generatingInline')}
                                            </span>
                                          </>
                                        ) : (
                                          <>
                                            <span className="claude-ai-guide-label">
                                              {t('aiGuide.generate')}
                                            </span>
                                          </>
                                        )}
                                      </button>
                                      {!canManageAiGuide && (
                                        <span
                                          className="claude-disabled-control-hit-layer claude-disabled-control-hit-layer--pill"
                                          data-tooltip={t('common.adminOnly')}
                                          aria-hidden="true"
                                        />
                                      )}
                                    </span>
                                  )}
                                  {hasHints && (
                                    <button
                                      type="button"
                                      className={`claude-ai-guide-btn${showAiGuide ? ' active' : ''}`}
                                      onClick={toggleAiGuide}
                                    >
                                      <span className="claude-ai-guide-label">{t('aiGuide.label')}</span>
                                      <span className="claude-ai-guide-toggle">
                                        <span className="claude-ai-guide-toggle-track">
                                          <span className="claude-ai-guide-toggle-thumb"></span>
                                        </span>
                                      </span>
                                    </button>
                                  )}
                                </div>
                              );
                            })()}
                        </div>
                        {generatingHints && (
                          <div className="claude-ai-guide-progress">
                            <div className="claude-ai-guide-progress-bar"></div>
                            <p className="claude-ai-guide-progress-text">
                              {t('aiGuide.generatingAll')}
                            </p>
                          </div>
                        )}
                        {(() => {
                          const isAdmin = canUserManageProjectAi(activeProject, user);
                          const canAddChecklist =
                            isAdmin || !activeProject?.admin_only_add_checklist;
                          return (
                            <div className="claude-add-checklist-form claude-margin-bottom-8 claude-flex-gap-4">
                              <input
                                type="text"
                                value={newChecklistText}
                                onChange={(e) => setNewChecklistText(e.target.value)}
                                placeholder={
                                  canAddChecklist ? t('task.checklistPlaceholder') : t('checklist.adminOnlyInput')
                                }
                                className="claude-input-checklist"
                                disabled={!canAddChecklist}
                                onKeyPress={(e) =>
                                  canAddChecklist &&
                                  e.key === 'Enter' &&
                                  handleAddChecklist(selectedCategory.id, selectedTask.id)
                                }
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  if (!canAddChecklist) return;
                                  handleAddChecklist(selectedCategory.id, selectedTask.id);
                                }}
                                disabled={!canAddChecklist || !newChecklistText.trim()}
                                className="claude-btn-primary claude-btn-inline-md"
                              >
                                {t('common.add')}
                              </button>
                            </div>
                          );
                        })()}
                        {[...selectedTask.checklist]
                          .sort((a, b) => {
                            const ta = a.createdAt != null ? new Date(a.createdAt).getTime() : 0;
                            const tb = b.createdAt != null ? new Date(b.createdAt).getTime() : 0;
                            if (tb !== ta) return tb - ta;
                            return String(b.id).localeCompare(String(a.id));
                          })
                          .map((item) => (
                            <div
                              key={item.id}
                              className={`claude-checklist-item-container claude-margin-bottom-8${generatingHints && !item.hint ? ' claude-generating-shimmer' : ''}`}
                            >
                              {editingChecklistId === item.id ? (
                                <div className="claude-checklist-item-wrapper claude-flex-1 claude-flex-gap-4">
                                  <input
                                    type="text"
                                    value={editingChecklistText}
                                    onChange={(e) => setEditingChecklistText(e.target.value)}
                                    className="claude-input-checklist"
                                    onKeyDown={(e) =>
                                      e.key === 'Enter' &&
                                      handleUpdateChecklist(selectedCategory.id, selectedTask.id)
                                    }
                                  />
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handleUpdateChecklist(selectedCategory.id, selectedTask.id)
                                    }
                                    disabled={!editingChecklistText.trim()}
                                    className="claude-btn-primary claude-btn-inline-md"
                                  >
                                    {t('common.save')}
                                  </button>
                                  <button
                                    onClick={() => setEditingChecklistId(null)}
                                    className="claude-btn-outline claude-btn-inline-md"
                                  >
                                    {t('common.cancel')}
                                  </button>
                                </div>
                              ) : (
                                <>
                                  <div className="claude-checklist-item">
                                    <div className="claude-flex-align-center" style={{ flex: 1 }}>
                                      <input
                                        type="checkbox"
                                        className="claude-checkbox-input"
                                        checked={item.completed}
                                        disabled={
                                          !canUserManageProjectAi(activeProject, user) &&
                                          !!activeProject?.admin_only_check_checklist
                                        }
                                        title={
                                          !canUserManageProjectAi(activeProject, user) &&
                                          activeProject?.admin_only_check_checklist
                                            ? t('common.adminOnly')
                                            : undefined
                                        }
                                        onChange={() => {
                                          if (
                                            !canUserManageProjectAi(activeProject, user) &&
                                            activeProject?.admin_only_check_checklist
                                          ) {
                                            return;
                                          }
                                          handleToggleChecklist(
                                            selectedCategory.id,
                                            selectedTask.id,
                                            item.id
                                          );
                                        }}
                                      />
                                      <span
                                        className={`${item.completed ? 'claude-completed' : ''} claude-ml-8`}
                                      >
                                        {renderTextWithLinks(item.text)}
                                      </span>
                                    </div>

                                    <div
                                      className="claude-checklist-actions claude-flex-align-center-gap-8"
                                      onClick={(e) => e.preventDefault()}
                                    >
                                      {(activeProject.owner_id === user?.id ||
                                        activeProject.members?.find((m) => m.id === user?.id)
                                          ?.role === '관리자' ||
                                        item.createdBy === user?.id) && (
                                        <div className="claude-flex-align-center">
                                          <button
                                            onClick={() => {
                                              setEditingChecklistId(item.id);
                                              setEditingChecklistText(item.text);
                                            }}
                                            className="claude-circle-btn claude-btn-edit-circle"
                                          >
                                            ✎
                                          </button>
                                          <button
                                            onClick={() =>
                                              handleDeleteChecklist(
                                                selectedCategory.id,
                                                selectedTask.id,
                                                item.id
                                              )
                                            }
                                            className="claude-circle-btn claude-btn-delete-circle"
                                          >
                                            ×
                                          </button>
                                        </div>
                                      )}
                                      {item.completedAt && item.completedBy && (
                                        <div className="claude-checklist-completion-info">
                                          <span className="claude-checklist-completed-by">
                                            <MemberAvatarButton
                                              id={item.completedBy.id}
                                              name={item.completedBy.name}
                                              avatar={item.completedBy.avatar}
                                              onOpen={setActivityMember}
                                            >
                                              <SafeAvatar
                                                src={item.completedBy.avatar}
                                                alt="Avatar"
                                                className="claude-avatar-circle-img"
                                                fallbackClassName="claude-avatar-circle"
                                              />
                                            </MemberAvatarButton>
                                          </span>
                                          <span className="claude-checklist-date">
                                            {formatShortDotDate(item.completedAt)}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  {item.hint && showAiGuide && (
                                    <div className="claude-checklist-hint">
                                      <div className="claude-markdown">
                                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                          {item.hint}
                                        </ReactMarkdown>
                                      </div>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>

                    <div
                      id="task-modal-panel-posts"
                      role="tabpanel"
                      aria-labelledby="task-modal-tab-posts"
                      hidden={taskModalTab !== 'posts'}
                      className="claude-task-modal-tab-panel"
                    >
                      <div className="claude-task-posts-section claude-task-posts-section--modal-tab">
                        <div className="claude-post-composer-wrap">
                          {newPostFiles.length > 0 && (
                            <div className="claude-chat-selected-files">
                              {newPostFiles.map((f) => (
                                <span
                                  key={f.id}
                                  className={`claude-attachment-chip claude-attachment-chip-sm${
                                    f.status === 'uploading' ? ' is-uploading' : ''
                                  }${f.status === 'error' ? ' is-error' : ''}`}
                                >
                                  <HiOutlinePaperClip aria-hidden /> {f.file.name}
                                  {f.status === 'uploading' && <span> · {f.progress}%</span>}
                                  {f.status === 'error' && <span>{t('common.failedSuffix')}</span>}
                                  <button
                                    type="button"
                                    className="claude-chip-remove"
                                    onClick={() =>
                                      removeAttachmentEntry(newPostFiles, f.id, setNewPostFiles)
                                    }
                                    title={t('common.removeFile')}
                                  >
                                    ×
                                  </button>
                                </span>
                              ))}
                            </div>
                          )}
                          <div className="claude-chat-composer">
                            <input
                              type="file"
                              multiple
                              ref={newPostFileInputRef}
                              onChange={(e) => {
                                const picked = capPickedFiles(
                                  newPostFiles.length,
                                  Array.from(e.target.files || [])
                                );
                                if (picked.length === 0) {
                                  e.target.value = '';
                                  return;
                                }
                                const entries = buildPendingEntries(picked);
                                setNewPostFiles((prev) => [...prev, ...entries]);
                                startUploadBatch({
                                  kind: 'post',
                                  projectId: activeProject?.id,
                                  taskId: selectedTask.id,
                                  items: entries,
                                  setItems: setNewPostFiles,
                                });
                                e.target.value = '';
                              }}
                              className="claude-display-none"
                              accept="image/*,video/*,application/pdf,application/zip,application/x-zip-compressed,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/*,.hwp,.hwpx,.hwt,.hpt"
                            />
                            <button
                              type="button"
                              className="claude-chat-attach-btn"
                              onClick={() => newPostFileInputRef.current?.click()}
                              aria-label={t('common.attachFile')}
                              title={t('common.attachFile')}
                            >
                              <HiOutlinePaperClip />
                            </button>
                            <textarea
                              className={`claude-chat-textarea${
                                newPostError ? ' claude-input-error' : ''
                              }`}
                              placeholder={t('post.placeholder')}
                              value={newPost}
                              onChange={(e) => {
                                setNewPost(e.target.value);
                                if (newPostError) setNewPostError('');
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey && !isMobile) {
                                  e.preventDefault();
                                  handleAddPost(selectedCategory.id, selectedTask.id);
                                }
                              }}
                              rows={1}
                            />
                            <button
                              type="button"
                              className="claude-chat-send"
                              onClick={() => handleAddPost(selectedCategory.id, selectedTask.id)}
                              disabled={
                                newPostFiles.some(
                                  (f) => f.status === 'pending' || f.status === 'uploading'
                                ) ||
                                (!newPost.trim() && !newPostFiles.some((f) => f.status === 'done'))
                              }
                              aria-label={t('post.sendLabel')}
                            >
                              <HiOutlinePaperAirplane />
                            </button>
                          </div>
                          {newPostError && (
                            <div className="claude-error-text claude-error-text-composer">
                              {newPostError}
                            </div>
                          )}
                        </div>
                        {selectedTask.posts.map((post) => (
                          <div key={post.id} className="claude-post-item">
                            {editingPost && editingPost.id === post.id ? (
                              <div className="claude-edit-post-box">
                                <textarea
                                  value={editingPost.content}
                                  onChange={(e) =>
                                    setEditingPost({ ...editingPost, content: e.target.value })
                                  }
                                  className="claude-textarea-edit-post"
                                />
                                {editingPost.attachments && editingPost.attachments.length > 0 && (
                                  <div className="claude-edit-files">
                                    {editingPost.attachments.map((file, fIdx) => (
                                      <span
                                        key={fIdx}
                                        className="claude-attachment-chip claude-bg-white"
                                      >
                                        <HiOutlinePaperClip aria-hidden /> {file.name}
                                        <button
                                          type="button"
                                          className="claude-circle-btn claude-btn-delete-circle claude-ml-6"
                                          onClick={() => {
                                            const newFiles = editingPost.attachments.filter(
                                              (_, idx) => idx !== fIdx
                                            );
                                            setEditingPost({
                                              ...editingPost,
                                              attachments: newFiles,
                                            });
                                          }}
                                        >
                                          ×
                                        </button>
                                      </span>
                                    ))}
                                  </div>
                                )}
                                <div className="claude-flex-gap-4 claude-justify-flex-end">
                                  <button
                                    type="button"
                                    className="claude-btn-save-text"
                                    onClick={() =>
                                      handleUpdatePost(selectedCategory.id, selectedTask.id)
                                    }
                                  >
                                    {t('common.save')}
                                  </button>
                                  <button
                                    type="button"
                                    className="claude-btn-cancel-text"
                                    onClick={() => setEditingPost(null)}
                                  >
                                    {t('common.cancel')}
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <>
                                <div className="claude-post-header">
                                  <MemberAvatarButton
                                    id={post.authorId}
                                    name={post.author}
                                    avatar={post.avatar}
                                    onOpen={setActivityMember}
                                  >
                                    <SafeAvatar
                                      src={post.avatar}
                                      alt="Avatar"
                                      className="claude-post-avatar-img"
                                      fallbackClassName="claude-post-avatar"
                                    />
                                  </MemberAvatarButton>
                                  <span className="claude-post-author">{post.author}</span>
                                  <div className="claude-margin-left-auto claude-flex-align-center-gap-8">
                                    {post.author === currentUser.name && (
                                      <div className="claude-flex-align-center">
                                        <button
                                          type="button"
                                          className="claude-circle-btn claude-btn-edit-circle"
                                          onClick={() => setEditingPost(post)}
                                          aria-label={t('post.edit')}
                                        >
                                          ✎
                                        </button>
                                        <button
                                          type="button"
                                          className="claude-circle-btn claude-btn-delete-circle"
                                          onClick={() =>
                                            handleDeletePost(
                                              selectedCategory.id,
                                              selectedTask.id,
                                              post.id
                                            )
                                          }
                                          aria-label={t('post.delete')}
                                        >
                                          ×
                                        </button>
                                      </div>
                                    )}
                                    {post.authorId !== user?.id &&
                                      canUserManageProjectAi(activeProject, user) && (
                                        <button
                                          type="button"
                                          className={`claude-circle-btn claude-btn-hide-circle${post.hiddenAt ? ' is-active' : ''}`}
                                          onClick={() =>
                                            handleToggleHidePost(
                                              selectedCategory.id,
                                              selectedTask.id,
                                              post.id
                                            )
                                          }
                                          aria-label={post.hiddenAt ? t('common.unhide') : t('post.hide')}
                                          title={
                                            post.hiddenAt ? t('common.unhide') : t('post.hideAdmin')
                                          }
                                        >
                                          {post.hiddenAt ? (
                                            <HiOutlineEye aria-hidden />
                                          ) : (
                                            <HiOutlineEyeSlash aria-hidden />
                                          )}
                                        </button>
                                      )}
                                    <span className="claude-post-date">
                                      {formatStackFeedDate(post.createdAt)}
                                    </span>
                                  </div>
                                </div>
                                {post.hiddenAt ? (
                                  canUserManageProjectAi(activeProject, user) ? (
                                    (() => {
                                      const key = `post:${post.id}`;
                                      const expanded = hiddenRevealed.has(key);
                                      return (
                                        <div className="claude-chat-hidden-block">
                                          <button
                                            type="button"
                                            className={`claude-chat-hidden-banner claude-chat-hidden-banner--toggle${expanded ? ' is-expanded' : ''}`}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              toggleHiddenRevealed(key);
                                            }}
                                            aria-expanded={expanded}
                                          >
                                            <span>{t('post.hiddenBadge')}</span>
                                            <HiOutlineChevronDown
                                              className="claude-chat-hidden-chevron"
                                              aria-hidden
                                            />
                                          </button>
                                          {expanded && (
                                            <div className="claude-chat-hidden-revealed">
                                              {post.content && (
                                                <p className="claude-post-content">
                                                  {renderTextWithLinks(post.content)}
                                                </p>
                                              )}
                                              {post.attachments && post.attachments.length > 0 && (
                                                <ChatAttachments
                                                  attachments={post.attachments}
                                                  onOpenLightbox={(items, idx) =>
                                                    setChatLightbox({ items, index: idx })
                                                  }
                                                />
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      );
                                    })()
                                  ) : (
                                    <p className="claude-post-content claude-chat-hidden-placeholder">
                                      {t('post.hiddenNotice')}
                                    </p>
                                  )
                                ) : (
                                  <>
                                    {post.content && (
                                      <p className="claude-post-content">
                                        {renderTextWithLinks(post.content)}
                                      </p>
                                    )}
                                    {post.attachments && post.attachments.length > 0 && (
                                      <ChatAttachments
                                        attachments={post.attachments}
                                        onOpenLightbox={(items, idx) =>
                                          setChatLightbox({ items, index: idx })
                                        }
                                      />
                                    )}
                                  </>
                                )}
                              </>
                            )}

                            <div className="claude-post-comment-actions">
                              <button
                                type="button"
                                className={`claude-post-react-btn${post.likedByMe ? ' is-active' : ''}`}
                                onClick={() =>
                                  handleTogglePostLike(
                                    selectedCategory.id,
                                    selectedTask.id,
                                    post.id
                                  )
                                }
                                aria-pressed={!!post.likedByMe}
                                aria-label={post.likedByMe ? t('common.unlike') : t('common.like')}
                              >
                                {post.likedByMe ? (
                                  <HiHeart aria-hidden />
                                ) : (
                                  <HiOutlineHeart aria-hidden />
                                )}
                                <span className="claude-post-react-count">
                                  {post.likeCount || 0}
                                </span>
                              </button>
                              <button
                                type="button"
                                className="claude-post-react-btn"
                                onClick={() => {
                                  setReplyTargetComment(null);
                                  setCommentComposerPostId(post.id);
                                }}
                                aria-label={t('comment.write')}
                              >
                                <HiOutlineChatBubbleLeftEllipsis aria-hidden />
                                <span className="claude-post-react-count">
                                  {post.comments?.length || 0}
                                </span>
                              </button>
                            </div>

                            <div className="claude-post-comments-area">
                              <div className="claude-post-comments">
                                {post.comments.map((comment) => (
                                  <div key={comment.id} className="claude-comment-item">
                                    <MemberAvatarButton
                                      id={comment.authorId}
                                      name={comment.author}
                                      avatar={comment.avatar}
                                      onOpen={setActivityMember}
                                    >
                                      <SafeAvatar
                                        src={comment.avatar}
                                        alt="Avatar"
                                        className="claude-comment-avatar-img"
                                        fallbackClassName="claude-comment-avatar"
                                      />
                                    </MemberAvatarButton>
                                    <div className="claude-comment-body">
                                      {editingComment && editingComment.id === comment.id ? (
                                        <div className="claude-edit-comment-box claude-width-full">
                                          <textarea
                                            rows={3}
                                            value={editingComment.content}
                                            onChange={(e) =>
                                              setEditingComment({
                                                ...editingComment,
                                                content: e.target.value,
                                              })
                                            }
                                            className="claude-textarea-edit-comment"
                                          />
                                          {editingComment.attachments &&
                                            editingComment.attachments.length > 0 && (
                                              <div className="claude-edit-files claude-mb-8">
                                                {editingComment.attachments.map((file, fIdx) => (
                                                  <span
                                                    key={fIdx}
                                                    className="claude-attachment-chip claude-bg-white"
                                                  >
                                                    <HiOutlinePaperClip aria-hidden /> {file.name}
                                                    <button
                                                      type="button"
                                                      className="claude-circle-btn claude-btn-delete-circle claude-ml-6"
                                                      onClick={() => {
                                                        const newFiles =
                                                          editingComment.attachments.filter(
                                                            (_, idx) => idx !== fIdx
                                                          );
                                                        setEditingComment({
                                                          ...editingComment,
                                                          attachments: newFiles,
                                                        });
                                                      }}
                                                    >
                                                      ×
                                                    </button>
                                                  </span>
                                                ))}
                                              </div>
                                            )}
                                          <div className="claude-flex-gap-4 claude-justify-flex-end">
                                            <button
                                              className="claude-btn-save-text"
                                              onClick={() =>
                                                handleUpdateComment(
                                                  selectedCategory.id,
                                                  selectedTask.id,
                                                  post.id
                                                )
                                              }
                                            >
                                              {t('common.save')}
                                            </button>
                                            <button
                                              className="claude-btn-cancel-text"
                                              onClick={() => setEditingComment(null)}
                                            >
                                              {t('common.cancel')}
                                            </button>
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          <div className="claude-comment-meta">
                                            <span className="claude-comment-author">
                                              {comment.author}
                                            </span>
                                            <div className="claude-margin-left-auto claude-flex-align-center-gap-8">
                                              {comment.author === currentUser.name && (
                                                <div className="claude-flex-align-center">
                                                  <button
                                                    type="button"
                                                    className="claude-circle-btn claude-btn-edit-circle"
                                                    onClick={() => setEditingComment(comment)}
                                                    aria-label={t('comment.edit')}
                                                  >
                                                    ✎
                                                  </button>
                                                  <button
                                                    type="button"
                                                    className="claude-circle-btn claude-btn-delete-circle"
                                                    onClick={() =>
                                                      handleDeleteComment(
                                                        selectedCategory.id,
                                                        selectedTask.id,
                                                        post.id,
                                                        comment.id
                                                      )
                                                    }
                                                    aria-label={t('comment.delete')}
                                                  >
                                                    ×
                                                  </button>
                                                </div>
                                              )}
                                              {comment.authorId !== user?.id &&
                                                canUserManageProjectAi(activeProject, user) && (
                                                  <button
                                                    type="button"
                                                    className={`claude-circle-btn claude-btn-hide-circle${comment.hiddenAt ? ' is-active' : ''}`}
                                                    onClick={() =>
                                                      handleToggleHideComment(
                                                        selectedCategory.id,
                                                        selectedTask.id,
                                                        post.id,
                                                        comment.id
                                                      )
                                                    }
                                                    aria-label={
                                                      comment.hiddenAt ? t('common.unhide') : t('comment.hide')
                                                    }
                                                    title={
                                                      comment.hiddenAt
                                                        ? t('common.unhide')
                                                        : t('comment.hideAdmin')
                                                    }
                                                  >
                                                    {comment.hiddenAt ? (
                                                      <HiOutlineEye aria-hidden />
                                                    ) : (
                                                      <HiOutlineEyeSlash aria-hidden />
                                                    )}
                                                  </button>
                                                )}
                                              {comment.createdAt && (
                                                <span className="claude-comment-date">
                                                  {formatStackFeedDate(comment.createdAt)}
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                          {comment.parentCommentId && (
                                            <div className="claude-comment-reply-target claude-comment-reply-target--inline">
                                              <span
                                                className="claude-comment-reply-target-arrow"
                                                aria-hidden
                                              >
                                                ↳
                                              </span>
                                              <div className="claude-comment-reply-target-body">
                                                <span className="claude-comment-reply-target-author">
                                                  {comment.parentAuthor || t('common.deletedComment')}
                                                </span>
                                                {comment.parentHiddenAt ? (
                                                  <span className="claude-comment-reply-target-snippet claude-comment-reply-target-snippet--hidden">
                                                    {t('comment.hidden')}
                                                  </span>
                                                ) : (
                                                  comment.parentSnippet && (
                                                    <span className="claude-comment-reply-target-snippet">
                                                      {comment.parentSnippet}
                                                    </span>
                                                  )
                                                )}
                                              </div>
                                            </div>
                                          )}
                                          {comment.hiddenAt ? (
                                            canUserManageProjectAi(activeProject, user) ? (
                                              (() => {
                                                const key = `comment:${comment.id}`;
                                                const expanded = hiddenRevealed.has(key);
                                                return (
                                                  <div className="claude-chat-hidden-block">
                                                    <button
                                                      type="button"
                                                      className={`claude-chat-hidden-banner claude-chat-hidden-banner--toggle${expanded ? ' is-expanded' : ''}`}
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleHiddenRevealed(key);
                                                      }}
                                                      aria-expanded={expanded}
                                                    >
                                                      <span>{t('comment.hiddenBadge')}</span>
                                                      <HiOutlineChevronDown
                                                        className="claude-chat-hidden-chevron"
                                                        aria-hidden
                                                      />
                                                    </button>
                                                    {expanded && (
                                                      <div className="claude-chat-hidden-revealed">
                                                        {comment.content && (
                                                          <p>
                                                            {renderTextWithLinks(comment.content)}
                                                          </p>
                                                        )}
                                                        {comment.attachments &&
                                                          comment.attachments.length > 0 && (
                                                            <ChatAttachments
                                                              attachments={comment.attachments}
                                                              onOpenLightbox={(items, idx) =>
                                                                setChatLightbox({
                                                                  items,
                                                                  index: idx,
                                                                })
                                                              }
                                                            />
                                                          )}
                                                      </div>
                                                    )}
                                                  </div>
                                                );
                                              })()
                                            ) : (
                                              <p className="claude-chat-hidden-placeholder">
                                                {t('comment.hiddenNotice')}
                                              </p>
                                            )
                                          ) : (
                                            <>
                                              {comment.content && (
                                                <p>{renderTextWithLinks(comment.content)}</p>
                                              )}
                                              {comment.attachments &&
                                                comment.attachments.length > 0 && (
                                                  <ChatAttachments
                                                    attachments={comment.attachments}
                                                    onOpenLightbox={(items, idx) =>
                                                      setChatLightbox({ items, index: idx })
                                                    }
                                                  />
                                                )}
                                            </>
                                          )}
                                          <div className="claude-comment-actions-row">
                                            <button
                                              type="button"
                                              className={`claude-post-react-btn claude-post-react-btn--sm${comment.likedByMe ? ' is-active' : ''}`}
                                              onClick={() =>
                                                handleToggleCommentLike(
                                                  selectedCategory.id,
                                                  selectedTask.id,
                                                  post.id,
                                                  comment.id
                                                )
                                              }
                                              aria-pressed={!!comment.likedByMe}
                                              aria-label={
                                                comment.likedByMe ? t('common.unlike') : t('common.like')
                                              }
                                            >
                                              {comment.likedByMe ? (
                                                <HiHeart aria-hidden />
                                              ) : (
                                                <HiOutlineHeart aria-hidden />
                                              )}
                                              <span className="claude-post-react-count">
                                                {comment.likeCount || 0}
                                              </span>
                                            </button>
                                            <button
                                              type="button"
                                              className="claude-post-react-btn claude-post-react-btn--sm"
                                              onClick={() => {
                                                setReplyTargetComment(comment);
                                                setCommentComposerPostId(post.id);
                                              }}
                                              aria-label={t('comment.replyWrite')}
                                            >
                                              <HiOutlineChatBubbleLeftEllipsis aria-hidden />
                                              <span className="claude-post-react-count">
                                                {comment.replyCount || 0}
                                              </span>
                                            </button>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 협업자 초대 및 관리 팝업 */}
        {showInvitePopup && (
          <div className="claude-popup-overlay" onClick={handleCloseInvitePopup}>
            <div
              className="claude-popup-modal claude-team-manage-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="claude-popup-close" onClick={handleCloseInvitePopup}>
                ×
              </button>
              <h2>{t('invite.title')}</h2>

              <div className="claude-team-manage-scroll">
                {/* 초대 섹션 */}
                <div className="claude-form-group">
                  <div>{t('invite.noEmailSent')}</div>
                  <div className="claude-invite-input">
                    <input
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      placeholder={t('invite.emailPlaceholder')}
                      value={inviteEmail}
                      className={inviteError ? 'claude-input-error' : ''}
                      onChange={(e) => {
                        setInviteEmail(e.target.value);
                        if (inviteError) setInviteError('');
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleInviteSubmit();
                        }
                      }}
                    />
                    <button className="claude-btn-primary" onClick={handleInviteSubmit}>
                      {t('invite.invite')}
                    </button>
                  </div>
                  {inviteError && <div className="claude-error-text">{inviteError}</div>}
                </div>

                {/* 협업자 목록 섹션 */}
                <div className="claude-team-manage-section">
                  <div className="claude-team-manage-header">
                    <h3>{t('invite.currentMembers', { n: activeProject?.members.length || 0 })}</h3>
                    {selectedMembersToRemove.length > 0 && (
                      <button
                        className="claude-remove-selected-btn"
                        onClick={handleRemoveSelectedMembers}
                      >
                        {t('invite.removeSelected', { n: selectedMembersToRemove.length })}
                      </button>
                    )}
                  </div>
                  <div className="claude-team-manage-list">
                    {[...(activeProject?.members || [])]
                      .sort((a, b) => {
                        const aJoinedAt = new Date(a.joined_at || 0).getTime();
                        const bJoinedAt = new Date(b.joined_at || 0).getTime();
                        return aJoinedAt - bJoinedAt;
                      })
                      .map((member) => (
                        <div key={member.id} className="claude-team-manage-item">
                          <div className="claude-team-manage-checkbox">
                            <input
                              type="checkbox"
                              checked={selectedMembersToRemove.includes(member.id)}
                              onChange={() => handleToggleMemberSelection(member.id)}
                              disabled={member.role === '관리자'}
                              title={
                                member.role === '관리자'
                                  ? t('invite.adminCannotRemove')
                                  : undefined
                              }
                              aria-label={
                                member.role === '관리자'
                                  ? t('invite.adminCannotSelect')
                                  : t('invite.selectMemberToRemove', { name: member.name })
                              }
                            />
                            <SafeAvatar
                              src={member.avatar}
                              alt="Avatar"
                              className="claude-team-avatar-img claude-team-avatar-sm"
                              fallbackClassName="claude-team-avatar claude-team-avatar-sm"
                              as="span"
                            />
                            <div className="claude-team-text">
                              <strong>{member.name}</strong>
                              <span className="claude-team-email">
                                {t('invite.joinedDate', { date: new Date(member.joined_at).toLocaleDateString(i18n.language) })}
                              </span>
                            </div>
                          </div>
                          <span className="claude-team-role">{member.role}</span>
                        </div>
                      ))}
                  </div>
                </div>

                {/* 대기 중인 초대 목록 */}
                {(() => {
                  const currentPendingInvites = activeProject
                    ? pendingInvites[activeProject.id] || []
                    : [];
                  if (currentPendingInvites.length === 0) return null;
                  return (
                    <div className="claude-team-manage-section">
                      <h3>{t('invite.pendingInvites', { n: currentPendingInvites.length })}</h3>
                      <div className="claude-team-manage-list">
                        {currentPendingInvites.map((invite) => (
                          <div
                            key={invite.id}
                            className="claude-team-manage-item claude-team-pending"
                          >
                            <div className="claude-team-manage-info">
                              <span className="claude-team-avatar claude-team-avatar-sm">📧</span>
                              <div className="claude-team-text">
                                <strong>{invite.email}</strong>
                                <span className="claude-team-email">{t('invite.inviteSent')}</span>
                              </div>
                            </div>
                            <div className="claude-team-actions">
                              {/* <button
                            type="button"
                            className="claude-resend-btn"
                            onClick={() => handleResendInvite(invite.id)}
                          >
                            {t('invite.resend')}
                          </button> */}
                              <button
                                type="button"
                                className="claude-cancel-btn"
                                onClick={() => handleCancelInvite(invite.id)}
                              >
                                {t('common.cancel')}
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        )}

        {/* 협업자 할당 팝업 */}
        {showAssignPopup && (
          <div className="claude-popup-overlay" onClick={() => setShowAssignPopup(false)}>
            <div
              className="claude-popup-modal claude-assign-popup-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <button className="claude-popup-close" onClick={() => setShowAssignPopup(false)}>
                ×
              </button>
              <h2>{t('assign.title')}</h2>
              {(() => {
                const totalMembers = activeProject?.members?.length || 0;
                const draftCount = assignDraft?.length || 0;
                const allAssigned = totalMembers > 0 && draftCount === totalMembers;
                const noneAssigned = draftCount === 0;
                return (
                  <div className="claude-assign-bulk-actions">
                    <button
                      type="button"
                      className="claude-assign-bulk-btn"
                      onClick={() =>
                        setAssignDraft((activeProject?.members || []).map((m) => m.id))
                      }
                      disabled={allAssigned}
                    >
                      {t('assign.selectAll')}
                    </button>
                    <button
                      type="button"
                      className="claude-assign-bulk-btn"
                      onClick={() => setAssignDraft([])}
                      disabled={noneAssigned}
                    >
                      {t('assign.deselectAll')}
                    </button>
                  </div>
                );
              })()}
              <div className="claude-member-list">
                {[...(activeProject?.members || [])]
                  .sort((a, b) => {
                    const aJoinedAt = new Date(a.joined_at || 0).getTime();
                    const bJoinedAt = new Date(b.joined_at || 0).getTime();
                    return aJoinedAt - bJoinedAt;
                  })
                  .map((member) => {
                    const isAssigned = (assignDraft || []).includes(member.id);
                    return (
                      <div key={member.id} className="claude-member-item">
                        <input
                          type="checkbox"
                          checked={isAssigned}
                          onChange={() =>
                            setAssignDraft((prev) => {
                              const cur = prev || [];
                              return cur.includes(member.id)
                                ? cur.filter((id) => id !== member.id)
                                : [...cur, member.id];
                            })
                          }
                        />
                        <MemberAvatarButton
                          id={member.id}
                          name={member.name}
                          avatar={member.avatar}
                          onOpen={setActivityMember}
                        >
                          <SafeAvatar
                            src={member.avatar}
                            alt="Profile"
                            className="claude-team-avatar-img claude-team-avatar-sm"
                            fallbackClassName="claude-team-avatar claude-team-avatar-sm"
                            as="div"
                          />
                        </MemberAvatarButton>
                        <div className="claude-team-text">
                          <strong>{member.name}</strong>
                          <span className="claude-team-email">
                            {t('invite.joinedDate', { date: new Date(member.joined_at).toLocaleDateString(i18n.language) })}
                          </span>
                        </div>
                        <span className="claude-team-role">{member.role}</span>
                      </div>
                    );
                  })}
              </div>
              <div className="claude-template-publish-actions">
                <button
                  type="button"
                  className="claude-btn-outline"
                  onClick={() => setShowAssignPopup(false)}
                >
                  {t('common.cancel')}
                </button>
                <button type="button" className="claude-btn-primary" onClick={handleSaveAssignees}>
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 재전송 알림 팝업 */}
        {showResendPopup && (
          <div className="claude-popup-overlay" onClick={() => setShowResendPopup(false)}>
            <div className="claude-popup-modal claude-small" onClick={(e) => e.stopPropagation()}>
              <button className="claude-popup-close" onClick={() => setShowResendPopup(false)}>
                ×
              </button>
              <h3>{t('invite.resendDoneTitle')}</h3>
            </div>
          </div>
        )}

        {/* 비용 수정 팝업 */}
        {showCostEditPopup && (
          <div className="claude-popup-overlay" onClick={() => setShowCostEditPopup(false)}>
            <div className="claude-popup-modal claude-small" onClick={(e) => e.stopPropagation()}>
              <button className="claude-popup-close" onClick={() => setShowCostEditPopup(false)}>
                ×
              </button>
              <h2>{t('cost.editTitle')}</h2>
              <div className="claude-form-group">
                <div>{t('cost.numberOnly')}</div>
                <div className="claude-cost-input-row">
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="\\d*"
                    value={costEditValue}
                    onChange={(e) => {
                      const withComma = formatNumberWithComma(e.target.value);
                      setCostEditValue(withComma);
                      if (costEditError) setCostEditError('');
                    }}
                    onFocus={scrollInputIntoCenterOnFocus}
                    className={costEditError ? 'claude-input-error' : ''}
                    placeholder={t('cost.numberOnly')}
                  />
                  <span className="claude-cost-unit">{activeProject.cost_unit}</span>
                </div>
                {costEditError && <div className="claude-error-text">{costEditError}</div>}
              </div>
              <div className="claude-template-publish-actions">
                <button
                  type="button"
                  className="claude-btn-outline"
                  onClick={() => setShowCostEditPopup(false)}
                >
                  {t('common.cancel')}
                </button>
                <button type="button" className="claude-btn-primary" onClick={handleCostSave}>
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 예상 비용 근거 팝업 */}
        {showCostReason && (
          <div
            className="claude-popup-overlay"
            onClick={() => !isAiLoading && setShowCostReason(false)}
          >
            <div
              className="claude-popup-modal claude-cost-popup"
              onClick={(e) => e.stopPropagation()}
            >
              {isAiLoading && <div className="claude-loading-overlay"></div>}
              <button
                className="claude-popup-close"
                onClick={() => setShowCostReason(false)}
                disabled={isAiLoading}
              >
                ×
              </button>
              <h3>{t('cost.reasonTitle')}</h3>
              <ul className="claude-cost-reason">
                <li>
                  {activeProject.ai_estimated_cost
                    ? `${parseInt(activeProject.ai_estimated_cost).toLocaleString()}`
                    : '?'}{' '}
                  {activeProject.cost_unit}
                </li>
              </ul>
              <div className="claude-cost-reasons">
                <h4>{t('cost.reasonHeading')}</h4>
                <div className="claude-markdown">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {activeProject.ai_estimated_cost_reasons ||
                      t('cost.reasonEmpty')}
                  </ReactMarkdown>
                </div>
              </div>
              {isAiLoading ? (
                <div className="claude-ai-loading">
                  <div className="claude-loading-spinner"></div>
                </div>
              ) : (
                <div className="claude-template-publish-actions claude-mt-16">
                  <button
                    type="button"
                    className="claude-btn-outline"
                    onClick={() => setShowCostReason(false)}
                  >
                    {t('common.close')}
                  </button>
                  <button
                    type="button"
                    className="claude-btn-primary"
                    onClick={handleRecalculateCost}
                  >
                    {t('cost.recalculate')}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Dashboard;
